import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { DatePipe } from "@angular/common";
import * as L from "leaflet";
import "leaflet.markercluster";
import "leaflet.animatedmarker/src/AnimatedMarker";
import moment from "moment";
import * as turf from "@turf/turf";
import { Subscription } from "rxjs";
import { ApiService } from "src/app/providers/api.service";
import { VehWidgetComponent } from "../ams-vehicles/veh-widget/veh-widget.component";
// SECURITY (audit finding #9 - Formula Injection, CWE-1236)
import {
  sanitizeExcelCell,
  sanitizeRow,
} from "src/app/shared/utils/sanitize-export";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzMessageService } from "ng-zorro-antd/message";
import { es_ES } from "ng-zorro-antd/i18n";
import { NzIconModule } from "ng-zorro-antd/icon";
import { TransfereService } from "src/app/providers/transfer/transfer.service";
import { PlaybackService, ParkingCalculationResult } from "./playback.service";
import { DataGridModule } from "src/app/shared/components/data-grid/data-grid.module";

type VehicleStatusKey = "all" | "running" | "idle" | "stopped" | "offline";
type DetailCardKey = "driver" | "vehicle" | "trip" | "parking";
type HeaderTabKey = "livetrack" | "playback";

interface TrackingVehicle {
  id: string;
  shortName: string;
  status: Exclude<VehicleStatusKey, "all">;
  lat: number;
  lng: number;
  speed: number;
  lastPing: string;
  location: string;
  driver: {
    name: string;
    contact: string;
    license: string;
    doj: string;
  };
  vehicle: {
    make: string;
    model: string;
    plate: string;
    vin: string;
    fuel: string;
  };
  trip: {
    label: string;
    distance: string;
    duration: string;
    alerts: number;
  };
  tripId?: string;
  parkingSummary: {
    count: number;
    longest: string;
  };
  rawStatusId?: number;
  diffMins?: number;
  icon_path?: string;
  direction?: number;
  asrt_id?: number;
  icon_pathr?: string;
}

interface ParkingPoint {
  id: string;
  label: string;
  duration: string;
  window: string;
  location: string;
  lat: number;
  lng: number;
}

interface SummaryCard {
  key: string;
  label: string;
  helper: string;
  value: number;
  accent: string;
  background: string;
  icon?: string;
}

@Component({
  selector: "app-tracking",
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    VehWidgetComponent,
    NzSelectModule,
    NzIconModule,
    DataGridModule,
  ],
  templateUrl: "./tracking.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./tracking.component.scss"],
})
export class TrackingComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Stable bound reference for <app-data-grid [cellPrepared]>. */
  bound_onCellPrepared = (e: any) => this.onCellPrepared(e);

  constructor(
    private api: ApiService,
    private datepipe: DatePipe,
    private message: NzMessageService,
    private cdr: ChangeDetectorRef,
    private TransfereService: TransfereService,
    private playbackService: PlaybackService,
    private router: Router
  ) {}

  fromUrbanDashboard: boolean = false;

  statusFilter: VehicleStatusKey = "all";
  detailCard: DetailCardKey = "driver";
  selectedVehicle: TrackingVehicle | null = null;
  playbackLoading = false;
  playbackSummary: {
    distance: string;
    idle: string;
    alerts: number;
    avgSpeed: string;
  } | null = null;
  parkingResults: ParkingPoint[] = [];
  searchTerm = "";
  playbackMode: "range" | "trip" = "range";
  playbackTrip = {
    date: this.buildLocalDateValue(0),
    dateDisplay: "", // dd-mm-yyyy format for display
    tripId: "",
  };
  availableTrips: any[] = [];
  availableTripsData: any[] = []; // Store full trip data for accessing start/end times

  tenantOptions: any[] = [];
  selectedTenant = 0;
  tntLst: any[] = []; // Tenant list like live-tracking component
  vehicleGroupOptions: any[] = [];
  selectedVehicleGroup = 0;
  vehicleCategoryOptions: any[] = [];
  selectedVehicleCategory = 0;
  locationGroupOptions: any[] = [];
  selectedLocationGroup = 0;
  locationList: any[] = []; // Individual locations from selected group
  selectedLocation: any = "select"; // 'select' for all, 'deselect' for none, or location ID
  selectedVendor = 0;
  isSummaryLoading = false;
  showFiltersPanel = false;
  showPlaybackPanel = false;
  showVehicleDetailsSlider = false;
  showPlaybackBottomPanel = false; // Bottom panel closed by default, user can open it
  locationMarkers: L.Marker[] = [];
  locationPolygons: L.Polygon[] = [];
  locationCircles: L.Circle[] = [];
  private locationLayer = L.layerGroup();
  private _lastIndex: any[] = [];
  private plypoint1: any[] = [];
  private fncepolygon: L.Polygon | null = null;

  private allVehiclesRaw: any[] = [];
  private tenantFallbackIndex = 0; // Index tracking for initial tenant fallback
  private tenantFallbackActive = true; // Whether initial tenant fallback is still active
  private liveVehiclesSub: Subscription | null = null; // Cancel previous API call to prevent race conditions
  private filteredVehiclesRaw: any[] = [];
  quickTools = [
    { key: "filters", label: "Filters", icon: "🎛️" },
    { key: "layers", label: "Layers", icon: "🗺️" },
    { key: "trips", label: "Trips", icon: "🧭" },
    { key: "zoom", label: "Zoom", icon: "🔍" },
    { key: "vehicles", label: "Vehicles", icon: "🚗" },
  ];
  activeTool: string | null = null; // Start with layers closed
  headerTabs: Array<{ key: HeaderTabKey; label: string; icon: string }> = [
    { key: "livetrack", label: "Live Track", icon: "📡" },
    { key: "playback", label: "Playback", icon: "⏱️" },
  ];
  activeHeaderTab: HeaderTabKey = "livetrack";
  collapsedPanels: Record<HeaderTabKey, boolean> = {
    livetrack: false,
    playback: false,
  };
  showVehicleWidget = true;
  pageSize = 5;
  activeSummaryKey: string | null = null;
  showVehicleSlider = false;
  selectedVehicleRaw: any = null; // Raw vehicle data for slider
  selectedVehicleRawSnapshot: any = null; // Snapshot of vehicle data when widget opens (prevents updates during refresh)
  previousSelectedVehicleId: string | number | null = null; // Track previous vehicle ID to prevent unnecessary widget refreshes
  selectedVehicleZoomLevel: number | null = null; // Track zoom level for selected vehicle
  showVehicleStatusLegend = false; // Show vehicle status notations
  playbackBottomPanelTab: "info" | "parking" | "playback" = "info"; // Bottom panel tab
  playbackTerminalHeight = 300; // Default height in pixels
  isResizingTerminal = false; // Track if user is resizing
  playbackSpeedValue = 100; // Playback speed (100 = 1x, 150 = 1.5x, 200 = 2x, 250 = 3x)
  playbackSpeedOptions = [100, 150, 200, 250];
  playbackSpeedLabels = { 100: "1x", 150: "1.5x", 200: "2x", 250: "3x" };
  trackVehicleSpeed = "0";
  trackVehicleTimestamp = "--";
  actualTimeDisplay = "--"; // Actual time with seconds (like playback component)
  totalTravelTime = "--";
  travelDist = "--"; // Travel distance for tripwise card
  currentTravelDistance = 0; // Accumulated distance during playback (in meters)
  startingOdometer = 0; // Starting odometer reading for live distance calculation
  lastKnownOdometer = 0; // Last known odometer for smoother distance updates
  selectedTripData: any = null; // Selected trip data for driver details
  playbackParkingPoints: any[] = [];
  private lastParkingCalculation: ParkingCalculationResult | null = null; // Cache last calculation result
  private playbackRequestCounter = 0; // Prevents race conditions in async operations
  private isCalculatingParking = false; // Prevents duplicate calculations
  playbackCompleted = false;
  tripStartTimeDisplay = "--"; // Formatted trip start time in dd-MM-yyyy format
  tripEndTimeDisplay = "--"; // Formatted trip end time in dd-MM-yyyy format
  rangeFromTimeDisplay = "--"; // Formatted range from time in TH-DD-MM-YYYY HH:mm:ss format
  rangeToTimeDisplay = "--"; // Formatted range to time in TH-DD-MM-YYYY HH:mm:ss format
  isPlaybackPlaying = false; // Track if playback is currently playing
  playbackPoints: any[] = []; // Points for playback animation (gets mutated during playback)
  playbackSourcePoints: any[] = []; // Immutable source points after filtering+smoothing (for consistent data)
  playbackPolylineCoords: Array<[number, number]> = []; // Complete polyline coordinates for consistent drawing
  playbackDataPoints: any[] = []; // All playback points for table display (includes IMEI, vehicle name, etc.)
  playbackMarker: L.Marker | null = null; // Current playback marker
  playbackLine: L.Polyline | null = null; // Playback polyline (animated trail behind marker)
  playbackOverviewLine: L.Polyline | null = null; // Full-route overview polyline (drawn upfront)
  playbackTimerId: any = null; // Timer for playback animation
  pausePlayback = false; // Pause state for playback (true = paused, false = playing)
  playbackSpeedSliderValue = 100; // Slider value for speed (100-250)
  liveTrackRefreshInterval: any = null; // Interval for auto-refreshing livetrack data (20 seconds)
  finalSpdMrkrs: L.Marker[] = []; // Parking markers
  highlightedPointMarker: L.Marker | null = null; // Marker for highlighting selected point
  lastKnownSpeedCt: number | null = null; // Last known speed
  currentPlaybackTimeMs: number | null = null; // Current playback time
  lastDirectionAngle: number = 0; // Last known bearing (degrees) for vehicle direction
  plottedParkingSno: Set<number> = new Set(); // Track which parking points have been plotted during playback
  fencInOutDtls: any[] = []; // Fence in/out details
  Route_collectPointArray: any[] = []; // Route collection point markers (covered)
  Route_collectPointArray1: any[] = []; // Route collection point markers (not covered)
  stslist: any[] = []; // Vehicle list for DataGrid (like livetrack)
  // Vehicle lists by status (like livetrack component)
  allVehiclesList: any[] = [];
  vehiclesList: any[] = []; // Online vehicles
  offlineVehicles: any[] = [];
  repairVehicles: any[] = [];
  notOpratedVehicles: any[] = [];
  runningVehicles: any[] = [];
  stationedShort: any[] = [];
  stationedMedium: any[] = [];
  stationedLong: any[] = [];
  layerOptions = {
    roads: true,
    landmarks: true,
    clusters: true,
  };
  baseLayers: { [key: string]: L.TileLayer } = {};
  currentBaseLayer: L.TileLayer | null = null;
  selectedLayer: "street" | "osm" | "sat" = "street";
  showLocationFilter = false;
  vehicleOptions = {
    follow: false,
  };
  leftDrawerOpen = true;
  selectedVehicles: Set<string> = new Set();
  showPlaybackDrawer = false;
  currentPage = 1;
  itemsPerPage = 10;
  isLoading = true;
  tripDataLoading = false; // Loading state for trip data binding
  quickTripPresets = [
    {
      id: "TRIP-MORN",
      label: "Morning Sweep",
      date: this.buildLocalDateValue(0),
      route: "Haripur Loop",
    },
    {
      id: "TRIP-NOON",
      label: "Midday Collection",
      date: this.buildLocalDateValue(0),
      route: "Bamra Sector",
    },
    {
      id: "TRIP-EVE",
      label: "Evening Return",
      date: this.buildLocalDateValue(0),
      route: "Govindpur Depot",
    },
  ];

  vehicleStatuses = [
    { key: "all" as VehicleStatusKey, label: "All", color: "#0f172a" },
    { key: "running" as VehicleStatusKey, label: "Running", color: "#22c55e" },
    { key: "idle" as VehicleStatusKey, label: "Idle", color: "#facc15" },
    { key: "stopped" as VehicleStatusKey, label: "Stopped", color: "#ef4444" },
    {
      key: "inactive" as VehicleStatusKey,
      label: "Inactive",
      color: "#ec4899",
    },
    { key: "offline" as VehicleStatusKey, label: "NoData", color: "#94a3b8" },
  ];

  summaryCards: SummaryCard[] = [
    {
      key: "total",
      label: "ALL",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#b36005",
    }, // actv_bg_clr: #b36005
    {
      key: "online",
      label: "Online",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#4caf50",
    }, // actv_bg_clr: #4caf50
    {
      key: "offline",
      label: "Offline",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#ff7043",
    }, // actv_bg_clr: #ff7043
    {
      key: "repair",
      label: "Repair",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#754fb8",
    }, // actv_bg_clr: #754fb8
    {
      key: "others",
      label: "Others",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#354f5b",
    }, // actv_bg_clr: #354f5b (matching live-tracking)
    {
      key: "running",
      label: "Running",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#26a69a",
    }, // actv_bg_clr: #26a69a
    {
      key: "stationedShort",
      label: "Stationed <5min",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#6557f7",
    }, // actv_bg_clr: #6557f7
    {
      key: "stationedMedium",
      label: "Stationed 5-15min",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#ffb74d",
    }, // actv_bg_clr: #ffb74d
    {
      key: "stationedLong",
      label: "Stationed >15min",
      helper: "",
      value: 0,
      accent: "#ffffff",
      background: "#ef5350",
    }, // actv_bg_clr: #ef5350
  ];

  vehicles: TrackingVehicle[] = [];
  playbackVehicles: TrackingVehicle[] = []; // All vehicles available for playback selection
  filteredPlaybackVehicles: TrackingVehicle[] = []; // Filtered vehicles for playback dropdown
  playbackVehicleSearchTerm: string = ""; // Search term for playback vehicle selection
  showVehicleDropdown: boolean = false; // Show/hide vehicle dropdown
  showParkingPointsTable: boolean = false; // Show/hide parking points table modal

  playbackFilters = {
    vehicleId: "",
    from: "",
    to: "",
    fromDisplay: "", // dd-mm-yyyy HH:mm:ss format for display
    toDisplay: "", // dd-mm-yyyy HH:mm:ss format for display
    speed: 1,
  };

  // Store pending vehicle ID from trip data (to be selected after vehicles load)
  private pendingPlaybackVehicleId: any = null;

  private map?: L.Map;
  private vehicleLayer = L.layerGroup();
  private vehicleClusterGroup: L.MarkerClusterGroup | null = null; // Marker cluster group for livetrack
  private parkingLayer = L.layerGroup();
  private markerRegistry = new Map<string, L.Marker>();
  private previousPlaybackPoint: any = null; // Track previous point for smooth animation
  // India geographic bounds to constrain map and playback view
  private readonly indiaBounds = L.latLngBounds([6, 68], [38.5, 97.5]);

  goBackToDashboard() {
    this.router.navigate([`internal/dashboard/urban-dashboard`]);
  }

  ngOnInit(): void {
    this.fromUrbanDashboard =
      this.TransfereService.getLoclData("from_urban_dashboard") === true;
    this.TransfereService.setLoclData("from_urban_dashboard", false);
    this.bootstrapTenantContext();

    // Check for trip data from ulb-dashboard (or other dashboards)
    const tripData = this.TransfereService.getLoclData("plybckDta");

    // Initialize playback filters with current date/time (like playback component)
    const today = new Date();
    const fromDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0
    );
    const toDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59
    );
    this.playbackFilters.from = this.formatDateTimeLocal24Hr(fromDate);
    this.playbackFilters.to = this.formatDateTimeLocal24Hr(toDate);
    this.playbackFilters.fromDisplay =
      this.datepipe.transform(today, "dd-MM-yyyy") + " 00:00:00";
    this.playbackFilters.toDisplay =
      this.datepipe.transform(today, "dd-MM-yyyy") + " 23:59:59";
    this.playbackTrip.date = this.buildLocalDateValue(0); // Default to current date for trip
    this.playbackTrip.dateDisplay = this.datepipe.transform(
      today,
      "dd-MM-yyyy"
    );

    this.fetchSelectionData();
    this.fetchLiveVehicles();

    // If trip data exists, set up playback mode after data is loaded
    if (tripData) {
      // Set tenant ID (tnt_id) from trip data if available
      if (tripData.tnt_id) {
        this.selectedTenant = tripData.tnt_id;
      }

      // Clear the stored data after reading
      this.TransfereService.ClearLocalData("plybckDta");
      console.log("tripData", tripData);
      // Store trip data for later use after vehicles are loaded
      const tripVehicleId = tripData.asrt_id;
      const tripDate = tripData.actl_strt_ts
        ? moment(tripData.actl_strt_ts).format("YYYY-MM-DD")
        : tripData.actl_strt
        ? moment(tripData.actl_strt).format("YYYY-MM-DD")
        : this.buildLocalDateValue(0);
      const tripId = tripData.trp_rn_id || tripData.trip_rn_id;

      // Determine playback mode: if actl_strt and actl_end are available, use timeline mode, otherwise use trip mode
      const hasStartAndEnd =
        tripData.actl_strt !== undefined &&
        tripData.actl_strt !== null &&
        tripData.actl_end !== undefined &&
        tripData.actl_end !== null;
      const playbackModeToUse = hasStartAndEnd ? "range" : "trip";

      // Wait for vehicles to load, then set up playback
      setTimeout(() => {
        // Set playback mode (trip or timeline) based on available data
        this.setPlaybackMode(playbackModeToUse);

        // Activate playback tab (this loads playback vehicles)
        this.activateHeaderTab("playback");

        // Set vehicle ID (asrt_id) after playback tab is activated
        if (tripVehicleId) {
          this.playbackFilters.vehicleId = String(tripVehicleId);
          // Also store as pending in case vehicles haven't loaded yet
          this.pendingPlaybackVehicleId = tripVehicleId;
        }

        if (playbackModeToUse === "range") {
          // Timeline mode: Set from and to dates from actl_strt_ts and actl_end_ts (preferred) or actl_strt/actl_end
          // Parse dd-MM-yyyy HH:mm format if available, otherwise parse other formats
          if (tripData.actl_strt_ts) {
            let startDate: string;
            let startDisplay: string;
            // Check if already in dd-MM-yyyy HH:mm format (like "07-01-2026 06:22")
            if (
              tripData.actl_strt_ts.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}/)
            ) {
              // Parse dd-MM-yyyy HH:mm format
              const parsed = moment(tripData.actl_strt_ts, "DD-MM-YYYY HH:mm");
              startDate = parsed.format("YYYY-MM-DDTHH:mm");
              startDisplay = tripData.actl_strt_ts;
            } else if (tripData.actl_strt_ts.match(/^\d{2}-\d{2}-\d{4}$/)) {
              // Parse dd-MM-yyyy format (date only)
              const parsed = moment(tripData.actl_strt_ts, "DD-MM-YYYY");
              startDate = parsed.format("YYYY-MM-DDTHH:mm");
              startDisplay = tripData.actl_strt_ts;
            } else {
              // Parse ISO format or other formats
              const parsed = moment(tripData.actl_strt_ts);
              startDate = parsed.format("YYYY-MM-DDTHH:mm");
              startDisplay = parsed.format("dd-MM-yyyy HH:mm");
            }
            this.playbackFilters.from = startDate;
            this.tripStartTimeDisplay = startDisplay;
          } else if (tripData.actl_strt) {
            let startDate: string;
            let startDisplay: string;
            // Check if already in dd-MM-yyyy format
            if (tripData.actl_strt.match(/^\d{2}-\d{2}-\d{4}$/)) {
              // Parse dd-MM-yyyy format
              const parsed = moment(tripData.actl_strt, "DD-MM-YYYY");
              startDate = parsed.format("YYYY-MM-DDTHH:mm");
              startDisplay = tripData.actl_strt;
            } else {
              // Parse ISO format or other formats
              const parsed = moment(tripData.actl_strt);
              startDate = parsed.format("YYYY-MM-DDTHH:mm");
              startDisplay = parsed.format("dd-MM-yyyy HH:mm");
            }
            this.playbackFilters.from = startDate;
            this.tripStartTimeDisplay = startDisplay;
          }

          if (tripData.actl_end_ts) {
            let endDate: string;
            let endDisplay: string;
            // Check if already in dd-MM-yyyy HH:mm format (like "07-01-2026 12:47")
            if (
              tripData.actl_end_ts.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}/)
            ) {
              // Parse dd-MM-yyyy HH:mm format
              const parsed = moment(tripData.actl_end_ts, "DD-MM-YYYY HH:mm");
              endDate = parsed.format("YYYY-MM-DDTHH:mm");
              endDisplay = tripData.actl_end_ts;
            } else if (tripData.actl_end_ts.match(/^\d{2}-\d{2}-\d{4}$/)) {
              // Parse dd-MM-yyyy format (date only)
              const parsed = moment(tripData.actl_end_ts, "DD-MM-YYYY");
              endDate = parsed.format("YYYY-MM-DDTHH:mm");
              endDisplay = tripData.actl_end_ts;
            } else {
              // Parse ISO format or other formats
              const parsed = moment(tripData.actl_end_ts);
              endDate = parsed.format("YYYY-MM-DDTHH:mm");
              endDisplay = parsed.format("dd-MM-yyyy HH:mm");
            }
            this.playbackFilters.to = endDate;
            this.tripEndTimeDisplay = endDisplay;
            // Also update range display format
            const toMoment = moment(endDate, "YYYY-MM-DDTHH:mm");
            this.rangeToTimeDisplay = toMoment.isValid()
              ? `TH-${toMoment.format("DD-MM-YYYY HH:mm:ss")}`
              : "--";
          } else if (tripData.actl_end) {
            let endDate: string;
            let endDisplay: string;
            // Check if already in dd-MM-yyyy format
            if (tripData.actl_end.match(/^\d{2}-\d{2}-\d{4}$/)) {
              // Parse dd-MM-yyyy format
              const parsed = moment(tripData.actl_end, "DD-MM-YYYY");
              endDate = parsed.format("YYYY-MM-DDTHH:mm");
              endDisplay = tripData.actl_end;
            } else {
              // Parse ISO format or other formats
              const parsed = moment(tripData.actl_end);
              endDate = parsed.format("YYYY-MM-DDTHH:mm");
              endDisplay = parsed.format("dd-MM-yyyy HH:mm");
            }
            this.playbackFilters.to = endDate;
            this.tripEndTimeDisplay = endDisplay;
          }
        } else {
          // Trip mode: Set trip date and trip ID
          if (tripDate) {
            this.playbackTrip.date = tripDate;
          }

          // Load trips for the selected vehicle and date
          if (tripVehicleId) {
            this.updateAvailableTrips();

            // After trips are loaded, set the trip ID
            setTimeout(() => {
              if (tripId && this.availableTripsData.length > 0) {
                // Verify trip exists in available trips
                const tripExists = this.availableTripsData.find(
                  (trip: any) =>
                    trip.trp_rn_id == tripId || trip.trip_rn_id == tripId
                );
                if (tripExists) {
                  this.playbackTrip.tripId = tripId;
                  // Update playback filters with trip start/end times if available (for display)
                  // Format to dd-MM-yyyy HH:mm for display
                  if (tripExists.actl_strt_ts || tripExists.actl_start_ts) {
                    const startTime =
                      tripExists.actl_strt_ts || tripExists.actl_start_ts;
                    // Check if already in dd-MM-yyyy HH:mm format
                    if (
                      startTime &&
                      startTime.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}/)
                    ) {
                      // Already in dd-MM-yyyy HH:mm format, use as is for display but convert for internal use
                      const parsed = moment(startTime, "DD-MM-YYYY HH:mm");
                      this.playbackFilters.from =
                        parsed.format("YYYY-MM-DDTHH:mm") || "";
                      this.tripStartTimeDisplay = startTime;
                    } else if (
                      startTime &&
                      startTime.match(/^\d{2}-\d{2}-\d{4}$/)
                    ) {
                      // dd-MM-yyyy format (date only)
                      const parsed = moment(startTime, "DD-MM-YYYY");
                      this.playbackFilters.from =
                        parsed.format("YYYY-MM-DDTHH:mm") || "";
                      this.tripStartTimeDisplay = startTime;
                    } else if (startTime) {
                      // Parse and format to dd-MM-yyyy HH:mm for display
                      const parsed = moment(startTime);
                      this.tripStartTimeDisplay =
                        parsed.format("dd-MM-yyyy HH:mm") || "--";
                      this.playbackFilters.from =
                        parsed.format("YYYY-MM-DDTHH:mm") || "";
                    }
                  }
                  if (tripExists.actl_end_ts || tripExists.actl_end_ts) {
                    const endTime =
                      tripExists.actl_end_ts || tripExists.actl_end_ts;
                    // Check if already in dd-MM-yyyy HH:mm format
                    if (
                      endTime &&
                      endTime.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}/)
                    ) {
                      // Already in dd-MM-yyyy HH:mm format, use as is for display but convert for internal use
                      const parsed = moment(endTime, "DD-MM-YYYY HH:mm");
                      this.playbackFilters.to =
                        parsed.format("YYYY-MM-DDTHH:mm") || "";
                      this.tripEndTimeDisplay = endTime;
                    } else if (
                      endTime &&
                      endTime.match(/^\d{2}-\d{2}-\d{4}$/)
                    ) {
                      // dd-MM-yyyy format (date only)
                      const parsed = moment(endTime, "DD-MM-YYYY");
                      this.playbackFilters.to =
                        parsed.format("YYYY-MM-DDTHH:mm") || "";
                      this.tripEndTimeDisplay = endTime;
                    } else if (endTime) {
                      // Parse and format to dd-MM-yyyy HH:mm for display
                      const parsed = moment(endTime);
                      this.tripEndTimeDisplay =
                        parsed.format("dd-MM-yyyy HH:mm") || "--";
                      this.playbackFilters.to =
                        parsed.format("YYYY-MM-DDTHH:mm") || "";
                    }
                  }
                } else {
                  console.warn("Trip ID not found in available trips");
                }
              }
              this.cdr.detectChanges();
            }, 500);
          }
        }
      }, 1500);
    }

    // Set default to show total vehicles
    this.activeSummaryKey = "total";
  }

  ngAfterViewInit(): void {
    this.initMap();
    // Invalidate map size after init to ensure tiles render correctly
    if (this.map) {
      setTimeout(() => this.map.invalidateSize(), 200);
    }
    // Render markers after map is initialized
    setTimeout(() => {
      this.renderVehicleMarkers();
      // Start auto-refresh for livetrack mode after map is ready
      if (this.activeHeaderTab === "livetrack") {
        this.startLiveTrackAutoRefresh();
      }
    }, 300);
  }

  ngOnDestroy(): void {
    // Cancel any in-flight vehicle API call
    if (this.liveVehiclesSub) {
      this.liveVehiclesSub.unsubscribe();
      this.liveVehiclesSub = null;
    }

    // Stop live track auto-refresh
    this.stopLiveTrackAutoRefresh();

    // Clear live track refresh interval
    if (this.liveTrackRefreshInterval) {
      clearInterval(this.liveTrackRefreshInterval);
      this.liveTrackRefreshInterval = null;
    }

    // Clear playback timer
    if (this.playbackTimerId) {
      clearTimeout(this.playbackTimerId);
      this.playbackTimerId = null;
    }

    // Clear all animated markers and their popups
    if (this.mov_marker) {
      Object.keys(this.mov_marker).forEach((key) => {
        const marker = this.mov_marker[key];
        if (marker) {
          try {
            // Clear popup update interval if exists
            const intervalId = (marker as any).popupUpdateInterval;
            if (intervalId) {
              clearInterval(intervalId);
            }

            // Close popup
            if (marker.closePopup) {
              marker.closePopup();
            }

            // Remove from map
            if (this.map) {
              this.map.removeLayer(marker);
            }
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      });
      this.mov_marker = {};
    }

    // Clear deletemarkers array
    if (this.deletemarkers && this.deletemarkers.length > 0) {
      this.deletemarkers.forEach((marker: any) => {
        if (marker && this.map) {
          try {
            if (marker.closePopup) marker.closePopup();
            this.map.removeLayer(marker);
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      });
      this.deletemarkers = [];
    }

    // Clear location markers
    if (this.locationMarkers && this.locationMarkers.length > 0) {
      this.locationMarkers.forEach((marker) => {
        if (marker && this.map) {
          try {
            this.map.removeLayer(marker);
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      });
      this.locationMarkers = [];
    }

    // Clear location polygons and circles
    if (this.locationPolygons && this.locationPolygons.length > 0) {
      this.locationPolygons.forEach((polygon) => {
        if (polygon && this.map) {
          try {
            this.map.removeLayer(polygon);
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      });
      this.locationPolygons = [];
    }

    if (this.locationCircles && this.locationCircles.length > 0) {
      this.locationCircles.forEach((circle) => {
        if (circle && this.map) {
          try {
            this.map.removeLayer(circle);
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      });
      this.locationCircles = [];
    }

    // Clear parking markers
    if (this.finalSpdMrkrs && this.finalSpdMrkrs.length > 0) {
      this.finalSpdMrkrs.forEach((marker) => {
        if (marker && this.map) {
          try {
            marker.closePopup();
            this.map.removeLayer(marker);
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      });
      this.finalSpdMrkrs = [];
    }

    // Clear highlighted point marker
    if (this.highlightedPointMarker && this.map) {
      try {
        this.highlightedPointMarker.closePopup();
        this.map.removeLayer(this.highlightedPointMarker);
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.highlightedPointMarker = null;
    }

    // Clear playback markers
    if (this.playbackMarker && this.map) {
      try {
        this.map.removeLayer(this.playbackMarker);
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.playbackMarker = null;
    }

    // Clear playback line
    if (this.playbackLine && this.map) {
      try {
        this.map.removeLayer(this.playbackLine);
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.playbackLine = null;
    }

    // Clear fence polygon
    if (this.fncepolygon && this.map) {
      try {
        this.map.removeLayer(this.fncepolygon);
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.fncepolygon = null;
    }

    // Clear layers
    if (this.vehicleClusterGroup) {
      try {
        this.vehicleClusterGroup.clearLayers();
        if (this.map) {
          this.map.removeLayer(this.vehicleClusterGroup);
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.vehicleClusterGroup = null;
    }

    if (this.vehicleLayer) {
      try {
        this.vehicleLayer.clearLayers();
        if (this.map) {
          this.map.removeLayer(this.vehicleLayer);
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
    }

    if (this.parkingLayer) {
      try {
        this.parkingLayer.clearLayers();
        if (this.map) {
          this.map.removeLayer(this.parkingLayer);
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
    }

    if (this.locationLayer) {
      try {
        this.locationLayer.clearLayers();
        if (this.map) {
          this.map.removeLayer(this.locationLayer);
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
    }

    // Clear marker registry
    this.markerRegistry.clear();

    // Clean up map instance
    if (this.map) {
      try {
        // Remove all event listeners
        this.map.off();

        // Remove all layers
        this.map.eachLayer((layer) => {
          try {
            this.map?.removeLayer(layer);
          } catch (e) {
            // Ignore errors during cleanup
          }
        });

        // Remove map from DOM
        this.map.remove();
      } catch (e) {
        // Ignore errors during cleanup
      } finally {
        this.map = undefined;
      }
    }

    // Clear all arrays and objects
    this.m_prv_lat = {};
    this.m_prv_lng = {};
    this.mov_marker = {};
    this.deletemarkers = [];
    this.allVehiclesRaw = [];
    this.filteredVehiclesRaw = [];
    this.vehicles = [];
    this.stslist = [];
    this.playbackPoints = [];
    this.playbackDataPoints = [];
    this.playbackParkingPoints = [];
    this.Route_collectPointArray = [];
    this.Route_collectPointArray1 = [];
    this.fencInOutDtls = [];
    this.plypoint1 = [];
    this._lastIndex = [];
  }

  get filteredStsList(): any[] {
    let list = [...this.stslist];
    // Filter by search term (vehicle number)
    if (this.searchTerm && this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      list = list.filter(
        (vehicle) =>
          vehicle.asrt_nm && vehicle.asrt_nm.toLowerCase().includes(term)
      );
    }
    return list;
  }

  get paginatedStsList(): any[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredStsList.slice(start, end);
  }

  get totalPagesStsList(): number {
    return Math.ceil(this.filteredStsList.length / this.itemsPerPage);
  }

  get filteredVehicles(): TrackingVehicle[] {
    let list = [...this.vehicles];
    if (this.statusFilter !== "all") {
      list = list.filter((vehicle) => vehicle.status === this.statusFilter);
    }
    return list;
  }

  get paginatedVehicles(): TrackingVehicle[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredVehicles.slice(start, end);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredVehicles.length / this.itemsPerPage);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  previousPage(): void {
    const maxPages =
      this.activeHeaderTab === "livetrack"
        ? this.totalPagesStsList
        : this.totalPages;
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPagesStsList) {
      this.currentPage++;
    }
  }

  setStatusFilter(key: VehicleStatusKey): void {
    this.statusFilter = key;
    setTimeout(() => this.renderVehicleMarkers(), 0);
  }

  setDetailCard(card: DetailCardKey): void {
    this.detailCard = card;
  }

  refreshMap(): void {
    this.renderVehicleMarkers();
    if (this.parkingResults.length) {
      this.paintParkingPoints();
    }
  }

  selectVehicle(
    vehicle: TrackingVehicle,
    centerOnMap = false,
    zoom = 17
  ): void {
    this.selectedVehicle = vehicle;
    this.playbackFilters.vehicleId = vehicle.id;
    if (vehicle.tripId) {
      this.playbackTrip.tripId = vehicle.tripId;
    }
    this.detailCard = "driver";

    // Find raw vehicle data for slider - try multiple matching strategies
    this.selectedVehicleRaw =
      this.allVehiclesRaw.find(
        (v) =>
          v.asrt_nm === vehicle.id ||
          v.asrt_id === vehicle.id ||
          (v.asrt_nm && v.asrt_nm.toString() === vehicle.id.toString())
      ) || null;

    // If not found in allVehiclesRaw, create a basic raw vehicle object from the vehicle data
    if (!this.selectedVehicleRaw && vehicle) {
      this.selectedVehicleRaw = {
        asrt_id: vehicle.id,
        asrt_nm: vehicle.id,
        lat: vehicle.lat,
        lng: vehicle.lng,
        speed_ct: vehicle.speed || 0,
        asrt_sts_nm: vehicle.status || "",
        asrt_ctgry_nm: "",
        tdy_dst: 0,
        data_received_ts: new Date().toISOString(),
      };
    }

    if (centerOnMap) {
      this.focusOnVehicle(vehicle, zoom);
    }

    // In livetrack mode, show vehicle widget when vehicle is selected
    if (this.activeHeaderTab === "livetrack") {
      const currentVehicleId =
        this.selectedVehicleRaw?.asrt_id?.toString() ||
        this.selectedVehicleRaw?.asrt_nm?.toString();

      // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
      if (
        this.selectedVehicleRaw &&
        this.previousSelectedVehicleId !== currentVehicleId
      ) {
        this.selectedVehicleRawSnapshot = JSON.parse(
          JSON.stringify(this.selectedVehicleRaw)
        );
        this.previousSelectedVehicleId = currentVehicleId;
      }
      this.showVehicleSlider = true;
    }

    // Re-render markers to update selection highlight
    this.renderVehicleMarkers();
  }

  simulatePlayback(): void {
    console.log(
      "simulatePlayback",
      this.playbackMode,
      this.playbackFilters.vehicleId,
      this.playbackFilters.from,
      this.playbackFilters.to,
      this.playbackTrip.date,
      this.playbackTrip.tripId
    );
    // Clear all livetrack markers when starting playback (matching live-tracking behavior)
    this.clearAllLiveTrackMarkers();

    // Clear any existing playback data including parking data
    this.clearPlaybackData();
    this.clearParkingData();

    // Reset speed to 1x when starting playback
    this.playbackSpeedSliderValue = 100;
    this.playbackSpeedValue = 100;

    // Set play mode when clicking run playback
    this.isPlaybackPlaying = true;
    this.pausePlayback = false;

    // Reset card visibility when starting new playback
    this.showVehicleCard = true;
    this.showDriverCard = true;
    this.showParkingCard = true;
    this.showTripwiseCard = true;
    this.showPlaybackDataCard = true;

    // Set initial card states: tripwise and speed open, others closed during playback
    this.collapsedCards = {
      tripwise: false, // Open
      vehicle: true, // Closed
      driver: true, // Closed
      parking: true, // Closed
      playbackData: true, // Closed
    };
    this.showSpeedSlider = true; // Speed adjuster open

    if (this.playbackMode === "range") {
      if (
        !this.playbackFilters.vehicleId ||
        !this.playbackFilters.from ||
        !this.playbackFilters.to
      ) {
        return;
      }
      this.getPlaybackByRange();
    } else {
      // Trip-wise playback - trp_rn_id is the main element
      console.log(
        "Trip-wise playback - trp_rn_id:",
        this.playbackTrip,
        "date:",
        this.playbackTrip.date
      );
      if (!this.playbackTrip.tripId) {
        console.warn("Trip ID (trp_rn_id) is required for trip-wise playback");
        return;
      }
      // trp_rn_id will be passed to getPlaybackByTrip() which uses it as the main element
      this.getPlaybackByTrip();
    }

    // Terminal is closed by default, user can open it manually
    // Don't auto-open terminal, let user control it

    // Zoom to vehicle location using tenant lat/lng (like live-tracking component)
    this.zoomToVehicleLocation();
  }

  private zoomToVehicleLocation(): void {
    if (!this.map) {
      return;
    }

    // After playback data loads, zoom to playback route bounds
    setTimeout(() => {
      if (this.playbackPoints && this.playbackPoints.length > 0) {
        // Clamp playback points to India bounds to avoid panning outside country
        const south = this.indiaBounds.getSouth();
        const north = this.indiaBounds.getNorth();
        const west = this.indiaBounds.getWest();
        const east = this.indiaBounds.getEast();
        const clampedPoints = this.playbackPoints
          .filter((p: any) => p.lat && p.lng)
          .map((p: any) => [
            Math.min(Math.max(p.lat, south), north),
            Math.min(Math.max(p.lng, west), east),
          ]);

        if (clampedPoints.length > 0) {
          const bounds = L.latLngBounds(clampedPoints);
          this.map.fitBounds(bounds, { padding: [50, 50] });
        } else {
          this.zoomToTenantLocation();
        }
      } else if (
        this.selectedVehicle &&
        this.selectedVehicle.lat &&
        this.selectedVehicle.lng
      ) {
        // Fallback to vehicle location
        this.map.flyTo(
          [this.selectedVehicle.lat, this.selectedVehicle.lng],
          17,
          {
            animate: true,
            duration: 0.8,
          }
        );
        this.selectedVehicleZoomLevel = 17;
      } else {
        // Fallback to tenant location
        this.zoomToTenantLocation();
      }
    }, 500);
  }

  private zoomToTenantLocation(): void {
    if (!this.map) {
      return;
    }

    // Zoom to tenant location like live-tracking component (line 251)
    if (!this.selectedTenant || this.selectedTenant == 0) {
      // Use client-level lat/lng for "All" tenant
      const userDtls: any = JSON.parse(
        localStorage.getItem("wto_clients") || "[]"
      );
      if (userDtls[0]?.lat && userDtls[0]?.lng) {
        this.map.setView([userDtls[0].lat, userDtls[0].lng], 11);
      }
    } else {
      const tnt = this.tntLst?.find(
        (t: any) => t.tnt_id == this.selectedTenant
      );
      if (tnt?.lat && tnt?.lng) {
        this.map.setView([tnt.lat, tnt.lng], 13);
      }
    }
  }

  getPlaybackByRange(): void {
    this.playbackLoading = true;
    this.clearPlaybackData();
    this.clearParkingData();
    this.playbackCompleted = false;
    this.isPlaybackPlaying = false;

    // Format dates to YYYY-MM-DD HH:mm:ss for API (convert from datetime-local format YYYY-MM-DDTHH:mm)
    // Parse the datetime-local format and convert to YYYY-MM-DD HH:mm:ss
    let fromDate: string;
    let toDate: string;

    if (this.playbackFilters.from) {
      // Parse datetime-local format (YYYY-MM-DDTHH:mm or YYYY-MM-DDTHH:mm:ss) and convert to YYYY-MM-DD HH:mm:ss
      const fromMoment = moment(
        this.playbackFilters.from,
        ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"],
        true
      );
      fromDate = fromMoment.format("YYYY-MM-DD HH:mm:ss");
      // Update display format
      this.rangeFromTimeDisplay = fromMoment.isValid()
        ? `TH-${fromMoment.format("DD-MM-YYYY HH:mm:ss")}`
        : "--";
    } else {
      fromDate = "";
      this.rangeFromTimeDisplay = "--";
    }

    if (this.playbackFilters.to) {
      // Parse datetime-local format (YYYY-MM-DDTHH:mm or YYYY-MM-DDTHH:mm:ss) and convert to YYYY-MM-DD HH:mm:ss
      const toMoment = moment(
        this.playbackFilters.to,
        ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"],
        true
      );
      toDate = toMoment.format("YYYY-MM-DD HH:mm:ss");
      // Update display format
      this.rangeToTimeDisplay = toMoment.isValid()
        ? `TH-${toMoment.format("DD-MM-YYYY HH:mm:ss")}`
        : "--";
    } else {
      toDate = "";
      this.rangeToTimeDisplay = "--";
    }

    // Get asrt_id from raw vehicle data
    const rawVehicle = this.allVehiclesRaw.find(
      (v) =>
        v.asrt_nm === this.playbackFilters.vehicleId ||
        v.asrt_id === this.playbackFilters.vehicleId
    );
    const asrt_id = rawVehicle?.asrt_id || this.playbackFilters.vehicleId;

    const data = {
      checkbit: "byVeh",
      sdate: fromDate,
      edate: toDate,
      asrt_id: asrt_id,
      trp_rn_id: 0,
    };

    console.log("Playback API call:", data);
    this.api.post(data, "playback/vehicle/playback").subscribe(
      (res) => {
        const playData = res?.["data"];
        console.log("Playback data:", playData);
        if (playData) {
          // Initialize playback animation (like playback component)
          this.initializePlayback(playData);
        }
        this.playbackLoading = false;
      },
      (err) => {
        console.error("Playback API error:", err);
        this.playbackLoading = false;
      }
    );
  }

  /**
   * Parse timestamp string to Date object
   * Handles both "add_date" and "Signall" field formats
   * Format: "DD-MM-YYYY HH:mm:ss" or "DD-MM-YYYY HH:mm"
   * @param timestampStr - Timestamp string to parse
   * @returns Date object or null if invalid
   */
  parseTimestamp(timestampStr: string): Date | null {
    if (!timestampStr) return null;

    const ts = timestampStr.trim();
    const parts = ts.split(" ");
    if (parts.length < 2) return null;

    const datePart = parts[0].split("-");
    const timePart = parts[1].split(":");

    if (datePart.length !== 3 || timePart.length < 2) return null;

    // Create date: month is 0-indexed in JavaScript Date
    const day = parseInt(datePart[0], 10);
    const month = parseInt(datePart[1], 10) - 1;
    const year = parseInt(datePart[2], 10);
    const hour = parseInt(timePart[0], 10);
    const minute = parseInt(timePart[1], 10);
    const second = timePart.length >= 3 ? parseInt(timePart[2], 10) : 0;

    const date = new Date(year, month, day, hour, minute, second);

    // Validate the date
    if (isNaN(date.getTime())) return null;

    return date;
  }

  /**
   * Get parking points from GPS playback data - EXACT COPY OF parking.js LOGIC
   *
   * Detects continuous parking blocks where:
   * - Speed stays 0 continuously (speed_ct === 0)
   * - Ignition is ON (ignition_in === 1) OR ignition data not available
   * - Duration > 1 minute
   *
   * This function is deterministic and repeatable - same input always produces same output
   * @param data - Array of GPS points (uses playbackPoints or provided array)
   * @param vehicleName - Name of the vehicle for parking points
   *
   * Uses PlaybackService for consistent parking point calculation based on parking.js logic.
   * This ensures the same number of parking points are displayed every time playback is initiated.
   */
  /**
   * Calculate parking points from provided data snapshot (DETERMINISTIC)
   *
   * GUARANTEES:
   * 1. Same input data ALWAYS produces same output
   * 2. Data is sorted by timestamp before processing
   * 3. Lat/lng are rounded to fixed precision (6 decimals)
   * 4. Prevents race conditions with request counter
   * 5. Results are cached for verification
   *
   * @param data - Stable snapshot of filtered GPS points (should be a deep copy)
   * @param vehicleName - Name of the vehicle for parking points
   */
  calculateParkingPointsFromData(data: any[], vehicleName: string): void {
    // Increment request counter for race condition prevention
    const currentRequest = ++this.playbackRequestCounter;

    // Prevent duplicate calculations
    if (this.isCalculatingParking) {
      console.warn(
        "[Parking] Calculation already in progress, skipping duplicate request"
      );
      return;
    }

    this.isCalculatingParking = true;

    try {
      // CRITICAL: Reset state completely before new calculation
      this.playbackParkingPoints = [];
      this.lastParkingCalculation = null;

      if (!data || !Array.isArray(data) || data.length === 0) {
        console.log("[Parking] No data provided");
        return;
      }

      // Check for race condition - if another request came in, abort this one
      if (currentRequest !== this.playbackRequestCounter) {
        console.warn(
          "[Parking] Race condition detected, aborting stale calculation"
        );
        return;
      }

      // Use the new DETERMINISTIC calculation from PlaybackService
      // This method:
      // 1. Sorts data by timestamp
      // 2. Rounds lat/lng to 6 decimal places
      // 3. Uses fixed thresholds
      // 4. Returns immutable frozen results
      const calculationResult =
        this.playbackService.calculateParkingPointsDeterministic(data);

      // Verify consistency (in development)
      if (typeof (window as any)["__DEV__"] !== "undefined") {
        this.playbackService.verifyConsistency(data);
      }

      // Check again for race condition after calculation
      if (currentRequest !== this.playbackRequestCounter) {
        console.warn(
          "[Parking] Race condition detected after calculation, discarding results"
        );
        return;
      }

      // Cache the calculation result for verification
      this.lastParkingCalculation = calculationResult;

      // Convert parking points to the format expected by the component
      // Use Object.freeze to prevent accidental mutations
      this.playbackParkingPoints = calculationResult.parkingPoints.map(
        (parking) =>
          Object.freeze({
            sno: parking.parkingNumber,
            Sno: parking.parkingNumber,
            asrt_nm: vehicleName,
            Signal: parking.startTime,
            signalTime: parking.startTime,
            lastTime: parking.endTime,
            stoppageTime: parking.durationMinutes,
            lat: parking.lat,
            lng: parking.lng,
            originalIndex: parking.index,
            startTimestamp: parking.startTimestamp,
            endTimestamp: parking.endTimestamp,
          })
      );

      // Log results with verification hash
      console.log(
        `[Parking] DETERMINISTIC Result: ${calculationResult.totalCount} points, ` +
          `duration: ${calculationResult.totalDurationMinutes} min, hash: ${calculationResult.inputHash}`
      );

      if (this.playbackParkingPoints.length > 0) {
        console.log(
          "[Parking] Details:",
          this.playbackParkingPoints.map((p) => ({
            P: p.Sno,
            Duration: p.stoppageTime + " min",
            From: p.signalTime,
            To: p.lastTime,
            Lat: p.lat,
            Lng: p.lng,
          }))
        );
      }
    } finally {
      this.isCalculatingParking = false;
    }
  }

  /**
   * @deprecated Use calculateParkingPointsFromData instead for consistent results
   */
  calculateParkingPoints(vehicleName: string): void {
    // Delegate to the new method using current playbackPoints
    this.calculateParkingPointsFromData(this.playbackPoints, vehicleName);
  }

  /**
   * Parse time string to milliseconds for sorting
   * @param timeStr - Time string to parse
   * @returns Milliseconds since epoch, or 0 if invalid
   */
  parseTimeToMs(timeStr: string): number {
    if (!timeStr) return 0;

    let momentTime = moment(timeStr, "DD-MM-YYYY HH:mm:ss", true);
    if (!momentTime.isValid()) {
      momentTime = moment(timeStr, "DD-MM-YYYY HH:mm", true);
    }

    return momentTime.isValid() ? momentTime.valueOf() : 0;
  }

  /**
   * Calculate parking duration in minutes between two time strings
   * @param startTime - Start time string
   * @param endTime - End time string
   * @returns Duration in minutes (0 if invalid)
   */
  calculateParkingDuration(startTime: string, endTime: string): number {
    if (!startTime || !endTime) {
      return 0;
    }

    // Try parsing with seconds format first
    let startMoment = moment(startTime, "DD-MM-YYYY HH:mm:ss", true);
    if (!startMoment.isValid()) {
      // Try without seconds
      startMoment = moment(startTime, "DD-MM-YYYY HH:mm", true);
    }

    let endMoment = moment(endTime, "DD-MM-YYYY HH:mm:ss", true);
    if (!endMoment.isValid()) {
      // Try without seconds
      endMoment = moment(endTime, "DD-MM-YYYY HH:mm", true);
    }

    if (!startMoment.isValid() || !endMoment.isValid()) {
      return 0;
    }

    const diffMs = endMoment.diff(startMoment);
    const diffMinutes = diffMs / 60000;

    // Return 0 if negative or invalid
    return diffMinutes > 0 ? diffMinutes : 0;
  }

  /**
   * Plot all parking markers on the map after calculation
   * This ensures all parking markers are displayed at once, not dynamically during animation
   */
  plotParkingMarkers(): void {
    if (
      !this.map ||
      !this.playbackParkingPoints ||
      this.playbackParkingPoints.length === 0
    ) {
      return;
    }

    // Get vehicle name
    const rawVehicle = this.allVehiclesRaw.find(
      (v) =>
        v.asrt_nm === this.playbackFilters.vehicleId ||
        v.asrt_id === this.playbackFilters.vehicleId
    );
    const selectedAsrtNm =
      rawVehicle?.asrt_nm || this.selectedVehicle?.id || "";

    // Clear existing parking markers from layer
    if (this.parkingLayer) {
      this.parkingLayer.clearLayers();
    }
    this.finalSpdMrkrs.forEach((marker) => {
      if (this.map && this.map.hasLayer(marker)) {
        this.map.removeLayer(marker);
      }
    });
    this.finalSpdMrkrs = [];

    // Ensure parking layer is added to map
    if (this.parkingLayer && !this.map.hasLayer(this.parkingLayer)) {
      this.parkingLayer.addTo(this.map);
    }

    // Plot all parking markers
    this.playbackParkingPoints.forEach((parkingPoint, index) => {
      const parkingNumber = index + 1;
      const markerColor =
        parkingPoint.stoppageTime >= 10 ? "#dc3545" : "#6f42c1"; // Red for >= 10 min, Purple for < 10 min

      // Create popup content
      const popupContent = `
        <div style="min-width: 200px;">
          <h6 style="margin: 0 0 8px 0; font-weight: 600;">Parking Point P${parkingNumber}</h6>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <tr>
              <td style="padding: 4px; font-weight: 600;">Vehicle:</td>
              <td style="padding: 4px;">${
                parkingPoint.asrt_nm || selectedAsrtNm || "--"
              }</td>
            </tr>
            <tr>
              <td style="padding: 4px; font-weight: 600;">From:</td>
              <td style="padding: 4px;">${
                parkingPoint.signalTime || parkingPoint.Signal || "--"
              }</td>
            </tr>
            <tr>
              <td style="padding: 4px; font-weight: 600;">To:</td>
              <td style="padding: 4px;">${parkingPoint.lastTime || "--"}</td>
            </tr>
            <tr>
              <td style="padding: 4px; font-weight: 600;">Duration:</td>
              <td style="padding: 4px;">${
                parkingPoint.stoppageTime || "--"
              } min</td>
            </tr>
            <tr>
              <td style="padding: 4px; font-weight: 600;">Latitude:</td>
              <td style="padding: 4px;">${parkingPoint.lat || "--"}</td>
            </tr>
            <tr>
              <td style="padding: 4px; font-weight: 600;">Longitude:</td>
              <td style="padding: 4px;">${parkingPoint.lng || "--"}</td>
            </tr>
          </table>
        </div>
      `;

      const customParkingIcon = L.divIcon({
        className: "custom-parking-marker",
        html: `<div style="background-color: ${markerColor}; color: white; width: 25px; height: 25px; display: flex; align-items: center; justify-content: center; border: 2px solid ${markerColor}; border-radius: 4px; font-weight: bold; font-size: 10px; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">P${parkingNumber}</div>`,
        iconSize: [25, 25],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12],
      });

      // Store point data in marker options
      const markerData = {
        sno: parkingNumber,
        Sno: parkingNumber,
        asrt_nm: parkingPoint.asrt_nm || selectedAsrtNm || "--",
        Signal: parkingPoint.signalTime || parkingPoint.Signal || "--",
        signalTime: parkingPoint.signalTime || parkingPoint.Signal || "--",
        lastTime: parkingPoint.lastTime || "--",
        stoppageTime: parkingPoint.stoppageTime || "--",
        lat: parkingPoint.lat,
        lng: parkingPoint.lng,
      };

      const parkingMarker = L.marker([parkingPoint.lat, parkingPoint.lng], {
        icon: customParkingIcon,
        data: markerData,
        zIndexOffset: 1000 + index, // Ensure parking markers appear above route lines
      }).bindPopup(popupContent, {
        maxWidth: 250,
      });

      // Add click handler
      parkingMarker.on("click", () => {
        parkingMarker.openPopup();
      });

      (parkingMarker as any).options.isPlayback = true;

      // Add marker to parking layer instead of directly to map
      this.parkingLayer.addLayer(parkingMarker);
      this.finalSpdMrkrs.push(parkingMarker);
    });

    console.log(
      `Plotted ${this.finalSpdMrkrs.length} parking markers on map along with vehicle movement`
    );
  }

  /**
   * Plot a single parking marker dynamically during playback
   * Called when vehicle reaches a parking point during playback animation
   * @param point - The playback point that contains parking data (Sno, stoppageTime, etc.)
   */
  plotSingleParkingMarker(point: any): void {
    if (!this.map || !point || !point.Sno || !point.stoppageTime) {
      return;
    }

    // Get vehicle name
    const rawVehicle = this.allVehiclesRaw.find(
      (v) =>
        v.asrt_nm === this.playbackFilters.vehicleId ||
        v.asrt_id === this.playbackFilters.vehicleId
    );
    const selectedAsrtNm =
      rawVehicle?.asrt_nm || this.selectedVehicle?.id || point.asrt_nm || "";

    // Ensure parking layer is added to map
    if (this.parkingLayer && !this.map.hasLayer(this.parkingLayer)) {
      this.parkingLayer.addTo(this.map);
    }

    const parkingNumber = Number(point.Sno);

    // Get the original parking data from playbackParkingPoints for consistent data
    // Use Number() to ensure type match when finding
    const originalParking = this.playbackParkingPoints.find(
      (p: any) => Number(p.Sno) === parkingNumber
    );

    // Log for debugging
    console.log(
      `[Parking Marker] Plotting P${parkingNumber}, Found in playbackParkingPoints:`,
      originalParking ? "Yes" : "No"
    );
    if (originalParking) {
      console.log(`[Parking Marker] P${parkingNumber} Data:`, {
        lat: originalParking.lat,
        lng: originalParking.lng,
        duration: originalParking.stoppageTime,
        from: originalParking.signalTime,
        to: originalParking.lastTime,
      });
    }

    // Use data from playbackParkingPoints (calculated from filtered data) for consistency
    const parkingLat = originalParking?.lat ?? point.parkingLat ?? point.lat;
    const parkingLng = originalParking?.lng ?? point.parkingLng ?? point.lng;
    const parkingStartTime =
      originalParking?.signalTime ??
      point.parkingStartTime ??
      point.signalTime ??
      point.Signal;
    const parkingEndTime =
      originalParking?.lastTime ?? point.parkingEndTime ?? point.lastTime;
    const parkingDuration =
      originalParking?.stoppageTime ??
      point.parkingDuration ??
      point.stoppageTime;
    const markerColor = parkingDuration >= 10 ? "#dc3545" : "#6f42c1"; // Red for >= 10 min, Purple for < 10 min

    // Create popup content with consistent data from parking calculation
    const popupContent = `
      <div style="min-width: 200px;">
        <h6 style="margin: 0 0 8px 0; font-weight: 600;">Parking Point P${parkingNumber}</h6>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <tr>
            <td style="padding: 4px; font-weight: 600;">Vehicle:</td>
            <td style="padding: 4px;">${selectedAsrtNm || "--"}</td>
          </tr>
          <tr>
            <td style="padding: 4px; font-weight: 600;">From:</td>
            <td style="padding: 4px;">${parkingStartTime || "--"}</td>
          </tr>
          <tr>
            <td style="padding: 4px; font-weight: 600;">To:</td>
            <td style="padding: 4px;">${parkingEndTime || "--"}</td>
          </tr>
          <tr>
            <td style="padding: 4px; font-weight: 600;">Duration:</td>
            <td style="padding: 4px;">${parkingDuration || "--"} min</td>
          </tr>
          <tr>
            <td style="padding: 4px; font-weight: 600;">Latitude:</td>
            <td style="padding: 4px;">${
              parkingLat?.toFixed ? parkingLat.toFixed(6) : parkingLat || "--"
            }</td>
          </tr>
          <tr>
            <td style="padding: 4px; font-weight: 600;">Longitude:</td>
            <td style="padding: 4px;">${
              parkingLng?.toFixed ? parkingLng.toFixed(6) : parkingLng || "--"
            }</td>
          </tr>
        </table>
      </div>
    `;

    const customParkingIcon = L.divIcon({
      className: "custom-parking-marker",
      html: `<div style="background-color: ${markerColor}; color: white; width: 25px; height: 25px; display: flex; align-items: center; justify-content: center; border: 2px solid ${markerColor}; border-radius: 4px; font-weight: bold; font-size: 10px; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">P${parkingNumber}</div>`,
      iconSize: [25, 25],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12],
    });

    // Store point data in marker options - use consistent data from parking calculation
    const markerData = {
      sno: parkingNumber,
      Sno: parkingNumber,
      asrt_nm: selectedAsrtNm || "--",
      Signal: parkingStartTime || "--",
      signalTime: parkingStartTime || "--",
      lastTime: parkingEndTime || "--",
      stoppageTime: parkingDuration || "--",
      lat: parkingLat,
      lng: parkingLng,
    };

    // Use parking lat/lng from the original calculation for consistent positioning
    const parkingMarker = L.marker([parkingLat, parkingLng], {
      icon: customParkingIcon,
      data: markerData,
      zIndexOffset: 1000 + parkingNumber, // Ensure parking markers appear above route lines
    }).bindPopup(popupContent, {
      maxWidth: 250,
    });

    // Add click handler
    parkingMarker.on("click", () => {
      parkingMarker.openPopup();
    });

    (parkingMarker as any).options.isPlayback = true;

    // Add marker to parking layer
    this.parkingLayer.addLayer(parkingMarker);
    this.finalSpdMrkrs.push(parkingMarker);

    console.log(
      `Plotted parking marker P${parkingNumber} at position during playback`
    );
  }

  initializePlayback(playData: any): void {
    if (!this.map || !playData) {
      return;
    }

    // Clear existing playback layers
    this.clearPlaybackData();
    this.finalSpdMrkrs = [];
    this.playbackPoints = [];
    this.playbackSourcePoints = []; // Reset immutable source data
    this.playbackPolylineCoords = []; // Reset polyline coordinates
    this.plottedParkingSno.clear(); // Reset tracking of plotted parking points for new playback
    this.fencInOutDtls = [];
    this.Route_collectPointArray = [];
    this.Route_collectPointArray1 = [];

    // Reset state variables for consistent processing across playback runs
    this.lastKnownSpeedCt = null;

    // Get vehicle name from raw data
    const rawVehicle = this.allVehiclesRaw.find(
      (v) =>
        v.asrt_nm === this.playbackFilters.vehicleId ||
        v.asrt_id === this.playbackFilters.vehicleId
    );
    const selectedAsrtNm =
      rawVehicle?.asrt_nm || this.selectedVehicle?.id || "";

    // Process fence details (like playback component)
    if (playData.fncDtls) {
      for (let i = 0; i < playData.fncDtls.length; i++) {
        this.fencInOutDtls.push({
          SNo: playData.fncDtls[i].SNo,
          fnce_id: playData.fncDtls[i].fnce_id,
          fnce_nm: playData.fncDtls[i].fnce_nm,
          fnc_in: playData.fncDtls[i].actl_arvl_ts,
          fnc_ut: playData.fncDtls[i].actl_dprte_ts,
          fnce_cvr_sts: playData.fncDtls[i].fnce_cvr_sts,
          stoppage: playData.fncDtls[i].stoppage,
          lati: playData.fncDtls[i].lat,
          longi: playData.fncDtls[i].lng,
          icn: playData.fncDtls[i].fncs_icn_pth,
          cvr_sts: playData.fncDtls[i].cvr_sts,
        });

        // Add fence markers (covered)
        if (playData.fncDtls[i].fnce_cvr_sts == 1) {
          const fenceIcon = new L.Icon({
            iconUrl: `assets/images/markers/mapIcons/m/${playData.fncDtls[i].fncs_icn_pth}`,
            iconSize: [32, 30],
            iconAnchor: [22, 45],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
          });
          const fenceMarker = L.marker(
            [playData.fncDtls[i].lat, playData.fncDtls[i].lng],
            {
              draggable: false,
              opacity: 5,
              icon: fenceIcon,
            }
          )
            .addTo(this.map)
            .bindPopup(
              `<div style='color: #000;'><b style='line-height: 21px; color: #1a56a2;'>Fence Name:  </b>${playData.fncDtls[i].fnce_nm}</div>`
            )
            .closePopup();
          const fenceCircle = L.circle(
            [playData.fncDtls[i].lat, playData.fncDtls[i].lng],
            50,
            {
              stroke: false,
              fillColor: "#ffde00",
              fillOpacity: 0.5,
            }
          ).addTo(this.map);
          (fenceMarker as any).options.isPlayback = true;
          (fenceCircle as any).options.isPlayback = true;
          this.Route_collectPointArray.push(fenceMarker, fenceCircle);
        }
        // Add fence markers (not covered)
        if (playData.fncDtls[i].fnce_cvr_sts == 0) {
          const fenceIcon = new L.Icon({
            iconUrl: `assets/images/markers/mapIcons/m/${playData.fncDtls[i].fncs_icn_pth}_o_m.png`,
            iconSize: [32, 30],
            iconAnchor: [22, 45],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
          });
          const fenceMarker = L.marker(
            [playData.fncDtls[i].lat, playData.fncDtls[i].lng],
            {
              draggable: false,
              opacity: 5,
              icon: fenceIcon,
            }
          )
            .addTo(this.map)
            .bindPopup(
              `<div style='color: #000;'><b style='line-height: 21px; color: #1a56a2;'>Fence Name:  </b>${playData.fncDtls[i].fnce_nm}</div>`
            )
            .closePopup();
          const fenceCircle = L.circle(
            [playData.fncDtls[i].lat, playData.fncDtls[i].lng],
            50,
            {
              stroke: false,
              fillColor: "#ffde00",
              fillOpacity: 0.5,
            }
          ).addTo(this.map);
          (fenceMarker as any).options.isPlayback = true;
          (fenceCircle as any).options.isPlayback = true;
          this.Route_collectPointArray1.push(fenceMarker, fenceCircle);
        }
      }
    }

    // Process playback data (like playback component)
    if (playData && playData.lat && playData.lat.length > 0) {
      const strt = playData.mapPoints[0].Signall;
      const end = playData.mapPoints[playData.mapPoints.length - 1].Signall;
      this.totalTravelTime = moment
        .utc(
          moment(end, "DD-MM-YYYY HH:mm").diff(moment(strt, "DD-MM-YYYY HH:mm"))
        )
        .format("HH:mm:ss");

      // Build playback points array - include ALL points including speed 0 points
      // Process data in sequential order
      this.playbackPoints = [];
      this.playbackDataPoints = [];

      // Process all points sequentially
      for (let c = 0; c < playData.lat.length; c++) {
        const mapPoint = playData.mapPoints[c];
        if (!mapPoint) continue;

        const point = {
          lat: playData.lat[c],
          lng: playData.lng[c],
          speed_ct: Number(mapPoint?.speed_ct || 0),
          Signal: mapPoint?.Signall || "",
          asrt_nm: selectedAsrtNm,
          drcn_ct: mapPoint?.drcn_ct || 0,
          dev_imei_nu: rawVehicle?.device_id || mapPoint?.device_id || "--",
          milage_ct: mapPoint?.milage_ct || mapPoint?.odmtr_ct || null,
          odmtr_ct: mapPoint?.odmtr_ct || mapPoint?.milage_ct || null,
          ignition_in:
            mapPoint?.ignition_in !== undefined ? mapPoint.ignition_in : null,
          sgnl_strngth_ct:
            mapPoint?.sgnl_strngth_ct !== undefined
              ? mapPoint.sgnl_strngth_ct
              : null,
          sno: mapPoint?.sno, // Will be reassigned sequentially after all points are added
        };
        this.playbackPoints.push(point);
        this.playbackDataPoints.push(point);
      }

      // Calculate travel distance from odometer readings (odmtr_ct) using RAW API data
      // Distance = last odometer - first odometer (directly from playData.mapPoints)
      // This ensures we use the actual first and last records from the API response
      if (playData.mapPoints && playData.mapPoints.length > 0) {
        const firstMapPoint = playData.mapPoints[0];
        const lastMapPoint = playData.mapPoints[playData.mapPoints.length - 1];
        this.startingOdometer =
          firstMapPoint?.odmtr_ct || firstMapPoint?.milage_ct || 0;
        this.lastKnownOdometer = this.startingOdometer; // Initialize last known odometer
        const endOdometer =
          lastMapPoint?.odmtr_ct || lastMapPoint?.milage_ct || 0;

        // Calculate final distance for display
        if (
          this.startingOdometer > 0 &&
          endOdometer > 0 &&
          endOdometer >= this.startingOdometer
        ) {
          const totalDist = endOdometer - this.startingOdometer;
          this.travelDist = totalDist.toFixed(2) + " km";
        } else {
          this.travelDist = "0.00 km";
        }

        console.log(
          `[Playback] API mapPoints: ${playData.mapPoints.length}, Processed points: ${this.playbackDataPoints.length}`
        );
        console.log(
          `[Playback] First Odometer: ${this.startingOdometer}, Last Odometer: ${endOdometer}, Distance: ${this.travelDist}`
        );
      }

      // Reassign sequential sno values to playbackDataPoints (ensures correct sno regardless of sorting)

      // Use the SAME points the table shows for the animation path, so the marker
      // visits every row and the polyline matches the table exactly.
      this.playbackPoints = JSON.parse(JSON.stringify(this.playbackDataPoints));
      console.log(
        `[Playback] Animation points: ${this.playbackPoints.length} (matches table)`
      );

      // CRITICAL: Create a deep copy BEFORE any mutations
      // This ensures parking calculation always uses the same stable input data
      const filteredDataSnapshot = JSON.parse(
        JSON.stringify(this.playbackPoints)
      );
      console.log(
        `[Playback] Data snapshot created: ${filteredDataSnapshot.length} points`
      );

      // Calculate parking points from the STABLE SNAPSHOT (not from playbackPoints which gets mutated)
      // This ensures parking points are deterministic and consistent every time
      this.calculateParkingPointsFromData(filteredDataSnapshot, selectedAsrtNm);

      // Store parking data indexed by timestamp for re-application after smoothing
      const parkingDataByTimestamp = new Map<string, any>();
      this.playbackParkingPoints.forEach((parking: any) => {
        if (parking.signalTime) {
          parkingDataByTimestamp.set(parking.signalTime, {
            Sno: parking.Sno,
            sno: parking.sno,
            stoppageTime: parking.stoppageTime,
            lastTime: parking.lastTime,
            signalTime: parking.signalTime,
            isParking: true,
            parkingNumber: parking.Sno,
            parkingLat: parking.lat,
            parkingLng: parking.lng,
            parkingStartTime: parking.signalTime,
            parkingEndTime: parking.lastTime,
            parkingDuration: parking.stoppageTime,
          });
        }
      });

      // Apply parking data to playbackPoints BEFORE smoothing
      this.playbackPoints.forEach((point: any) => {
        if (point.Signal && parkingDataByTimestamp.has(point.Signal)) {
          const parkingData = parkingDataByTimestamp.get(point.Signal);
          Object.assign(point, parkingData);
        }
      });

      // Skip smoothing: animation must pass through every table row exactly.
      // Smoothing would interpolate Chaikin curves between anchors and the polyline
      // would no longer pass through moving rows (speed > 0).

      // Re-apply parking data to smoothed points by matching timestamps
      // This ensures parking markers appear at correct positions even after smoothing
      this.playbackPoints.forEach((point: any) => {
        if (point.Signal && parkingDataByTimestamp.has(point.Signal)) {
          const parkingData = parkingDataByTimestamp.get(point.Signal);
          point.Sno = parkingData.Sno;
          point.sno = parkingData.sno;
          point.stoppageTime = parkingData.stoppageTime;
          point.lastTime = parkingData.lastTime;
          point.signalTime = parkingData.signalTime;
          point.isParking = parkingData.isParking;
          point.parkingNumber = parkingData.parkingNumber;
          point.parkingLat = parkingData.parkingLat;
          point.parkingLng = parkingData.parkingLng;
          point.parkingStartTime = parkingData.parkingStartTime;
          point.parkingEndTime = parkingData.parkingEndTime;
          point.parkingDuration = parkingData.parkingDuration;
        }
      });

      // Store immutable source data for consistent display across playback runs
      // This ensures parking points, polyline, and data are consistent every time
      this.playbackSourcePoints = JSON.parse(
        JSON.stringify(this.playbackPoints)
      );

      // Draw the polyline through ALL table rows (playbackDataPoints) so every clickable
      // row is visible on the line. Animation still runs on the smoothed/filtered set;
      // parking markers come from the filtered subset which is contained in this data,
      // so they remain on the line.
      this.playbackPolylineCoords = (this.playbackDataPoints || [])
        .filter(
          (p: any) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng)
        )
        .map((p: any) => [p.lat, p.lng] as [number, number]);

      // Log consistency info
      console.log(
        `[Playback Consistency] Source points: ${this.playbackSourcePoints.length}, Polyline coords: ${this.playbackPolylineCoords.length}, Data points: ${this.playbackDataPoints.length}, Parking points: ${this.playbackParkingPoints.length}`
      );

      // Add route polyline if route data exists (like playback component)
      if (playData.rte_arc_lne_tx) {
        try {
          const routePolyline = L.polyline(
            JSON.parse(playData.rte_arc_lne_tx),
            {
              color: "#008000",
              weight: 5,
              lineCap: "butt",
              opacity: 0.5,
              smoothFactor: 10.0,
              lineJoin: "round",
            }
          ).addTo(this.map);
          (routePolyline as any).options.isPlayback = true;
        } catch (e) {
          console.warn("Could not parse route polyline:", e);
        }
      }

      // Add start and end markers
      const startIcon = L.icon({
        iconUrl: "assets/images/markers/playback/startPoint.png",
        iconSize: [42, 45],
        iconAnchor: [12, 41],
      });
      const startMarker = L.marker(
        [this.playbackPoints[0].lat, this.playbackPoints[0].lng],
        { icon: startIcon }
      ).addTo(this.map);
      startMarker.bindPopup(
        `<b>Vehicle No : ${this.playbackPoints[0].asrt_nm}<br>Starting Time : ${this.playbackPoints[0].Signal}</b>`
      );
      (startMarker as any).options.isPlayback = true;

      const endIcon = L.icon({
        iconUrl: "assets/images/markers/playback/endPoint.png",
        iconSize: [42, 45],
        iconAnchor: [12, 41],
      });
      const endMarker = L.marker(
        [
          this.playbackPoints[this.playbackPoints.length - 1].lat,
          this.playbackPoints[this.playbackPoints.length - 1].lng,
        ],
        { icon: endIcon }
      ).addTo(this.map);
      endMarker.bindPopup(
        `<b>Vehicle No : ${
          this.playbackPoints[this.playbackPoints.length - 1].asrt_nm
        }<br>Ending Time : ${
          this.playbackPoints[this.playbackPoints.length - 1].Signal
        }</b>`
      );
      (endMarker as any).options.isPlayback = true;

      // Draw the full route upfront so user sees the entire path immediately
      if (this.playbackOverviewLine) {
        this.map.removeLayer(this.playbackOverviewLine);
      }
      if (
        this.playbackPolylineCoords &&
        this.playbackPolylineCoords.length > 1
      ) {
        this.playbackOverviewLine = L.polyline(this.playbackPolylineCoords, {
          color: "#0088cc",
          weight: 3,
          opacity: 0.45,
          smoothFactor: 1.5,
          lineJoin: "round",
          lineCap: "round",
        }).addTo(this.map);
        (this.playbackOverviewLine as any).options.isPlayback = true;
        this.map.fitBounds(this.playbackOverviewLine.getBounds(), {
          padding: [40, 40],
        });
      }

      // Create polyline for animated route (trail behind the moving marker)
      this.playbackLine = L.polyline([], {
        color: "blue",
        weight: 4,
        opacity: 0.8,
        smoothFactor: 2.0,
        lineJoin: "round",
        lineCap: "round",
      }).addTo(this.map);
      (this.playbackLine as any).options.isPlayback = true;

      // Plot all parking markers upfront so they are visible immediately along the playback path
      this.plotParkingMarkers();
      this.playbackParkingPoints.forEach((p: any) =>
        this.plottedParkingSno.add(p.Sno)
      );

      // Show speed and trip details panels
      this.isPlaybackPlaying = true;

      // Create a working copy for animation from immutable source (source data remains unchanged)
      // This ensures consistent data when re-running playback without refetching
      this.playbackPoints = JSON.parse(
        JSON.stringify(this.playbackSourcePoints)
      );

      // Start playback animation
      this.pausePlayback = false;
      this.playbackTimerId = this.updatePlayback();
    }
  }

  updatePlayback(): any {
    if (!this.pausePlayback && this.playbackPoints.length > 0) {
      const speedMultiplier = this.calculateSpeedMultiplier();
      const baseDelay = 150; // Increased base delay for smoother animation
      const speedval = Math.max(30, Math.round(baseDelay / speedMultiplier)); // Minimum 30ms for smoothness

      return setTimeout(() => {
        if (this.pausePlayback) {
          // If paused, don't continue but don't mark as completed
          return;
        }

        if (this.playbackPoints.length === 0) {
          // Mark playback as completed when all points are played AND plotting is done
          this.playbackCompleted = true;
          this.isPlaybackPlaying = false;
          this.playbackTimerId = null;
          this.previousPlaybackPoint = null;

          // Ensure data is available for export
          console.log(
            "Playback completed. Data points:",
            this.playbackDataPoints.length,
            "Parking points:",
            this.playbackParkingPoints.length
          );

          // Force change detection to update export buttons
          this.cdr.detectChanges();

          // Show trip completion message (matching play-back.component.ts line 1638)
          try {
            this.message.success("TRIP COMPLETED", {
              nzDuration: 2000,
            });
          } catch (e) {
            console.log("Error showing trip completion message:", e);
          }
          return;
        }

        const nextPoint = this.playbackPoints.shift();
        this.redrawPlayback(nextPoint, this.previousPlaybackPoint);
        this.previousPlaybackPoint = nextPoint;
        this.playbackTimerId = this.updatePlayback();
      }, speedval);
    } else if (
      !this.pausePlayback &&
      this.playbackPoints.length === 0 &&
      !this.playbackCompleted
    ) {
      // Handle case where playback finishes but completion wasn't set
      this.playbackCompleted = true;
      this.isPlaybackPlaying = false;
      this.playbackTimerId = null;
      this.previousPlaybackPoint = null;
      this.cdr.detectChanges();
    }
  }

  calculateSpeedMultiplier(): number {
    if (this.playbackSpeedValue <= 100) return 0.5;
    if (this.playbackSpeedValue <= 150) return 1.0;
    if (this.playbackSpeedValue <= 200) return 2.0;
    return 3.0;
  }

  redrawPlayback(point: any, previousPoint?: any): void {
    if (!this.map || !point) return;

    // Get vehicle name for parking points
    const rawVehicle = this.allVehiclesRaw.find(
      (v) =>
        v.asrt_nm === this.playbackFilters.vehicleId ||
        v.asrt_id === this.playbackFilters.vehicleId
    );
    const selectedAsrtNm =
      rawVehicle?.asrt_nm || this.selectedVehicle?.id || "";

    // Calculate and update live distance using odometer readings with smooth transitions
    // Live distance = current point odometer - starting odometer
    const currentOdometer = point?.odmtr_ct || point?.milage_ct || 0;

    if (
      this.startingOdometer > 0 &&
      currentOdometer > 0 &&
      currentOdometer >= this.startingOdometer
    ) {
      // Valid odometer reading - update distance and store as last known
      this.lastKnownOdometer = currentOdometer;
      const liveDistance = currentOdometer - this.startingOdometer;
      this.travelDist = liveDistance.toFixed(2) + " km";
      console.log(
        `[Odometer] Point odometer: ${currentOdometer}, Start: ${
          this.startingOdometer
        }, Distance: ${liveDistance.toFixed(2)} km`
      );
    } else if (this.startingOdometer > 0 && this.lastKnownOdometer > 0) {
      // No valid odometer for this point - use last known odometer for smooth display
      // Optionally interpolate using GPS distance from previous point
      if (
        previousPoint &&
        previousPoint.lat &&
        previousPoint.lng &&
        point.lat &&
        point.lng &&
        this.map
      ) {
        const gpsDistance = this.map.distance(
          [previousPoint.lat, previousPoint.lng],
          [point.lat, point.lng]
        );
        if (!isNaN(gpsDistance) && gpsDistance > 0) {
          // Add small GPS distance to last known odometer for smooth transition
          const interpolatedOdometer =
            this.lastKnownOdometer + gpsDistance / 1000;
          this.lastKnownOdometer = interpolatedOdometer;
          const liveDistance = interpolatedOdometer - this.startingOdometer;
          this.travelDist = liveDistance.toFixed(2) + " km";
        }
        console.log(
          `[Odometer Interpolated] Start: ${
            this.startingOdometer
          }, LastKnown: ${this.lastKnownOdometer.toFixed(2)}`
        );
      }
    }

    // Update speed and timestamp
    if (typeof point.speed_ct === "number" && !isNaN(point.speed_ct)) {
      this.lastKnownSpeedCt = point.speed_ct;
      this.trackVehicleSpeed = point.speed_ct + " Kms";
    } else if (this.lastKnownSpeedCt !== null) {
      this.trackVehicleSpeed = this.lastKnownSpeedCt + " Kms";
    }

    if (point.Signal) {
      this.trackVehicleTimestamp = point.Signal;
      try {
        const m = (moment as any)(point.Signal, "DD-MM-YYYY HH:mm");
        if (m && m.isValid && m.isValid()) {
          this.currentPlaybackTimeMs = m.valueOf();
          const d = m.toDate();
          this.trackVehicleTimestamp =
            this.datepipe.transform(d, "dd-MM-yyyy HH:mm") || "--";
          this.actualTimeDisplay =
            this.datepipe.transform(d, "dd-MM-yyyy HH:mm:ss") || "--";
        } else {
          // Try parsing with seconds format
          const m2 = (moment as any)(point.Signal, "DD-MM-YYYY HH:mm:ss");
          if (m2 && m2.isValid && m2.isValid()) {
            this.currentPlaybackTimeMs = m2.valueOf();
            const d = m2.toDate();
            this.trackVehicleTimestamp =
              this.datepipe.transform(d, "dd-MM-yyyy HH:mm") || "--";
            this.actualTimeDisplay =
              this.datepipe.transform(d, "dd-MM-yyyy HH:mm:ss") || "--";
          } else {
            this.actualTimeDisplay = this.trackVehicleTimestamp;
          }
        }
      } catch {
        this.actualTimeDisplay = this.trackVehicleTimestamp;
      }
    } else if (this.currentPlaybackTimeMs != null) {
      this.currentPlaybackTimeMs += 100;
      try {
        const d = new Date(this.currentPlaybackTimeMs);
        this.trackVehicleTimestamp =
          this.datepipe.transform(d, "dd-MM-yyyy HH:mm") || "--";
        this.actualTimeDisplay =
          this.datepipe.transform(d, "dd-MM-yyyy HH:mm:ss") || "--";
      } catch {
        this.actualTimeDisplay = this.trackVehicleTimestamp;
      }
    }

    // Plot parking marker dynamically if this point marks the start of a parking event
    // Check if current point has parking data (Sno and stoppageTime) and hasn't been plotted yet
    if (
      point.Sno &&
      point.stoppageTime &&
      point.stoppageTime > 0 &&
      !this.plottedParkingSno.has(point.Sno)
    ) {
      this.plotSingleParkingMarker(point);
      this.plottedParkingSno.add(point.Sno);
    }

    // Update vehicle marker with smooth animation
    const asrtIcon = L.icon({
      iconUrl: "assets/images/markers/playback/startPoint2.png",
      iconSize: [41, 25],
      iconAnchor: [22, 25],
      popupAnchor: [1, -34],
    });

    // Smooth marker movement (like zomato/swiggy)
    const prevPoint = previousPoint || this.previousPlaybackPoint;
    let prevLatLng: L.LatLng | null = null;

    if (this.playbackMarker && prevPoint) {
      prevLatLng = this.playbackMarker.getLatLng();

      // Calculate distance for smooth interpolation
      const distance = this.map.distance(
        [prevPoint.lat, prevPoint.lng],
        [point.lat, point.lng]
      );

      // If distance is large, use smooth panTo instead of instant setView
      if (distance > 50) {
        // Smooth transition using panTo with duration
        this.map.panTo([point.lat, point.lng], {
          animate: true,
          duration: 0.5,
          easeLinearity: 0.25,
        });

        // Smooth marker movement
        const startLat = prevPoint.lat;
        const startLng = prevPoint.lng;
        const endLat = point.lat;
        const endLng = point.lng;
        const steps = Math.min(Math.max(Math.ceil(distance / 10), 2), 10); // 2-10 steps

        let currentStep = 0;
        const moveMarker = () => {
          if (currentStep >= steps) {
            // Final position
            if (this.playbackMarker) {
              this.playbackMarker.setLatLng([endLat, endLng]);
              // Calculate and set direction for final position
              this.updateMarkerDirection(
                this.playbackMarker,
                prevLatLng,
                point
              );
            }
            return;
          }

          currentStep++;
          const t = currentStep / steps;
          // Ease-in-out curve for smooth animation
          const easedT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

          const currentLat = startLat + (endLat - startLat) * easedT;
          const currentLng = startLng + (endLng - startLng) * easedT;

          if (this.playbackMarker) {
            this.playbackMarker.setLatLng([currentLat, currentLng]);
          }

          if (currentStep < steps) {
            setTimeout(moveMarker, 50 / steps); // Distribute 50ms across steps
          }
        };

        moveMarker();
      } else {
        // Small distance - instant update
        if (this.playbackMarker) {
          this.playbackMarker.setLatLng([point.lat, point.lng]);
          // Calculate and set direction
          this.updateMarkerDirection(this.playbackMarker, prevLatLng, point);
        }
        this.map.panTo([point.lat, point.lng], {
          animate: true,
          duration: 0.3,
        });
      }
    } else {
      // First point or no previous point
      if (this.playbackMarker) {
        this.map.removeLayer(this.playbackMarker);
      }
      this.playbackMarker = L.marker([point.lat, point.lng], {
        icon: asrtIcon,
      }).addTo(this.map);
      (this.playbackMarker as any).options.isPlayback = true;
      // Set initial direction if available
      this.updateMarkerDirection(this.playbackMarker, null, point);
    }

    // Add point to polyline
    if (this.playbackLine) {
      this.playbackLine.addLatLng([point.lat, point.lng]);
    }

    // Store current point as previous for next iteration
    this.previousPlaybackPoint = point;
  }

  /**
   * Update marker direction/rotation angle (like playback component lines 1586-1596)
   */
  private updateMarkerDirection(
    marker: L.Marker,
    prevLatLng: L.LatLng | null,
    point: any
  ): void {
    if (!marker || !(marker as any).setRotationAngle) {
      return;
    }

    // Compute direction using turf.bearing (fallback to drcn_ct or lastDirectionAngle)
    let initBearing =
      typeof point.drcn_ct === "number"
        ? point.drcn_ct
        : this.lastDirectionAngle;

    try {
      if (prevLatLng) {
        const prev = [prevLatLng.lng, prevLatLng.lat];
        const curr = [point.lng, point.lat];
        initBearing = turf.bearing(prev as any, curr as any);
      }
    } catch (e) {
      console.warn("Error calculating bearing:", e);
    }

    // Apply rotation angle directly (matching play-back.component.ts line 1595)
    marker.setRotationAngle(initBearing);
    this.lastDirectionAngle = initBearing;
  }

  getPlaybackByTrip(): void {
    console.log("getPlaybackByTrip - trp_rn_id:", this.playbackTrip.tripId);
    this.playbackLoading = true;
    this.clearPlaybackData();
    this.clearParkingData();
    this.playbackCompleted = false;
    this.isPlaybackPlaying = false;

    // Get asrt_id from raw vehicle data
    const rawVehicle = this.allVehiclesRaw.find(
      (v) =>
        v.asrt_nm === this.playbackFilters.vehicleId ||
        v.asrt_id === this.playbackFilters.vehicleId
    );
    const asrt_id = rawVehicle?.asrt_id || this.playbackFilters.vehicleId;

    // Get trp_rn_id - this is the main element for trip-wise playback
    const trp_rn_id = this.playbackTrip.tripId || 0;

    // Find selected trip data for driver details and timing (optional - backend will use trp_rn_id)
    let sdate: string = "";
    let edate: string = "";

    if (trp_rn_id && this.availableTripsData.length > 0) {
      const selectedTrip = this.availableTripsData.find(
        (trip: any) =>
          trip.trip_rn_id == trp_rn_id || trip.trp_rn_id == trp_rn_id
      );

      if (selectedTrip) {
        this.selectedTripData = selectedTrip; // Store for driver details
        // Extract timing if available (backend can also fetch from trp_rn_id)
        sdate = selectedTrip.actl_strt_ts || selectedTrip.actl_start_ts || "";
        edate = selectedTrip.actl_end_ts || selectedTrip.actl_end_ts || "";
      }
    }

    // Format timing if available, otherwise backend will use trp_rn_id to fetch timing
    // Convert to YYYY-MM-DD HH:mm format for API
    if (sdate && edate) {
      // Check if already in dd-MM-yyyy HH:mm format
      if (sdate.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}/)) {
        sdate = moment(sdate, "DD-MM-YYYY HH:mm").format("YYYY-MM-DD HH:mm");
      } else {
        sdate = moment(sdate).format("YYYY-MM-DD HH:mm") || sdate;
      }
      if (edate.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}/)) {
        edate = moment(edate, "DD-MM-YYYY HH:mm").format("YYYY-MM-DD HH:mm");
      } else {
        edate = moment(edate).format("YYYY-MM-DD HH:mm") || edate;
      }
    } else {
      // If timing not available, use default (backend will prioritize trp_rn_id)
      const today = new Date();
      const fromDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0
      );
      const toDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59
      );
      sdate = this.datepipe.transform(fromDate, "yyyy-MM-dd HH:mm") || "";
      edate = this.datepipe.transform(toDate, "yyyy-MM-dd HH:mm") || "";
    }

    // trp_rn_id is the main element - backend will use it to fetch trip timing
    const data = {
      checkbit: "byVeh",
      sdate: sdate, // Optional - backend can fetch from trp_rn_id
      edate: edate, // Optional - backend can fetch from trp_rn_id
      asrt_id: asrt_id,
      trp_rn_id: trp_rn_id, // Main element for trip-wise playback
    };

    console.log("Trip Playback API call (trp_rn_id is main element):", data);
    this.api.post(data, "playback/vehicle/playback").subscribe(
      (res) => {
        const playData = res?.["data"];
        if (playData) {
          // Initialize playback animation (like playback component)
          this.initializePlayback(playData);
        }
        this.playbackLoading = false;
      },
      (err) => {
        console.error("Playback API error:", err);
        this.playbackLoading = false;
      }
    );
  }

  plotPlaybackRouteFromAPI(playData: any): void {
    if (!this.map || !playData) {
      return;
    }

    // Clear existing playback layers
    this.clearPlaybackData();

    // Plot route polyline if available
    if (playData.rtplydata) {
      const rtplydata =
        typeof playData.rtplydata === "string"
          ? JSON.parse(playData.rtplydata)
          : playData.rtplydata;
      if (rtplydata && rtplydata.length > 0) {
        const latlngs = rtplydata
          .map((p: any) => [p.lat, p.lng])
          .filter((p: any) => p[0] && p[1]);

        if (latlngs.length > 0) {
          const polyline = L.polyline(latlngs, {
            color: "#2563eb",
            weight: 4,
            opacity: 0.7,
            smoothFactor: 1.0,
          }).addTo(this.map);

          (polyline as any).options.isPlayback = true;

          // Add start and end markers
          const startIcon = L.icon({
            iconUrl: "assets/images/markers/playback/startPoint.png",
            iconSize: [42, 45],
            iconAnchor: [12, 41],
          });
          const startMarker = L.marker(latlngs[0], { icon: startIcon }).addTo(
            this.map
          );
          (startMarker as any).options.isPlayback = true;

          const endIcon = L.icon({
            iconUrl: "assets/images/markers/playback/endPoint.png",
            iconSize: [42, 45],
            iconAnchor: [12, 41],
          });
          const endMarker = L.marker(latlngs[latlngs.length - 1], {
            icon: endIcon,
          }).addTo(this.map);
          (endMarker as any).options.isPlayback = true;

          // Fit map to route bounds
          this.map.fitBounds(polyline.getBounds());
        }
      }
    }

    // Plot fence details if available (like playback component)
    if (playData.fncDtls && Array.isArray(playData.fncDtls)) {
      playData.fncDtls.forEach((fence: any) => {
        if (fence.lat && fence.lng) {
          const iconPath = fence.fncs_icn_pth || "fenceIcon";
          const iconSuffix = fence.fnce_cvr_sts == 1 ? "_r_m" : "_o_m";
          const icon = L.icon({
            iconUrl: `assets/images/markers/mapIcons/m/${iconPath}.png`,
            iconSize: [32, 30],
            iconAnchor: [22, 45],
            popupAnchor: [1, -34],
          });

          const marker = L.marker([fence.lat, fence.lng], { icon }).addTo(
            this.map
          );
          marker.bindPopup(`<b>Fence Name:</b> ${fence.fnce_nm}`).closePopup();
          (marker as any).options.isPlayback = true;

          // Add circle
          const circle = L.circle([fence.lat, fence.lng], {
            radius: 50,
            stroke: false,
            fillColor: "#ffde00",
            fillOpacity: 0.5,
          }).addTo(this.map);
          (circle as any).options.isPlayback = true;
        }
      });
    }
  }

  extractParkingPoints(mapPoints: any[]): void {
    // Extract parking points from mapPoints (similar to playback component)
    this.playbackParkingPoints = [];
    if (!mapPoints || mapPoints.length === 0) {
      return;
    }

    // Process parking points similar to playback component logic
    // This will be populated when playback is run
    mapPoints.forEach((point: any, index: number) => {
      if (point.isParking || point.stoppageTime) {
        this.playbackParkingPoints.push({
          sno: this.playbackParkingPoints.length + 1,
          asrt_nm: this.selectedVehicle?.id || "--",
          Signal: point.signalTime || "--",
          lastTime: point.lastTime || "--",
          stoppageTime: point.stoppageTime || "--",
          lat: point.lat || "--",
          lng: point.lng || "--",
        });
      }
    });
  }

  plotPlaybackRouteFromTrip(rtplydata: any[]): void {
    if (!this.map || !rtplydata || rtplydata.length === 0) {
      return;
    }

    const latlngs = rtplydata
      .map((p: any) => [p.lat, p.lng])
      .filter((p: any) => p[0] && p[1]);

    if (latlngs.length > 0) {
      const polyline = L.polyline(latlngs, {
        color: "#008000",
        weight: 5,
        opacity: 0.6,
        smoothFactor: 10.0,
      }).addTo(this.map);

      (polyline as any).options.isPlayback = true;
      (polyline as any).options.playbackType = "trip";

      // Add start and end markers
      const startIcon = L.icon({
        iconUrl: "assets/images/markers/playback/startPoint.png",
        iconSize: [42, 45],
        iconAnchor: [12, 41],
      });
      const startMarker = L.marker(latlngs[0], { icon: startIcon }).addTo(
        this.map
      );
      (startMarker as any).options.isPlayback = true;

      const endIcon = L.icon({
        iconUrl: "assets/images/markers/playback/endPoint.png",
        iconSize: [42, 45],
        iconAnchor: [12, 41],
      });
      const endMarker = L.marker(latlngs[latlngs.length - 1], {
        icon: endIcon,
      }).addTo(this.map);
      (endMarker as any).options.isPlayback = true;

      this.map.fitBounds(polyline.getBounds());
    }
  }

  focusOnParking(point: ParkingPoint): void {
    if (!this.map) {
      return;
    }
    this.map.flyTo([point.lat, point.lng], 15, {
      animate: true,
      duration: 0.8,
    });
  }

  onTenantChange(): void {
    this.selectedVehicleGroup = 0;
    this.selectedVehicleCategory = 0;
    this.selectedLocationGroup = 0;
    this.isLoading = true;
    this.isSummaryLoading = true;
    this.fetchSelectionData();
    this.fetchLiveVehicles();
  }

  onLocationGroupChange(): void {
    // Location filters should NOT affect vehicle plotting - only location markers
    // Don't call applyVehicleFilters() here
    if (this.selectedLocationGroup) {
      this.fetchLocationList();
      // Plot locations for both livetrack and playback modes
      if (this.activeHeaderTab === "livetrack") {
        this.plotLocationsOnMap();
      } else if (this.activeHeaderTab === "playback") {
        this.plotPlaybackLocations();
      }
    } else {
      this.locationList = [];
      this.selectedLocation = "deselect";
      this.clearLocationMarkers();
    }
  }

  plotPlaybackLocations(): void {
    // Use same logic as livetrack but for playback map
    this.plotLocationsOnMap();
  }

  fetchLocationList(): void {
    if (!this.selectedLocationGroup) {
      return;
    }
    // Fetch locations for the selected group (similar to livetrack's fncsByGrp)
    this.api
      .get(`playback/landmark/bygroups/${this.selectedLocationGroup}`)
      .subscribe((res) => {
        this.locationList = res?.["data"] ?? [];
        if (this.locationList.length > 0) {
          this.selectedLocation = "select"; // Default to showing all
          this.plotLocationsOnMap();
        }
      });
  }

  onLocationChange(): void {
    this.plotLocationsOnMap();
  }

  private closeAllOverlayPanels(): void {
    this.showFiltersPanel = false;
    this.activeTool = null;
    this.showLocationFilter = false;
    this.showVehicleStatusLegend = false;
    this.showSpeedSlider = false;
  }

  toggleFiltersPanel(): void {
    const opening = !this.showFiltersPanel;
    this.closeAllOverlayPanels();
    this.showFiltersPanel = opening;
  }

  toggleVehicleDetailsSlider(): void {
    this.showVehicleDetailsSlider = !this.showVehicleDetailsSlider;
  }

  setPlaybackMode(mode: "range" | "trip"): void {
    // Clear playback data when switching modes
    this.clearPlaybackData();
    this.playbackMode = mode;

    // Reset filters based on mode
    if (mode === "trip") {
      this.playbackTrip.date = this.buildLocalDateValue(0); // Default to current date
      this.playbackTrip.tripId = "";
    } else {
      // Set default playback dates: current date 00:00:00 to 23:59:59 (24hr format)
      const today = new Date();
      const fromDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0
      );
      const toDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59
      );
      this.playbackFilters.from = this.formatDateTimeLocal24Hr(fromDate);
      this.playbackFilters.to = this.formatDateTimeLocal24Hr(toDate);
    }
  }

  activateHeaderTab(tab: HeaderTabKey): void {
    if (this.collapsedPanels[tab]) {
      this.collapsedPanels[tab] = false;
    }

    const previousTab = this.activeHeaderTab;
    this.activeHeaderTab = tab;

    // Manage auto-refresh interval based on mode
    if (tab === "livetrack") {
      // Start auto-refresh when switching to livetrack
      this.startLiveTrackAutoRefresh();
    } else if (tab === "playback") {
      // Stop auto-refresh when switching to playback
      this.stopLiveTrackAutoRefresh();
    }

    // Clear vehicles when switching modes
    if (previousTab !== tab) {
      if (tab === "playback") {
        // Clear all livetrack markers and layers when switching to playback
        this.clearAllLiveTrackMarkers();
        this.showVehicleSlider = false;
        this.clearPlaybackData();
        // Update playbackVehicles for playback mode and apply group/category filters
        this.playbackVehicles = this.allVehiclesRaw.map((vehicle, idx) =>
          this.mapVehicleToDisplay(vehicle, idx)
        );
        this.filterPlaybackVehicles();
        // Zoom to vehicle location using tenant lat/lng
        this.zoomToVehicleLocation();
      } else if (tab === "livetrack") {
        // Clear playback data and re-render livetrack vehicles
        this.clearPlaybackData();
        this.showVehicleSlider = false;
        // Clear selected vehicle details when switching from playback to livetrack
        this.selectedVehicle = null;
        this.selectedVehicleRaw = null;
        this.selectedVehicleRawSnapshot = null; // Clear snapshot when switching modes
        this.playbackFilters.vehicleId = "";
        this.playbackTrip.tripId = "";
        this.playbackTrip.date = this.buildLocalDateValue(0);
        // Update playbackVehicles for livetrack mode (use filtered vehicles)
        this.playbackVehicles = this.vehicles;
        // Re-render vehicles after a short delay to ensure map is ready
        setTimeout(() => {
          this.renderVehicleMarkers();
        }, 100);
        // Zoom to tenant location for livetrack
        this.zoomToTenantLocation();
      }
    }

    // When switching to livetrack, show vehicle slider if vehicle is selected
    if (tab === "livetrack" && this.selectedVehicle) {
      const currentVehicleId =
        this.selectedVehicleRaw?.asrt_id?.toString() ||
        this.selectedVehicleRaw?.asrt_nm?.toString();

      // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
      if (
        this.selectedVehicleRaw &&
        this.previousSelectedVehicleId !== currentVehicleId
      ) {
        this.selectedVehicleRawSnapshot = JSON.parse(
          JSON.stringify(this.selectedVehicleRaw)
        );
        this.previousSelectedVehicleId = currentVehicleId;
      }
      this.showVehicleSlider = true;
    }
  }

  startLiveTrackAutoRefresh(): void {
    // Clear existing interval if any
    this.stopLiveTrackAutoRefresh();

    // Only start interval if in livetrack mode
    if (this.activeHeaderTab === "livetrack") {
      // Fetch immediately first time, then set interval for every 10 seconds
      // This ensures vehicles are updated right away and move smoothly
      this.fetchLiveVehicles();

      // Set interval for subsequent refreshes (matching live-tracking approach with 10 seconds)
      this.liveTrackRefreshInterval = setInterval(() => {
        if (this.activeHeaderTab === "livetrack") {
          console.log(
            "Auto-refreshing livetrack vehicles (every 10 seconds)..."
          );
          this.fetchLiveVehicles();
        } else {
          // Stop if mode changed
          this.stopLiveTrackAutoRefresh();
        }
      }, 10000); // 10 seconds = 10000 milliseconds (faster refresh for smoother movement)
    }
  }

  stopLiveTrackAutoRefresh(): void {
    if (this.liveTrackRefreshInterval) {
      clearInterval(this.liveTrackRefreshInterval);
      this.liveTrackRefreshInterval = null;
    }
  }

  toggleModeButton(): void {
    if (this.activeHeaderTab === "livetrack") {
      // Switch to playback if vehicle is selected
      // if (this.selectedVehicle) {
      // Clear all live track markers before switching to playback
      this.clearAllLiveTrackMarkers();
      this.activeHeaderTab = "playback";
      this.showPlaybackPanel = true;
      this.showPlaybackBottomPanel = false; // Panel closed by default, user can open it
      this.showVehicleSlider = false; // Close vehicle slider when switching to playback
      this.selectedVehicleRawSnapshot = null; // Clear snapshot when switching to playback
      // Load playback data when switching to playback
      this.loadPlaybackData();
      // }
    } else {
      // Switch to livetrack
      this.activeHeaderTab = "livetrack";
      this.showPlaybackPanel = false;
      this.showPlaybackBottomPanel = false; // Close bottom panel when switching to livetrack
      // Clear playback data
      this.clearPlaybackData();
      // Clear selected vehicle and show all vehicles
      this.selectedVehicle = null;
      this.selectedVehicleRaw = null;
      this.selectedVehicleRawSnapshot = null; // Clear snapshot
      this.showVehicleSlider = false; // Hide vehicle slider
      this.activeSummaryKey = "total"; // Show total vehicles by default
      this.updateStsList(); // Update table to show all vehicles
    }
  }

  togglePlaybackBottomPanel(): void {
    this.showPlaybackBottomPanel = !this.showPlaybackBottomPanel;
  }

  loadPlaybackData(): void {
    if (this.activeHeaderTab === "playback" && this.selectedVehicle) {
      // Fetch trips for selected vehicle
      this.updateAvailableTrips();
      // Load location filters from playback component logic
      this.loadPlaybackLocationFilters();
    }
  }

  clearPlaybackData(): void {
    // Stop playback animation
    if (this.playbackTimerId) {
      clearTimeout(this.playbackTimerId);
      this.playbackTimerId = null;
    }
    this.pausePlayback = true;
    this.isPlaybackPlaying = false;
    this.playbackPoints = [];
    this.playbackSourcePoints = []; // Clear immutable source data
    this.playbackPolylineCoords = []; // Clear polyline coordinates
    this.playbackDataPoints = [];
    this.finalSpdMrkrs = [];
    this.playbackParkingPoints = [];
    this.plottedParkingSno.clear(); // Reset tracking of plotted parking points
    this.currentTravelDistance = 0; // Reset accumulated distance
    this.startingOdometer = 0; // Reset starting odometer
    this.lastKnownOdometer = 0; // Reset last known odometer
    this.travelDist = "--"; // Reset distance display
    this.lastKnownSpeedCt = null; // Reset speed state for consistent processing

    // Reset parking calculation state (prevents stale data and race conditions)
    this.lastParkingCalculation = null;
    this.isCalculatingParking = false;
    // Increment request counter to invalidate any in-flight calculations
    this.playbackRequestCounter++;

    // Clear highlighted point marker
    if (this.highlightedPointMarker) {
      this.map?.removeLayer(this.highlightedPointMarker);
      this.highlightedPointMarker = null;
    }

    // Clear playback polylines and markers
    if (this.map) {
      const layersToRemove: any[] = [];
      this.map.eachLayer((layer: any) => {
        if (
          layer.options &&
          (layer.options.isPlayback || layer.options.playbackType)
        ) {
          layersToRemove.push(layer);
        }
      });
      layersToRemove.forEach((layer) => this.map.removeLayer(layer));
    }

    // Clear marker references
    if (this.playbackMarker) {
      this.playbackMarker = null;
    }
    if (this.playbackLine) {
      this.playbackLine = null;
    }
    if (this.playbackOverviewLine) {
      this.playbackOverviewLine = null;
    }

    // Reset direction tracking
    this.lastDirectionAngle = 0;
    this.previousPlaybackPoint = null;
  }

  /**
   * Clear all parking data for a vehicle when starting new playback
   */
  clearParkingData(): void {
    // Clear parking markers from map
    if (this.map && this.finalSpdMrkrs.length > 0) {
      this.finalSpdMrkrs.forEach((marker) => {
        if (this.map && this.map.hasLayer(marker)) {
          this.map.removeLayer(marker);
        }
      });
    }

    // Clear parking arrays
    this.finalSpdMrkrs = [];
    this.playbackParkingPoints = [];
    this.plottedParkingSno.clear(); // Reset tracking of plotted parking points

    // Clear parking layer if it exists
    if (this.parkingLayer) {
      this.parkingLayer.clearLayers();
      if (this.map && this.map.hasLayer(this.parkingLayer)) {
        this.map.removeLayer(this.parkingLayer);
      }
    }
  }

  loadPlaybackLocationFilters(): void {
    // Load location filters similar to playback component
    if (this.selectedTenant) {
      this.api
        .get(`playback/landmark/groups/${this.selectedTenant}`)
        .subscribe((res) => {
          this.locationGroupOptions = res?.["data"] ?? [];
        });
    }
  }

  closeVehicleSlider(): void {
    this.showVehicleSlider = false;
    this.selectedVehicleRawSnapshot = null; // Clear snapshot when closing
    this.previousSelectedVehicleId = null; // Clear previous vehicle ID tracking
    this.selectedVehicleZoomLevel = null; // Clear zoom level tracking
    // Force change detection to ensure icons return to right position
    this.cdr.detectChanges();
  }

  showVehicleCard = true;
  showDriverCard = true;
  showParkingCard = true;
  showTripwiseCard = true;
  showPlaybackDataCard = true; // New card for playback data table
  // Collapsed state for overlay cards (true = collapsed, false = expanded)
  collapsedCards: Record<string, boolean> = {
    tripwise: false,
    vehicle: false,
    driver: false,
    parking: false,
    playbackData: false,
  };
  showSpeedSlider = false;

  toggleSpeedSlider(): void {
    const opening = !this.showSpeedSlider;
    this.closeAllOverlayPanels();
    this.showSpeedSlider = opening;
  }

  closeVehicleCard(): void {
    this.showVehicleCard = false;
  }

  closeDriverCard(): void {
    this.showDriverCard = false;
  }

  closeParkingCard(): void {
    this.showParkingCard = false;
  }

  closeTripwiseCard(): void {
    this.showTripwiseCard = false;
  }

  toggleCard(cardKey: string): void {
    // When clicking on parking or playbackData card, open them
    if (cardKey === "parking" || cardKey === "playbackData") {
      this.collapsedCards[cardKey] = false; // Open the card
    } else {
      this.collapsedCards[cardKey] = !this.collapsedCards[cardKey];
    }
  }

  isCardCollapsed(cardKey: string): boolean {
    return this.collapsedCards[cardKey] || false;
  }

  // Highlight point on polyline when clicking table row
  highlightPlaybackPoint(point: any): void {
    if (!this.map || !point) return;

    // Remove previous highlight marker
    if (this.highlightedPointMarker) {
      this.map.removeLayer(this.highlightedPointMarker);
      this.highlightedPointMarker = null;
    }

    // Create highlight marker
    const highlightIcon = L.divIcon({
      className: "playback-point-highlight",
      html: '<div style="width: 20px; height: 20px; border-radius: 50%; background-color: #ff0000; border: 3px solid #ffffff; box-shadow: 0 0 10px rgba(255,0,0,0.8);"></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    this.highlightedPointMarker = L.marker([point.lat, point.lng], {
      icon: highlightIcon,
    }).addTo(this.map);

    // Zoom to point
    this.map.setView([point.lat, point.lng], this.map.getZoom(), {
      animate: true,
      duration: 0.5,
    });

    // Open popup with point details
    const popupContent = `
      <div style="min-width: 200px;">
        <b>Vehicle:</b> ${point.asrt_nm || "--"}<br>
        <b>IMEI:</b> ${point.dev_imei_nu || "--"}<br>
        <b>Speed:</b> ${point.speed_ct || 0} km/h<br>
        <b>Latitude:</b> ${point.lat.toFixed(6)}<br>
        <b>Longitude:</b> ${point.lng.toFixed(6)}<br>
        <b>Time:</b> ${point.Signal || "--"}
      </div>
    `;
    this.highlightedPointMarker.bindPopup(popupContent).openPopup();
  }

  onPlaybackVehicleSearchChange(): void {
    if (
      !this.playbackVehicleSearchTerm ||
      this.playbackVehicleSearchTerm.trim() === ""
    ) {
      this.filteredPlaybackVehicles = this.playbackVehicles;
    } else {
      const searchLower = this.playbackVehicleSearchTerm.toLowerCase().trim();
      this.filteredPlaybackVehicles = this.playbackVehicles.filter(
        (v) =>
          v.id?.toLowerCase().includes(searchLower) ||
          (v as any).asrt_nm?.toLowerCase().includes(searchLower) ||
          v.shortName?.toLowerCase().includes(searchLower)
      );
    }
    this.showVehicleDropdown = true;
  }

  selectPlaybackVehicle(vehicle: TrackingVehicle): void {
    this.playbackFilters.vehicleId = String(vehicle.asrt_id);
    this.playbackVehicleSearchTerm =
      vehicle.id || (vehicle as any).asrt_nm || "";
    this.showVehicleDropdown = false;
    this.onPlaybackVehicleChange();
  }

  getSelectedVehicleName(): string {
    if (!this.playbackFilters.vehicleId) {
      return "Select vehicle";
    }
    const vehicle = this.playbackVehicles.find(
      (v) =>
        String(v.asrt_id) === String(this.playbackFilters.vehicleId) ||
        String(v.id) === String(this.playbackFilters.vehicleId)
    );
    return vehicle
      ? vehicle.id || (vehicle as any).asrt_nm || ""
      : "Select vehicle";
  }

  toggleFullscreen(): void {
    const mapContainer = document.getElementById("tracking-map");
    if (!mapContainer) return;

    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (mapContainer.requestFullscreen) {
        mapContainer.requestFullscreen();
      } else if ((mapContainer as any).webkitRequestFullscreen) {
        (mapContainer as any).webkitRequestFullscreen();
      } else if ((mapContainer as any).msRequestFullscreen) {
        (mapContainer as any).msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  }

  focusOnParkingPoint(point: any): void {
    if (this.map && point.lat && point.lng) {
      // Zoom to parking point
      this.map.flyTo([point.lat, point.lng], 15, {
        animate: true,
        duration: 0.8,
      });

      // Find and show popup for parking marker (like playback component)
      const parkingMarker = this.finalSpdMrkrs.find((mrkr: any) => {
        const markerData = mrkr.options?.data;
        return (
          markerData &&
          markerData.lat == point.lat &&
          markerData.lng == point.lng
        );
      });

      if (parkingMarker) {
        // Open popup on the marker
        parkingMarker.openPopup();
      } else {
        // Create popup content with lat/lng and tabular details (like playback component)
        const popupContent = `
          <div style="min-width: 200px;">
            <h6 style="margin: 0 0 8px 0; font-weight: 600;">Parking Point P${
              point.sno || "--"
            }</h6>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <tr>
                <td style="padding: 4px; font-weight: 600;">Vehicle:</td>
                <td style="padding: 4px;">${point.asrt_nm || "--"}</td>
              </tr>
              <tr>
                <td style="padding: 4px; font-weight: 600;">From:</td>
                <td style="padding: 4px;">${point.Signal || "--"}</td>
              </tr>
              <tr>
                <td style="padding: 4px; font-weight: 600;">To:</td>
                <td style="padding: 4px;">${point.lastTime || "--"}</td>
              </tr>
              <tr>
                <td style="padding: 4px; font-weight: 600;">Duration:</td>
                <td style="padding: 4px;">${point.stoppageTime || "--"} min</td>
              </tr>
              <tr>
                <td style="padding: 4px; font-weight: 600;">Latitude:</td>
                <td style="padding: 4px;">${point.lat || "--"}</td>
              </tr>
              <tr>
                <td style="padding: 4px; font-weight: 600;">Longitude:</td>
                <td style="padding: 4px;">${point.lng || "--"}</td>
              </tr>
            </table>
          </div>
        `;

        // Show popup at parking point location
        L.popup([point.lat, point.lng], {
          content: popupContent,
          maxWidth: 250,
        }).openOn(this.map);
      }
    }
  }

  toggleVehicleStatusLegend(): void {
    const opening = !this.showVehicleStatusLegend;
    this.closeAllOverlayPanels();
    this.showVehicleStatusLegend = opening;
  }

  setPlaybackBottomTab(tab: "info" | "parking" | "playback"): void {
    this.playbackBottomPanelTab = tab;
  }

  onParkingPointRowClick(e: any): void {
    if (e.data) {
      this.focusOnParkingPoint(e.data);
    }
  }

  onPlaybackPointRowClick(e: any): void {
    if (e.data) {
      this.highlightPlaybackPoint(e.data);
    }
  }

  startResizeTerminal(event: MouseEvent): void {
    event.preventDefault();
    this.isResizingTerminal = true;
    const startY = event.clientY;
    const startHeight = this.playbackTerminalHeight;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isResizingTerminal) return;
      const deltaY = startY - e.clientY; // Inverted because we're resizing from bottom
      const newHeight = Math.max(200, Math.min(600, startHeight + deltaY));
      this.playbackTerminalHeight = newHeight;
    };

    const onMouseUp = () => {
      this.isResizingTerminal = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  onPlaybackSpeedChange(value: number): void {
    this.playbackSpeedValue = value;
    this.playbackSpeedSliderValue = value;
    // Restart playback with new speed if currently playing
    if (this.isPlaybackPlaying && !this.pausePlayback) {
      // Speed change will be applied in next update cycle
    }
  }

  onPlaybackSpeedSliderChange(value: number): void {
    this.playbackSpeedSliderValue = value;
    this.playbackSpeedValue = value;
    // Restart playback with new speed if currently playing
    if (this.isPlaybackPlaying && !this.pausePlayback) {
      // Speed change will be applied in next update cycle
    }
  }

  playPlayback(): void {
    // If playbackPoints is empty but we have source data, restore from source (for replay)
    if (
      this.playbackPoints.length === 0 &&
      this.playbackSourcePoints.length > 0
    ) {
      // Restore playback points from immutable source for consistent replay
      this.playbackPoints = JSON.parse(
        JSON.stringify(this.playbackSourcePoints)
      );
      this.plottedParkingSno.clear(); // Reset parking markers for replay
      this.playbackCompleted = false;
      this.previousPlaybackPoint = null; // Reset previous point for fresh animation

      // Clear and recreate the polyline for replay
      if (this.playbackLine && this.map) {
        this.map.removeLayer(this.playbackLine);
      }
      if (this.map) {
        this.playbackLine = L.polyline([], {
          color: "blue",
          weight: 4,
          opacity: 0.8,
          smoothFactor: 2.0,
          lineJoin: "round",
          lineCap: "round",
        }).addTo(this.map);
        (this.playbackLine as any).options.isPlayback = true;
      }

      // Clear parking markers for fresh replay
      if (this.parkingLayer) {
        this.parkingLayer.clearLayers();
      }
      this.finalSpdMrkrs = [];

      console.log(
        `[Playback] Restored ${this.playbackPoints.length} points from source for replay`
      );
    }

    if (this.playbackPoints.length > 0) {
      this.pausePlayback = false;
      this.isPlaybackPlaying = true;
      // Resume playback from current index
      if (!this.playbackTimerId) {
        this.playbackTimerId = this.updatePlayback();
      }
    }
  }

  pausePlaybackControl(): void {
    this.pausePlayback = true;
    this.isPlaybackPlaying = false; // Mark as not playing

    // Set speed to 1x when vehicle is paused
    this.playbackSpeedSliderValue = 100;
    this.playbackSpeedValue = 100;

    if (this.playbackTimerId) {
      clearTimeout(this.playbackTimerId);
      this.playbackTimerId = null;
    }
  }

  trackPlaybackVehicle(): void {
    if (this.selectedVehicle && this.map) {
      // Center map on selected vehicle
      if (this.playbackMarker) {
        const latlng = this.playbackMarker.getLatLng();
        this.map.setView(latlng, 15, { animate: true, duration: 0.8 });
      } else if (this.selectedVehicle.lat && this.selectedVehicle.lng) {
        this.map.setView(
          [this.selectedVehicle.lat, this.selectedVehicle.lng],
          15,
          { animate: true, duration: 0.8 }
        );
      }
    }
  }

  exportParkingPointsExcel(): void {
    // Excel export functionality (similar to playback component)
    if (
      !this.playbackParkingPoints ||
      this.playbackParkingPoints.length === 0
    ) {
      return;
    }

    // Create workbook and worksheet
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("ParkingPoints");

    // Add headers
    worksheet.columns = [
      { header: "S.No", key: "sno", width: 10 },
      { header: "Vehicle", key: "asrt_nm", width: 15 },
      { header: "From", key: "Signal", width: 20 },
      { header: "To", key: "lastTime", width: 20 },
      { header: "Duration (min)", key: "stoppageTime", width: 15 },
      { header: "Latitude", key: "lat", width: 15 },
      { header: "Longitude", key: "lng", width: 15 },
    ];

    // Add data rows
    this.playbackParkingPoints.forEach((point: any) => {
      // SECURITY (audit finding #9 - Formula Injection, CWE-1236): asrt_nm is a
      // user-supplied vehicle name. sanitizeRow clones, so `point` is untouched.
      worksheet.addRow(
        sanitizeRow({
          sno: point.sno || "--",
          asrt_nm: point.asrt_nm || "--",
          Signal: point.Signal || "--",
          lastTime: point.lastTime || "--",
          stoppageTime: point.stoppageTime || "--",
          lat: point.lat || "--",
          lng: point.lng || "--",
        })
      );
    });

    // Style header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E0E0" },
    };

    // Export to Excel
    workbook.xlsx.writeBuffer().then((buffer) => {
      const fileName = `ParkingPoints_${moment().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.xlsx`;
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        fileName
      );
    });
  }

  // onParkingPointsExporting() removed with the <app-data-grid> conversion: it was the
  // canonical exportDataGrid({ customizeCell: sanitizeExcelCell }) block, now
  // done by DataGridComponent.exportExcel() and sanitised at the addRow() sink.

  // onPlaybackDataExporting() removed with the <app-data-grid> conversion: it was the
  // canonical exportDataGrid({ customizeCell: sanitizeExcelCell }) block, now
  // done by DataGridComponent.exportExcel() and sanitised at the addRow() sink.

  onPlaybackTenantChange(): void {
    // Reset category and group when tenant changes
    this.selectedVehicleCategory = 0;
    this.selectedVehicleGroup = 0;
    this.playbackFilters.vehicleId = "";
    this.fetchSelectionData();
    this.fetchLiveVehicles();
  }

  onPlaybackCategoryChange(): void {
    this.playbackFilters.vehicleId = "";
    this.filterPlaybackVehicles();
  }

  onPlaybackGroupChange(): void {
    // Reset category when group changes
    this.selectedVehicleCategory = 0;
    this.playbackFilters.vehicleId = "";
    this.fetchVehicleCategories();
    this.filterPlaybackVehicles();
  }

  private filterPlaybackVehicles(): void {
    if (!this.allVehiclesRaw || this.allVehiclesRaw.length === 0) {
      this.filteredPlaybackVehicles = [];
      return;
    }

    // Filter raw vehicles by group and category (like live-tracking getAssertList)
    let filtered = this.allVehiclesRaw;

    if (this.selectedVehicleGroup && this.selectedVehicleGroup != 0) {
      if (this.selectedVehicleCategory && this.selectedVehicleCategory != 0) {
        // Both group and category selected
        filtered = this.allVehiclesRaw.filter(
          (v) =>
            v.asrt_grp_id == this.selectedVehicleGroup &&
            v.asrt_ctgry_id == this.selectedVehicleCategory
        );
      } else {
        // Only group selected
        filtered = this.allVehiclesRaw.filter(
          (v) => v.asrt_grp_id == this.selectedVehicleGroup
        );
      }
    } else if (
      this.selectedVehicleCategory &&
      this.selectedVehicleCategory != 0
    ) {
      // Only category selected
      filtered = this.allVehiclesRaw.filter(
        (v) => v.asrt_ctgry_id == this.selectedVehicleCategory
      );
    }

    this.filteredPlaybackVehicles = filtered.map((vehicle, idx) =>
      this.mapVehicleToDisplay(vehicle, idx)
    );
  }

  onPlaybackVehicleChange(): void {
    // Clear playback data when vehicle changes
    this.clearPlaybackData();
    this.playbackTrip.tripId = ""; // Clear trip selection
    this.playbackTrip.date = this.buildLocalDateValue(0); // Reset to current date

    // Set speed to 1x when vehicle is selected in playback mode
    this.playbackSpeedSliderValue = 100;
    this.playbackSpeedValue = 100;

    // Reset timeline to current date from 00:00:00 to 23:59:59 (like playback component)
    const today = new Date();
    const fromDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0
    );
    const toDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59
    );

    this.playbackFilters.from = this.formatDateTimeLocal24Hr(fromDate);
    this.playbackFilters.to = this.formatDateTimeLocal24Hr(toDate);
    this.playbackFilters.fromDisplay =
      this.datepipe.transform(today, "dd-MM-yyyy") + " 00:00:00";
    this.playbackFilters.toDisplay =
      this.datepipe.transform(today, "dd-MM-yyyy") + " 23:59:59";

    if (this.playbackFilters.vehicleId) {
      this.updateAvailableTrips();
      // Update selected vehicle - search in filteredPlaybackVehicles (all vehicles) not just filtered vehicles
      const vehicleIdStr = this.playbackFilters.vehicleId?.toString();
      const vehicle =
        this.filteredPlaybackVehicles.find(
          (v) =>
            v.asrt_id?.toString() === vehicleIdStr ||
            v.id?.toString() === vehicleIdStr
        ) ||
        this.playbackVehicles.find(
          (v) =>
            v.asrt_id?.toString() === vehicleIdStr ||
            v.id?.toString() === vehicleIdStr
        );
      if (vehicle) {
        this.selectedVehicle = vehicle;
        this.selectedVehicleRaw =
          this.allVehiclesRaw.find(
            (v) =>
              v.asrt_nm === vehicle.id ||
              v.asrt_id?.toString() === vehicle.asrt_id?.toString()
          ) || null;
      }
    } else {
      // Clear selection if no vehicle
      this.selectedVehicle = null;
      this.selectedVehicleRaw = null;
    }
  }

  togglePanelVisibility(tab: HeaderTabKey, event: MouseEvent): void {
    event.stopPropagation();
    this.collapsedPanels[tab] = !this.collapsedPanels[tab];
    if (this.collapsedPanels[this.activeHeaderTab]) {
      const available = this.headerTabs.find(
        (t) => !this.collapsedPanels[t.key]
      );
      this.activeHeaderTab = available?.key ?? this.activeHeaderTab;
    }
  }

  toggleVehicleWidget(): void {
    this.showVehicleWidget = !this.showVehicleWidget;
    if (!this.showVehicleWidget) {
      this.vehicleOptions.follow = false;
    }
  }

  get activitySnapshot() {
    if (!this.selectedVehicle) {
      return null;
    }
    return {
      running:
        this.playbackSummary?.distance ??
        this.selectedVehicle.trip.distance ??
        "--",
      idle:
        this.playbackSummary?.idle ??
        this.selectedVehicle.parkingSummary.longest ??
        "--",
      alerts:
        this.playbackSummary?.alerts ?? this.selectedVehicle.trip.alerts ?? 0,
    };
  }

  get speedSnapshot() {
    if (!this.selectedVehicle) {
      return { avg: 0, max: 0, percent: 0 };
    }
    const avg = Number(this.selectedVehicle.speed) || 0;
    const max = Math.max(avg + 5, avg * 1.5, 60);
    return {
      avg,
      max,
      percent: Math.min(100, (avg / max) * 100),
    };
  }

  applySummaryFilter(key: string): void {
    // If clicking the same key, show all (total)
    if (this.activeSummaryKey === key) {
      this.activeSummaryKey = "total";
    } else {
      this.activeSummaryKey = key;
    }
    this.currentPage = 1; // Reset to first page on filter change
    // Update stslist based on selected filter
    this.updateStsList();
    // Re-render markers when filter changes
    setTimeout(() => this.renderVehicleMarkers(), 0);
    // Force change detection to update DataGrid
    setTimeout(() => {
      if (this.stslist && this.stslist.length > 0) {
        // Trigger DataGrid refresh
      }
    }, 0);
  }

  vehicleMatchesSummary(vehicle: TrackingVehicle, key: string): boolean {
    const rawVehicle = this.allVehiclesRaw.find(
      (v) =>
        v.asrt_nm === vehicle.id ||
        v.asrt_id === vehicle.id ||
        (v.asrt_nm && v.asrt_nm.toString() === vehicle.id.toString())
    );

    if (!rawVehicle) return false;

    const diffMins =
      vehicle.diffMins ?? this.diffInMinutes(rawVehicle.data_received_ts);

    switch (key) {
      case "all":
      case "total":
        return true;
      case "online":
        return (rawVehicle.asrtglblId ?? 0) === 1;
      case "offline":
        return (rawVehicle.asrtglblId ?? 0) === 2;
      case "repair":
        return (rawVehicle.asrtglblId ?? 0) === 3;
      case "running":
        // Running: speed > 0 and diffTimeInMin < 5 (must be online)
        return (
          (rawVehicle.asrtglblId ?? 0) === 1 &&
          rawVehicle.spd_ct > 0 &&
          diffMins < 5
        );
      case "stationedShort":
        // Stationed <5min: speed >= 0 and diffTimeInMin <= 5 (must be online)
        return (
          (rawVehicle.asrtglblId ?? 0) === 1 &&
          rawVehicle.spd_ct >= 0 &&
          diffMins <= 5
        );
      case "stationedMedium":
        // Stationed 5-15min: speed >= 0 and diffTimeInMin >= 5 and < 15 (must be online)
        return (
          (rawVehicle.asrtglblId ?? 0) === 1 &&
          rawVehicle.spd_ct >= 0 &&
          diffMins >= 5 &&
          diffMins < 15
        );
      case "stationedLong":
        // Stationed >15min: speed >= 0 and diffTimeInMin >= 15 (must be online)
        return (
          (rawVehicle.asrtglblId ?? 0) === 1 &&
          rawVehicle.spd_ct >= 0 &&
          diffMins >= 15
        );
      default:
        return true;
    }
  }

  toggleToolPanel(key: string): void {
    const opening = this.activeTool !== key;
    this.closeAllOverlayPanels();
    if (opening) this.activeTool = key;
  }

  toggleLocationFilter(): void {
    const opening = !this.showLocationFilter;
    this.closeAllOverlayPanels();
    this.showLocationFilter = opening;
  }

  closeLocationFilter(): void {
    this.showLocationFilter = false;
  }

  closeToolPanel(key: string): void {
    if (this.activeTool === key) {
      this.activeTool = null;
    }
  }

  toggleLayer(key: keyof typeof this.layerOptions): void {
    this.layerOptions[key] = !this.layerOptions[key];
  }

  toggleVehicleOption(key: keyof typeof this.vehicleOptions): void {
    this.vehicleOptions[key] = !this.vehicleOptions[key];
  }

  playbackMaxZoom = 18; // Max zoom level allowed during playback

  zoomIn(): void {
    if (
      this.map &&
      this.activeHeaderTab === "playback" &&
      this.map.getZoom() >= this.playbackMaxZoom
    ) {
      return;
    }
    this.map?.zoomIn();
  }

  zoomOut(): void {
    this.map?.zoomOut();
  }

  handleQuickTripSelect(preset: { id: string; date: string }): void {
    this.playbackMode = "trip";
    this.playbackTrip.tripId = preset.id;
    this.playbackTrip.date = preset.date;
    this.activeTool = null;
  }

  handleGridSelection(event: any): void {
    if (event?.selectedRowsData?.length) {
      this.selectVehicle(event.selectedRowsData[0], true);
    }
  }

  plotLocationsOnMap(): void {
    if (!this.map || !this.selectedLocationGroup) {
      return;
    }

    // Clear existing location markers (like livetrack's getLocOnMap)
    this.clearLocationMarkers();

    const locations: any[] = [];

    // Filter locations based on selection (like livetrack logic)
    if (this.selectedLocation === "select") {
      // Show all locations in the group
      this.locationList.forEach((loc: any) => {
        locations.push(loc);
      });
    } else if (this.selectedLocation === "deselect") {
      // Show none
      locations.length = 0;
    } else {
      // Show specific location
      this.locationList.forEach((loc: any) => {
        if (loc.fnce_id === this.selectedLocation) {
          locations.push(loc);
        }
      });
    }

    // Plot locations on map (like livetrack's getLocOnMap)
    locations.forEach((loc: any) => {
      if (!loc.lat || !loc.lng) {
        return;
      }

      // If fnce_type_id == 2, show only boundaries (polygon/circle), not the center icon marker
      const showMarkerOnly = loc.fnce_type_id !== 2;

      // Create marker icon only if not type 2 (like livetrack logic)
      if (showMarkerOnly) {
        let icon: L.Icon;
        if (!loc.fncs_icn_pth) {
          icon = new L.Icon({
            iconUrl: "assets/images/markers/mapIcons/m/fenceIcon.png",
            iconSize: [32, 30],
            iconAnchor: [10, 39],
            popupAnchor: [1, -34],
          });
        } else if (loc.fncs_icn_pth === "dumperBin") {
          const icn = loc.covr_status == 1 ? "_r_m" : "_o_m";
          icon = new L.Icon({
            iconUrl: `assets/images/markers/mapIcons/m/${loc.fncs_icn_pth}${icn}.png`,
            iconSize: [32, 30],
            iconAnchor: [22, 45],
            popupAnchor: [1, -34],
          });
        } else {
          const icn = loc.covr_status == 1 ? "_r_m" : "_o_m";
          icon = new L.Icon({
            iconUrl: `assets/images/markers/mapIcons/m/${loc.fncs_icn_pth}${icn}.png`,
            iconSize: [33, 33],
            iconAnchor: [16, 28],
          });
        }

        // Add marker only if not type 2
        const marker = L.marker([loc.lat, loc.lng], {
          draggable: false,
          opacity: 1,
          icon: icon,
        }).addTo(this.map!);
        marker
          .bindTooltip(loc.fnce_nm, {
            permanent: true,
            opacity: 0.7,
            offset: [0, -20],
          })
          .openTooltip();
        this._lastIndex.push(marker);
        this.locationLayer.addLayer(marker);
      }

      // Add circle if radius exists (skip for type 2 - only show polygon boundaries)
      if (loc.rds_ct && loc.fnce_type_id !== 2) {
        const circle = L.circle([loc.lat, loc.lng], {
          radius: loc.rds_ct,
        }).addTo(this.map!);
        circle.setStyle({
          fillColor: "#f55555",
          stroke: false,
          fillOpacity: 0.07,
        });
        this._lastIndex.push(circle);
        this.locationLayer.addLayer(circle);
      }

      // Add polygon if arc line exists (always show boundary, even for type 2)
      if (loc.fnce_arc_lne_tx) {
        try {
          const polygon = L.polygon(JSON.parse(loc.fnce_arc_lne_tx), {
            color: loc.fncs_clr_cd || "#f55555",
            fill: false,
          }).addTo(this.map!);
          polygon
            .bindTooltip(loc.fnce_nm, { permanent: true, opacity: 0.7 })
            .openTooltip();
          this.plypoint1.push({ ply: polygon, fnce_id: loc.fnce_id });
          this.locationLayer.addLayer(polygon);
          this.map!.fitBounds(polygon.getBounds());
        } catch (e) {
          console.warn("Error parsing polygon:", e);
        }
      }
    });
  }

  clearLocationMarkers(): void {
    // Clear like livetrack's getLocOnMap
    if (this._lastIndex && this._lastIndex.length > 0) {
      this._lastIndex.forEach((item: any) => {
        if (this.map) {
          this.map.removeLayer(item);
        }
      });
      this._lastIndex = [];
    }

    if (this.plypoint1 && this.plypoint1.length > 0) {
      this.plypoint1.forEach((item: any) => {
        if (this.map && item.ply) {
          this.map.removeLayer(item.ply);
        }
      });
      this.plypoint1 = [];
    }

    this.locationLayer.clearLayers();
    this.locationMarkers = [];
    this.locationPolygons = [];
    this.locationCircles = [];
  }

  clearAllLiveTrackMarkers(): void {
    // Clear vehicle markers and cluster group
    if (this.vehicleClusterGroup && this.map) {
      this.map.removeLayer(this.vehicleClusterGroup);
      this.vehicleClusterGroup.clearLayers();
      this.vehicleClusterGroup = null;
    }
    this.vehicleLayer.clearLayers();
    this.markerRegistry.clear();

    // Clear location markers
    this.clearLocationMarkers();

    // Clear parking layer
    this.parkingLayer.clearLayers();

    // Clear any remaining markers from map that are not in layers
    if (this.map) {
      this.map.eachLayer((layer: any) => {
        // Remove only livetrack markers (not playback markers)
        if (layer.options && layer.options.isPlayback !== true) {
          // Check if it's a vehicle marker or location marker
          if (
            layer instanceof L.Marker ||
            layer instanceof L.Circle ||
            layer instanceof L.Polygon
          ) {
            // Only remove if it's not part of a layer group we're managing
            if (
              !this.vehicleLayer.hasLayer(layer) &&
              !this.locationLayer.hasLayer(layer) &&
              !this.parkingLayer.hasLayer(layer)
            ) {
              this.map?.removeLayer(layer);
            }
          }
        }
      });
    }
  }

  private initMap(): void {
    // Ensure map container is visible before initialization
    const mapElement = document.getElementById("tracking-map");
    if (!mapElement) {
      console.error("Map container not found");
      return;
    }

    // Force map container to have proper dimensions
    if (mapElement.offsetHeight === 0) {
      mapElement.style.height = "100vh";
      mapElement.style.width = "100%";
    }
    // Use selected tenant's lat/lng for initial map center
    const userDtls: any = JSON.parse(
      localStorage.getItem("wto_clients") || "{}"
    );
    let centerLat = userDtls[0]?.lat || 17.6905;
    let centerLng = userDtls[0]?.lng || 83.2123;
    if (this.selectedTenant && this.tntLst?.length) {
      const tnt = this.tntLst.find((t: any) => t.tnt_id == this.selectedTenant);
      if (tnt?.lat && tnt?.lng) {
        centerLat = tnt.lat;
        centerLng = tnt.lng;
      }
    }
    this.map = L.map("tracking-map", {
      center: [centerLat, centerLng],
      zoom: 13,
      zoomControl: false,
      maxBounds: this.indiaBounds,
      maxBoundsViscosity: 0.8,
    });

    // Define base layers
    // Tile options that decide whether a flight looks smooth:
    //  maxNativeZoom     - the deepest zoom the SERVER actually has. Without it
    //                      Leaflet requests z20-22 tiles that do not exist, and
    //                      the holes stay grey. With it, it upscales the deepest
    //                      real tile instead - instant, no gaps.
    //  updateWhenZooming - false stops a tile refetch at EVERY intermediate zoom
    //                      the flight sweeps through. That wave of requests is
    //                      what made the map crawl in piece by piece.
    //  keepBuffer        - a wider ring of tiles kept around the viewport, so
    //                      the edges are already painted when the flight lands.
    const SMOOTH_TILES = { updateWhenZooming: false, keepBuffer: 4 };

    const googleStreets = L.tileLayer(
      "http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
      {
        maxZoom: 22,
        maxNativeZoom: 20,
        minZoom: 3,
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        attribution: "Google Streets",
        ...SMOOTH_TILES,
      }
    );

    const osm = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        maxZoom: 22,
        maxNativeZoom: 19,
        minZoom: 3,
        attribution: "OpenStreetMap contributors",
        ...SMOOTH_TILES,
      }
    );

    const googleSat = L.tileLayer(
      "http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      {
        maxZoom: 22,
        maxNativeZoom: 20,
        minZoom: 3,
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        attribution: "Google Satellite",
        ...SMOOTH_TILES,
      }
    );

    const openTopoMap = L.tileLayer(
      "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
      {
        maxZoom: 22,
        maxNativeZoom: 17,
        minZoom: 3,
        attribution: "OpenTopoMap",
        ...SMOOTH_TILES,
      }
    );

    this.baseLayers = {
      street: googleStreets,
      osm: osm,
      sat: googleSat,
      topo: openTopoMap,
    };

    // Add default layer (Google Streets)
    googleStreets.addTo(this.map);
    this.currentBaseLayer = googleStreets;
    this.selectedLayer = "street";

    L.control.zoom({ position: "bottomright" }).addTo(this.map);

    // Enforce playback max zoom for all zoom sources (scroll, double-click, pinch, etc.)
    this.map.on("zoomend", () => {
      if (
        this.activeHeaderTab === "playback" &&
        this.map.getZoom() > this.playbackMaxZoom
      ) {
        this.map.setZoom(this.playbackMaxZoom);
      }
    });

    this.vehicleLayer.addTo(this.map);
    this.parkingLayer.addTo(this.map);
    this.locationLayer.addTo(this.map);

    // Invalidate map size to ensure it renders properly
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 100);

    this.renderVehicleMarkers();
    if (this.selectedVehicle) {
      this.focusOnVehicle(this.selectedVehicle);
    }
  }

  setMapLayer(layer: "street" | "osm" | "sat" | "topo"): void {
    if (!this.map || !this.baseLayers[layer]) {
      return;
    }

    // Remove current base layer
    if (this.currentBaseLayer) {
      this.map.removeLayer(this.currentBaseLayer);
    }

    // Add new base layer
    this.currentBaseLayer = this.baseLayers[layer];
    this.currentBaseLayer.addTo(this.map);
    this.selectedLayer = layer as "street" | "osm" | "sat";
    this.activeTool = null; // Close layers panel after selection
  }

  /**
   * Maps vehicle category name to icon base name
   * Based on the icons.jpg file structure
   */
  private getVehicleIconBaseName(categoryName: string): string {
    if (!categoryName) return "car"; // Default fallback

    const categoryLower = categoryName.toLowerCase().trim();

    // Map category names to icon base names
    const categoryMap: { [key: string]: string } = {
      auto: "auto",
      tractor: "tractor",
      compactor: "compactor",
      watertank: "watertank",
      "water tank": "watertank",
      van: "van",
      "dogs van": "dogsvan",
      "dog van": "dogsvan",
      dogs: "dogsvan",
      car: "car",
      "sweeping machines": "sweeping",
      "sweeping machine": "sweeping",
      sweeper: "sweeping",
      "hook loader": "hookloader",
      hookloader: "hookloader",
      "tree pruning": "treepruning",
      "tree pruner": "treepruning",
      lorry: "lorry",
      tippers: "tipper",
      tipper: "tipper",
      dumper: "dumper",
      jcb: "jcb",
      isuzu: "isuzu",
      ladder: "ladder",
      "electric ladder": "electricladder",
      electricladder: "electricladder",
      buldozer: "buldozer",
      "dumper placer": "dumperplacer",
      dumper_placer: "dumperplacer",
    };

    // Try exact match first
    if (categoryMap[categoryLower]) {
      return categoryMap[categoryLower];
    }

    // Try partial match
    for (const [key, value] of Object.entries(categoryMap)) {
      if (categoryLower.includes(key) || key.includes(categoryLower)) {
        return value;
      }
    }

    // Default fallback
    return "car";
  }

  /**
   * Determines icon color based on vehicle status
   * Aligns map icon colors with table/status chips:
   * - running: green  (#26a69a -> green icon)
   * - stationedShort (<5m): blue   (no purple icon available, closest match)
   * - stationedMedium (5-15m): yellow (#ffb74d -> yellow icon)
   * - stationedLong (>=15m): red    (#ef5350 -> red icon)
   * - offline: gray  (closest available to offline orange chip)
   */
  private getVehicleIconColor(vehicle: any): string {
    // Offline vehicles (asrtglblId != 1)
    if (vehicle.asrtglblId != 1) {
      return "gray"; // Closest available to offline color
    }

    // Online vehicles
    const speed = parseFloat(vehicle.spd_ct) || 0;
    const diffTimeInMin = Number(vehicle.diffTimeInMin) || 0;

    // Running (speed > 5)
    if (speed > 5) {
      return "green";
    }

    // Stationed/parked (speed <= 5)
    if (speed <= 5) {
      if (diffTimeInMin >= 15) {
        return "red"; // stationedLong
      } else if (diffTimeInMin >= 5) {
        return "yellow"; // stationedMedium
      }
      return "blue"; // stationedShort / recent park
    }

    // Default fallback
    return "blue";
  }

  /**
   * Gets the complete icon URL for a vehicle based on icon_path_new and status
   */
  private getVehicleIconUrl(vehicle: any): string {
    // Get color based on status
    const color = this.getVehicleIconColor(vehicle);

    // Determine icon base name - prioritize icon_path_new
    let iconBaseName = "";

    if (vehicle.icon_path_new) {
      // Use icon_path_new directly (remove file extension if present)
      iconBaseName = vehicle.icon_path_new
        .replace(/\.png$/, "")
        .replace(/\.svg$/, "")
        .trim();
    } else if (vehicle.icon_path) {
      // Fallback: Extract base name from icon_path (remove existing suffixes like _r, _p, _o)
      iconBaseName = vehicle.icon_path
        .replace(/_[rop]_m\.png$/, "")
        .replace(/_[rop]\.png$/, "")
        .replace(/\.png$/, "")
        .replace(/\.svg$/, "")
        .toLowerCase();
    } else if (vehicle.asrt_ctgry_nm) {
      // Fallback: Use category name to get icon base name
      iconBaseName = this.getVehicleIconBaseName(vehicle.asrt_ctgry_nm);
    } else {
      // Final fallback to default
      iconBaseName = "car";
    }

    // Build new icon URL: assets/images/markers/mapIcons/m/new_icons/{iconBaseName}_{color}.png
    // Example: assets/images/markers/mapIcons/m/new_icons/auto_green.png
    const newIconUrl = `assets/images/markers/mapIcons/m/new_icons/${iconBaseName}_${color}.png`;

    return newIconUrl;
  }

  private renderVehicleMarkers(): void {
    if (!this.map || this.activeHeaderTab !== "livetrack") {
      return;
    }

    // Clear existing markers and cluster group (exactly like live-tracking)
    if (this.vehicleClusterGroup) {
      this.map.removeLayer(this.vehicleClusterGroup);
      this.vehicleClusterGroup.clearLayers();
    }

    // Clear deletemarkers
    for (var j = 0; j < this.deletemarkers.length; j++) {
      if (this.deletemarkers[j]) {
        this.map.removeLayer(this.deletemarkers[j]);
      }
    }
    this.deletemarkers = [];
    this.mov_marker = [];
    this.markerRegistry.clear();

    // Create marker cluster group (exactly like live-tracking line 1209)
    this.vehicleClusterGroup = new L.MarkerClusterGroup().addTo(this.map);

    const slctdDt = new Date();
    const currentTimeStmp = moment(slctdDt).format("YYYY-MM-DD HH:mm:ss");

    // Get vehicles to display based on active summary filter
    let vehiclesToShow: any[] = [];
    switch (this.activeSummaryKey) {
      case "total":
        vehiclesToShow = this.allVehiclesList;
        break;
      case "online":
        vehiclesToShow = this.vehiclesList;
        break;
      case "offline":
        vehiclesToShow = this.offlineVehicles;
        break;
      case "repair":
        vehiclesToShow = this.repairVehicles;
        break;
      case "running":
        vehiclesToShow = this.runningVehicles;
        break;
      case "stationedShort":
        vehiclesToShow = this.stationedShort;
        break;
      case "stationedMedium":
        vehiclesToShow = this.stationedMedium;
        break;
      case "stationedLong":
        vehiclesToShow = this.stationedLong;
        break;
      default:
        vehiclesToShow = this.allVehiclesList;
    }

    // Process vehicles exactly like live-tracking component (lines 1232-1357)
    for (var i = 0; i < vehiclesToShow.length; i++) {
      const vehicle = vehiclesToShow[i];

      if (
        !vehicle.lat ||
        !vehicle.lng ||
        vehicle.lat === null ||
        vehicle.lng === null
      ) {
        continue;
      }

      // Get vehicle icon with color coding based on status
      const iconUrl = this.getVehicleIconUrl(vehicle);

      const movIcon = new L.Icon({
        iconUrl: iconUrl,
        iconSize: [null, 20],
        iconAnchor: [10, 15],
        popupAnchor: [5, -24],
        shaowSize: [35, 35],
        asrt_id: vehicle.asrt_id,
        latlang: [vehicle.lat, vehicle.lng],
        title:
          vehicle.asrt_nm +
          " | " +
          vehicle.asrt_ctgry_nm +
          " | " +
          vehicle.asrt_grp_nm,
      });

      // Create animated marker exactly like live-tracking (line 1332)
      const parisKievLL = [
        [vehicle.lat, vehicle.lng],
        [vehicle.lat, vehicle.lng],
      ];
      const title =
        vehicle.asrt_nm +
        " | " +
        vehicle.asrt_ctgry_nm +
        " | " +
        vehicle.asrt_grp_nm +
        " | speed: " +
        vehicle.spd_ct +
        " kmph";
      const iconAngle = vehicle.direction || vehicle.drcn_ct || 0;

      // Create regular marker for clustering (animated markers can't be clustered directly)
      const regularMarker = L.marker([vehicle.lat, vehicle.lng], {
        icon: movIcon,
        permanent: false,
      }).bindPopup(title, {
        className: "vehicle-popup",
        maxWidth: 200,
        offset: [0, -10],
      });

      // Try to set rotation if available
      if ((regularMarker as any).setRotationAngle) {
        (regularMarker as any).setRotationAngle(iconAngle + 270);
      }

      // Add to cluster group for clustering when zoomed out
      this.vehicleClusterGroup.addLayer(regularMarker);

      // Also create animated marker for smooth movement (add to map directly)
      // Set duration to 10 seconds to match API refresh interval for smooth continuous movement
      this.mov_marker[vehicle.asrt_id] = L.animatedMarker(parisKievLL, {
        icon: movIcon,
        autoStart: false,
        duration: 10000, // 10 seconds - matches API refresh interval
        permanent: false,
      })
        .bindPopup(title, {
          className: "vehicle-popup",
          maxWidth: 200,
          offset: [0, -10],
          autoPan: false, // Prevent auto-panning when popup is open
        })
        .setRotationAngle(iconAngle + 270)
        .addTo(this.map);

      // Make popup follow the moving vehicle
      // Listen to move events and update popup position
      this.mov_marker[vehicle.asrt_id].on("move", () => {
        const marker = this.mov_marker[vehicle.asrt_id];
        if (marker) {
          try {
            const isOpen = marker.isPopupOpen && marker.isPopupOpen();
            if (isOpen) {
              const latlng = marker.getLatLng();
              const popup = marker.getPopup();
              if (popup && latlng) {
                popup.setLatLng(latlng);
              }
            }
          } catch (e) {
            // Ignore errors
          }
        }
      });

      // Also update popup position during animation using animation frame
      const updatePopupPosition = () => {
        const marker = this.mov_marker[vehicle.asrt_id];
        if (marker) {
          try {
            const isOpen = marker.isPopupOpen && marker.isPopupOpen();
            if (isOpen) {
              const latlng = marker.getLatLng();
              const popup = marker.getPopup();
              if (popup && latlng) {
                popup.setLatLng(latlng);
              }
            }
          } catch (e) {
            // Ignore errors
          }
        }
      };

      // Update popup position periodically when marker is animating
      this.mov_marker[vehicle.asrt_id].on("start", () => {
        const intervalId = setInterval(() => {
          const marker = this.mov_marker[vehicle.asrt_id];
          if (marker) {
            try {
              const isOpen = marker.isPopupOpen && marker.isPopupOpen();
              if (isOpen) {
                updatePopupPosition();
              } else {
                clearInterval(intervalId);
              }
            } catch (e) {
              clearInterval(intervalId);
            }
          } else {
            clearInterval(intervalId);
          }
        }, 100); // Update every 100ms during animation

        // Store interval ID for cleanup
        (this.mov_marker[vehicle.asrt_id] as any).popupUpdateInterval =
          intervalId;
      });

      this.mov_marker[vehicle.asrt_id].on("end", () => {
        const intervalId = (this.mov_marker[vehicle.asrt_id] as any)
          .popupUpdateInterval;
        if (intervalId) {
          clearInterval(intervalId);
          (this.mov_marker[vehicle.asrt_id] as any).popupUpdateInterval = null;
        }
      });

      // Store previous position for animation updates (matching live-tracking)
      this.m_prv_lat[vehicle.asrt_id] = vehicle.lat;
      this.m_prv_lng[vehicle.asrt_id] = vehicle.lng;

      // Initially show cluster markers, hide animated markers (will show when zoomed in)
      this.mov_marker[vehicle.asrt_id].setOpacity(0);

      // Store reference for zoom handling
      (regularMarker as any).animatedMarkerRef =
        this.mov_marker[vehicle.asrt_id];
      (this.mov_marker[vehicle.asrt_id] as any).regularMarkerRef =
        regularMarker;

      this.deletemarkers.push(this.mov_marker[vehicle.asrt_id]);
      this.deletemarkers.push(regularMarker);

      // Add click handler for animated marker (exactly like live-tracking lines 1339-1348)
      this.mov_marker[vehicle.asrt_id].on("click", (e: any) => {
        const clickedVehicle = vehiclesToShow.find(
          (v) => e.target?.options?.icon?.options?.asrt_id == v.asrt_id
        );
        if (clickedVehicle) {
          this.selectVehicleFromRaw(clickedVehicle);
          // Show vehicle widget when clicking on icon
          this.selectedVehicleRaw = clickedVehicle;
          const currentVehicleId =
            clickedVehicle?.asrt_id?.toString() ||
            clickedVehicle?.asrt_nm?.toString();
          // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
          if (this.previousSelectedVehicleId !== currentVehicleId) {
            this.selectedVehicleRawSnapshot = JSON.parse(
              JSON.stringify(clickedVehicle)
            );
            this.previousSelectedVehicleId = currentVehicleId;
          }
          this.showVehicleSlider = true;
        }
      });

      // Add click handler for regular marker (cluster marker)
      regularMarker.on("click", (e: any) => {
        const clickedVehicle = vehiclesToShow.find(
          (v) => e.target?.options?.icon?.options?.asrt_id == v.asrt_id
        );
        if (clickedVehicle) {
          this.selectVehicleFromRaw(clickedVehicle);
          // Show vehicle widget when clicking on icon
          this.selectedVehicleRaw = clickedVehicle;
          const currentVehicleId =
            clickedVehicle?.asrt_id?.toString() ||
            clickedVehicle?.asrt_nm?.toString();
          // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
          if (this.previousSelectedVehicleId !== currentVehicleId) {
            this.selectedVehicleRawSnapshot = JSON.parse(
              JSON.stringify(clickedVehicle)
            );
            this.previousSelectedVehicleId = currentVehicleId;
          }
          this.showVehicleSlider = true;
        }
      });

      // Add mouseover handler for animated marker (exactly like live-tracking lines 1350-1356)
      this.mov_marker[vehicle.asrt_id].on("mouseover", (e: any) => {
        const hoveredVehicle = vehiclesToShow.find(
          (v) => e.target?.options?.icon?.options?.asrt_id == v.asrt_id
        );
        if (hoveredVehicle && e.target?.options?.icon?.options?.latlang) {
          const popup = L.popup(e.target.options.icon.options.latlang, {
            content: e.target.options.icon.options.title,
          }).openOn(this.map);
        }
      });

      // Add mouseover handler for regular marker
      regularMarker.on("mouseover", (e: any) => {
        const hoveredVehicle = vehiclesToShow.find(
          (v) => e.target?.options?.icon?.options?.asrt_id == v.asrt_id
        );
        if (hoveredVehicle && e.target?.options?.icon?.options?.latlang) {
          const popup = L.popup(e.target.options.icon.options.latlang, {
            content: e.target.options.icon.options.title,
          }).openOn(this.map);
        }
      });

      // Register both markers for easy lookup
      this.markerRegistry.set(
        vehicle.asrt_id?.toString() || vehicle.asrt_nm,
        this.mov_marker[vehicle.asrt_id]
      );
      // Also register regular marker with a different key for cluster updates
      this.markerRegistry.set(
        `regular_${vehicle.asrt_id?.toString() || vehicle.asrt_nm}`,
        regularMarker
      );
    }

    // Add zoom handler to show/hide markers based on zoom level (only add once)
    if (!this.mapZoomHandlerAdded) {
      this.map.on("zoomend", () => {
        const zoom = this.map.getZoom();
        this.deletemarkers.forEach((marker: any) => {
          if (marker.animatedMarkerRef) {
            // This is a regular marker with animated reference
            if (zoom >= 13) {
              // Show animated markers, hide regular markers when zoomed in
              marker.setOpacity(0);
              if (marker.animatedMarkerRef) {
                marker.animatedMarkerRef.setOpacity(1);
              }
            } else {
              // Show regular markers (clusters), hide animated markers when zoomed out
              marker.setOpacity(1);
              if (marker.animatedMarkerRef) {
                marker.animatedMarkerRef.setOpacity(0);
              }
            }
          }
        });
      });
      this.mapZoomHandlerAdded = true;
    }
  }

  private mapZoomHandlerAdded = false;

  private selectVehicleFromRaw(rawVehicle: any): void {
    // Find matching TrackingVehicle and select it
    const matchingVehicle = this.vehicles.find(
      (v) =>
        v.id === rawVehicle.asrt_nm || v.id === rawVehicle.asrt_id?.toString()
    );
    if (matchingVehicle) {
      this.selectVehicle(matchingVehicle, true);
      this.selectedVehicleRaw = rawVehicle;
    } else {
      // Create a temporary vehicle object if not found
      this.selectedVehicleRaw = rawVehicle;
      // Zoom to vehicle location even if not in vehicles list
      if (rawVehicle.lat && rawVehicle.lng && this.map) {
        this.map.flyTo([rawVehicle.lat, rawVehicle.lng], 17, {
          animate: true,
          duration: 0.8,
        });
        this.selectedVehicleZoomLevel = 17;
      }
    }
  }

  private buildVehicleIcon(vehicle: TrackingVehicle): L.Icon | L.DivIcon {
    // Use actual vehicle icon matching livetrack component style exactly
    const rawVehicle = this.allVehiclesRaw.find(
      (v) =>
        v.asrt_nm === vehicle.id ||
        v.asrt_id === vehicle.id ||
        (v.asrt_nm && v.asrt_nm.toString() === vehicle.id.toString())
    );

    // Use the new icon system with color coding
    if (rawVehicle) {
      const iconUrl = this.getVehicleIconUrl(rawVehicle);
      const iconAngle = vehicle.direction || rawVehicle.drcn_ct || 0;

      const icon = new L.Icon({
        iconUrl: iconUrl,
        iconSize: [40, null],
        iconAnchor: [10, 15],
        popupAnchor: [5, -24],
        shadowSize: [35, 35],
      });

      return icon;
    }

    // Fallback to div icon if no icon path
    const statusColor = this.getVehicleStatusColor(vehicle);
    const isSelected = this.selectedVehicle?.id === vehicle.id;
    return L.divIcon({
      className: "vehicle-marker",
      html: `
        <div class="marker-chip status-${
          vehicle.status
        }" style="border-color: ${statusColor}; ${
        isSelected ? "box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3);" : ""
      }">
          <strong>${vehicle.shortName}</strong>
          <span>${vehicle.speed} km/h</span>
        </div>
      `,
      iconSize: [72, 48],
      iconAnchor: [36, 24],
    });
  }

  /**
   * Runs `fn` once the map has finished the flight it is currently on.
   *
   * Everything heavy - rebuilding ~174 markers and the cluster group, opening a
   * popup - has to wait for the landing. Run mid-flight it competes with the
   * animation frames and the tile requests, which is what made the map stutter
   * and fill in piece by piece. The timer is only a safety net: flying to a
   * point the map is already sitting on emits no `moveend`, and the callback
   * must not be stranded.
   */
  private afterMapSettles(fn: () => void, flightMs = 800): void {
    if (!this.map) {
      return;
    }
    let done = false;
    const run = () => {
      if (done) {
        return;
      }
      done = true;
      fn();
    };
    this.map.once("moveend", run);
    setTimeout(run, flightMs + 400);
  }

  private focusOnVehicle(vehicle: TrackingVehicle, zoom = 17): void {
    if (!this.map) {
      return;
    }
    // flyTo, never setView: setView only animates a short pan at an UNCHANGED
    // zoom, so a vehicle that is off-screen - or any zoom change - teleports.
    // flyTo arcs out, travels and zooms back in, which is the movement the map
    // is supposed to show when a row is picked from the table.
    this.map.flyTo([vehicle.lat, vehicle.lng], zoom, {
      animate: true,
      duration: 0.8,
    });
    this.selectedVehicleZoomLevel = zoom; // Store zoom level for selected vehicle
    const marker = this.markerRegistry.get(vehicle.id);
    // autoPan: false, and opened only after landing - a popup opening mid-flight
    // pans the map sideways to fit itself, fighting the flight that is running.
    marker?.bindPopup(
      `<strong>${vehicle.id}</strong><br/>${vehicle.location}`,
      { autoPan: false }
    );
    this.afterMapSettles(() => marker?.openPopup());
  }

  private paintParkingPoints(): void {
    if (!this.map) {
      return;
    }
    this.parkingLayer.clearLayers();
    this.parkingResults.forEach((point) => {
      const circle = L.circleMarker([point.lat, point.lng], {
        radius: 10,
        color: "#9333ea",
        weight: 2,
        fillColor: "#c084fc",
        fillOpacity: 0.6,
      }).addTo(this.parkingLayer);
      circle.bindPopup(
        `<strong>${point.label}</strong><br/>${point.location}<br/>${point.duration} stop`
      );
    });
  }

  private buildSampleParkingPoints(): ParkingPoint[] {
    const template: ParkingPoint[] = [
      {
        id: "P1",
        label: "Parking Bay A",
        duration: "14 mins",
        window: "10:42 AM - 10:56 AM",
        location: "Banhar Railway Crossing",
        lat: 21.8578,
        lng: 84.0182,
      },
      {
        id: "P2",
        label: "Fuel Stop",
        duration: "22 mins",
        window: "11:08 AM - 11:30 AM",
        location: "Masnikani Service Rd",
        lat: 21.9011,
        lng: 83.9781,
      },
      {
        id: "P3",
        label: "Landmark 12",
        duration: "9 mins",
        window: "11:44 AM - 11:53 AM",
        location: "Bhalupatra Check-post",
        lat: 21.8122,
        lng: 84.0712,
      },
    ];
    return template;
  }

  private buildLocalInputValue(offsetInMinutes: number): string {
    const date = new Date();
    date.setMinutes(date.getMinutes() + offsetInMinutes);
    const tzOffset = date.getTimezoneOffset() * 60000;
    const localISOTime = new Date(date.getTime() - tzOffset)
      .toISOString()
      .slice(0, 16);
    return localISOTime;
  }

  private buildLocalDateValue(offsetInDays: number): string {
    const date = new Date();
    date.setDate(date.getDate() + offsetInDays);
    return date.toISOString().slice(0, 10);
  }

  private formatDateTimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private formatDateTimeLocal24Hr(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  private bootstrapTenantContext(): void {
    const tenantRaw = localStorage.getItem("wto_clients");
    if (tenantRaw) {
      const clientData = JSON.parse(tenantRaw);
      if (Array.isArray(clientData) && clientData.length) {
        this.tenantOptions = clientData[0]?.tnts ?? [];
        this.tntLst = this.tenantOptions; // Store tenant list like live-tracking component
        if (this.tenantOptions.length >= 1) {
          this.selectedTenant = this.tenantOptions[0].tnt_id;
        }
      }
    }
    if (!this.selectedTenant && this.tenantOptions.length) {
      this.selectedTenant = this.tenantOptions[0].tnt_id;
    }
  }

  private fetchSelectionData(): void {
    if (!this.selectedTenant && this.selectedTenant !== 0) {
      return;
    }
    this.api
      .get(`playback/vehicle/groups/${this.selectedTenant}`)
      .subscribe((res) => {
        this.vehicleGroupOptions = res?.["data"] ?? [];
      });

    this.api
      .get(`playback/landmark/groups/${this.selectedTenant}`)
      .subscribe((res) => {
        this.locationGroupOptions = res?.["data"] ?? [];
      });

    // Fetch location points for plotting
    this.api.get("playback/location/points").subscribe((res) => {
      // Store all points for later use
    });

    // Fetch vehicle categories
    this.fetchVehicleCategories();
  }

  private fetchVehicleCategories(): void {
    if (!this.selectedTenant) {
      return;
    }
    const postData = { asrt_grp: this.selectedVehicleGroup || 0 };
    this.api.post(postData, "playback/categories/assets").subscribe((res) => {
      this.vehicleCategoryOptions = res?.["data"] ?? [];
    });
  }

  onVehicleCategoryChange(categoryId: number): void {
    this.selectedVehicleCategory = categoryId;
    this.applyVehicleFilters();
  }

  private fetchLiveVehicles(): void {
    if (!this.selectedTenant && this.selectedTenant !== 0) {
      this.vehicles = [];
      this.stslist = [];
      return;
    }

    // Cancel any in-flight API call to prevent race conditions with tenant fallback
    if (this.liveVehiclesSub) {
      this.liveVehiclesSub.unsubscribe();
      this.liveVehiclesSub = null;
    }

    // Don't show loading spinner on auto-refresh to avoid UI flicker
    const isAutoRefresh = !!this.liveTrackRefreshInterval;
    if (!isAutoRefresh) {
      this.isLoading = true;
      this.isSummaryLoading = true;
    }

    // Use same API as live-tracking component (line 1107)
    console.log(
      "Calling livetrack API:",
      `playback/vehicles/master/vehicles/${this.selectedTenant}/vendor/${this.selectedVendor}`
    );
    this.liveVehiclesSub = this.api
      .get(
        `playback/vehicles/master/vehicles/${this.selectedTenant}/vendor/${this.selectedVendor}`
      )
      .subscribe({
        next: (res) => {
          const previousVehicles = new Map(
            this.allVehiclesRaw.map((v) => [v.asrt_id || v.asrt_nm, v])
          );

          // Preserve selected vehicle before updating data (to keep widget open during refresh)
          const previouslySelectedVehicleId =
            this.selectedVehicleRaw?.asrt_id ||
            this.selectedVehicleRaw?.asrt_nm;
          const wasSliderOpen =
            this.showVehicleSlider && this.selectedVehicleRaw;

          this.allVehiclesRaw = res?.["data"] ?? [];
          this.filteredVehiclesRaw = this.allVehiclesRaw;

          // If no vehicles found on initial load, try next tenant that has vehicles
          if (
            this.tenantFallbackActive &&
            this.allVehiclesRaw.length === 0 &&
            this.tenantOptions.length > 1
          ) {
            this.tenantFallbackIndex++;
            if (this.tenantFallbackIndex < this.tenantOptions.length) {
              this.selectedTenant =
                this.tenantOptions[this.tenantFallbackIndex].tnt_id;
              this.fetchSelectionData();
              this.fetchLiveVehicles();
              return;
            }
          }
          this.tenantFallbackActive = false;

          // Apply filters - this populates status lists and summaries from filtered data
          this.applyVehicleFilters();

          // Restore selected vehicle after refresh if widget was open (preserve widget state)
          if (
            wasSliderOpen &&
            previouslySelectedVehicleId &&
            this.activeHeaderTab === "livetrack"
          ) {
            const updatedVehicle = this.allVehiclesRaw.find(
              (v) =>
                v.asrt_id?.toString() ===
                  previouslySelectedVehicleId?.toString() ||
                v.asrt_nm?.toString() ===
                  previouslySelectedVehicleId?.toString()
            );
            if (updatedVehicle) {
              const currentVehicleId =
                updatedVehicle.asrt_id?.toString() ||
                updatedVehicle.asrt_nm?.toString();

              // Only update snapshot if vehicle ID actually changed (prevents widget refresh)
              if (this.previousSelectedVehicleId !== currentVehicleId) {
                this.selectedVehicleRawSnapshot = JSON.parse(
                  JSON.stringify(updatedVehicle)
                );
                this.previousSelectedVehicleId = currentVehicleId;
              }
              // Always update selectedVehicleRaw with fresh data (for map display)
              this.selectedVehicleRaw = updatedVehicle;
              this.showVehicleSlider = true; // Keep widget open
            }
          }

          // Update playbackVehicles for playback mode dropdown (all vehicles, not filtered)
          if (this.activeHeaderTab === "playback") {
            this.playbackVehicles = this.allVehiclesRaw.map((vehicle, idx) =>
              this.mapVehicleToDisplay(vehicle, idx)
            );
            this.filterPlaybackVehicles();

            // If there's a pending vehicle ID from trip data, set it now that vehicles are loaded
            if (
              this.pendingPlaybackVehicleId &&
              this.filteredPlaybackVehicles.length > 0
            ) {
              // Find the vehicle in the list to verify it exists
              const vehicleExists = this.filteredPlaybackVehicles.find(
                (v: any) =>
                  v.asrt_id == this.pendingPlaybackVehicleId ||
                  String(v.asrt_id) === String(this.pendingPlaybackVehicleId)
              );
              if (vehicleExists) {
                // Use the exact asrt_id from the found vehicle, converted to string to match dropdown type
                this.playbackFilters.vehicleId = String(vehicleExists.asrt_id);
                console.log(
                  "Set pending playback vehicle:",
                  vehicleExists.asrt_id,
                  "Vehicle name:",
                  vehicleExists.id
                );
              }
              // Clear pending after setting
              this.pendingPlaybackVehicleId = null;
            }
          }

          // Update stslist after filters are applied - always update for livetrack mode
          setTimeout(() => {
            this.updateStsList(); // Update DataGrid data

            // Update map markers with smooth transitions for vehicle movement
            if (this.activeHeaderTab === "livetrack") {
              this.updateVehicleMarkersWithAnimation(previousVehicles);

              // After markers are updated, open popup only for selected vehicle and maintain zoom
              if (wasSliderOpen && this.selectedVehicleRaw && this.map) {
                setTimeout(() => {
                  this.openPopupForSelectedVehicle();
                  this.maintainZoomOnSelectedVehicle();
                }, 200); // Small delay to ensure markers are rendered
              }
            }

            // Turn off loading for initial load + fallback (not auto-refresh which causes flicker)
            if (!isAutoRefresh || this.isLoading) {
              this.isSummaryLoading = false;
              this.isLoading = false;
              // After loading overlay disappears, invalidate map size and zoom to tenant
              if (this.map) {
                setTimeout(() => {
                  this.map.invalidateSize();
                  this.zoomToTenantLocation();
                }, 100);
              }
            }
            // Force change detection to ensure table is visible
            this.cdr.detectChanges();
          }, 0);
        },
        error: () => {
          this.isSummaryLoading = false;
          this.isLoading = false;
        },
      });
  }

  private updateVehicleMarkersWithAnimation(
    previousVehicles: Map<any, any>
  ): void {
    if (!this.map || this.activeHeaderTab !== "livetrack") {
      return;
    }

    const currentVehicleIds = new Set(
      this.allVehiclesRaw.map((v) => v.asrt_id?.toString() || v.asrt_nm)
    );
    const previousVehicleIds = new Set(previousVehicles.keys());

    // Check if vehicles were added or removed (need full re-render)
    const vehiclesAdded = Array.from(currentVehicleIds).filter(
      (id) => !previousVehicleIds.has(id)
    );
    const vehiclesRemoved = Array.from(previousVehicleIds).filter(
      (id) => !currentVehicleIds.has(id)
    );

    // If vehicles were added or removed, do full re-render
    if (vehiclesAdded.length > 0 || vehiclesRemoved.length > 0) {
      setTimeout(() => {
        this.renderVehicleMarkers();
        // After full re-render, open popup and maintain zoom for selected vehicle
        if (this.showVehicleSlider && this.selectedVehicleRaw && this.map) {
          setTimeout(() => {
            this.openPopupForSelectedVehicle();
            this.maintainZoomOnSelectedVehicle();
          }, 200); // Small delay to ensure markers are rendered
        }
      }, 100);
      return;
    }

    // Update existing markers with new positions (smooth animation) - matching live-tracking approach
    this.allVehiclesRaw.forEach((vehicle) => {
      const vehicleId = vehicle.asrt_id;

      // If vehicle position changed, animate marker movement (matching live-tracking lines 1542-1575)
      if (
        vehicle.lat !== null &&
        vehicle.lng !== null &&
        this.m_prv_lat[vehicleId] != vehicle.lat &&
        this.m_prv_lat[vehicleId] != null &&
        this.m_prv_lng[vehicleId] != null
      ) {
        // Get animated marker
        const animatedMarker = this.mov_marker[vehicleId];
        if (animatedMarker) {
          // Remove old marker
          this.map.removeLayer(animatedMarker);

          // Create new path for animation (matching live-tracking line 1545)
          const parisKievLL = [
            [this.m_prv_lat[vehicleId], this.m_prv_lng[vehicleId]],
            [vehicle.lat, vehicle.lng],
          ];

          // Get icon using new icon system with color coding
          const iconUrl = this.getVehicleIconUrl(vehicle);
          const movIcon = new L.Icon({
            iconUrl: iconUrl,
            iconSize: [40, null],
            iconAnchor: [10, 15],
            popupAnchor: [5, -24],
            shaowSize: [35, 35],
            asrt_id: vehicle.asrt_id,
            latlang: [vehicle.lat, vehicle.lng],
            title:
              vehicle.asrt_nm +
              " | " +
              vehicle.asrt_ctgry_nm +
              " | " +
              vehicle.asrt_grp_nm,
          });

          const iconAngle = vehicle.direction || vehicle.drcn_ct || 0;
          const title =
            vehicle.asrt_nm +
            " | " +
            vehicle.asrt_ctgry_nm +
            " | " +
            vehicle.asrt_grp_nm +
            " | speed: " +
            vehicle.spd_ct +
            " kmph";

          // Create new animated marker with path (matching live-tracking line 1553)
          // Set duration to 10 seconds to match API refresh interval for smooth continuous movement
          this.mov_marker[vehicleId] = L.animatedMarker(parisKievLL, {
            icon: movIcon,
            autoStart: false,
            duration: 10000, // 10 seconds - matches API refresh interval for smooth animation
          })
            .setRotationAngle(iconAngle + 270)
            .bindPopup(title, {
              className: "vehicle-popup",
              maxWidth: 200,
              offset: [0, -10],
              autoPan: false, // Prevent auto-panning when popup is open
            })
            .addTo(this.map);

          // Make popup follow the moving vehicle
          // Listen to move events and update popup position
          this.mov_marker[vehicleId].on("move", () => {
            const marker = this.mov_marker[vehicleId];
            if (marker) {
              try {
                const isOpen = marker.isPopupOpen && marker.isPopupOpen();
                if (isOpen) {
                  const latlng = marker.getLatLng();
                  const popup = marker.getPopup();
                  if (popup && latlng) {
                    popup.setLatLng(latlng);
                  }
                }
              } catch (e) {
                // Ignore errors
              }
            }
          });

          // Also update popup position during animation using interval
          const updatePopupPosition = () => {
            const marker = this.mov_marker[vehicleId];
            if (marker) {
              try {
                const isOpen = marker.isPopupOpen && marker.isPopupOpen();
                if (isOpen) {
                  const latlng = marker.getLatLng();
                  const popup = marker.getPopup();
                  if (popup && latlng) {
                    popup.setLatLng(latlng);
                  }
                }
              } catch (e) {
                // Ignore errors
              }
            }
          };

          // Update popup position periodically when marker is animating
          this.mov_marker[vehicleId].on("start", () => {
            const intervalId = setInterval(() => {
              const marker = this.mov_marker[vehicleId];
              if (marker) {
                try {
                  const isOpen = marker.isPopupOpen && marker.isPopupOpen();
                  if (isOpen) {
                    updatePopupPosition();
                  } else {
                    clearInterval(intervalId);
                  }
                } catch (e) {
                  clearInterval(intervalId);
                }
              } else {
                clearInterval(intervalId);
              }
            }, 100); // Update every 100ms during animation

            // Store interval ID for cleanup
            (this.mov_marker[vehicleId] as any).popupUpdateInterval =
              intervalId;
          });

          this.mov_marker[vehicleId].on("end", () => {
            const intervalId = (this.mov_marker[vehicleId] as any)
              .popupUpdateInterval;
            if (intervalId) {
              clearInterval(intervalId);
              (this.mov_marker[vehicleId] as any).popupUpdateInterval = null;
            }
          });

          // Start animation immediately (matching live-tracking line 1559)
          // This ensures vehicles move smoothly between API calls
          this.mov_marker[vehicleId].start();

          // Update previous position
          this.m_prv_lat[vehicleId] = vehicle.lat;
          this.m_prv_lng[vehicleId] = vehicle.lng;

          // Re-add click handler
          this.mov_marker[vehicleId].on("click", (e: any) => {
            const clickedVehicle = this.allVehiclesRaw.find(
              (v) => e.target?.options?.icon?.options?.asrt_id == v.asrt_id
            );
            if (clickedVehicle) {
              this.selectVehicleFromRaw(clickedVehicle);
              this.selectedVehicleRaw = clickedVehicle;
              const currentVehicleId =
                clickedVehicle?.asrt_id?.toString() ||
                clickedVehicle?.asrt_nm?.toString();
              // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
              if (this.previousSelectedVehicleId !== currentVehicleId) {
                this.selectedVehicleRawSnapshot = JSON.parse(
                  JSON.stringify(clickedVehicle)
                );
                this.previousSelectedVehicleId = currentVehicleId;
              }
              this.showVehicleSlider = true;
            }
          });

          // Re-add mouseover handler
          this.mov_marker[vehicleId].on("mouseover", (e: any) => {
            if (e.target?.options?.icon?.options?.latlang) {
              const popup = L.popup(e.target.options.icon.options.latlang, {
                content: e.target.options.icon.options.title,
              }).openOn(this.map);
            }
          });

          // Maintain opacity based on zoom level
          const zoom = this.map.getZoom();
          if (zoom < 13) {
            this.mov_marker[vehicleId].setOpacity(0);
          } else {
            this.mov_marker[vehicleId].setOpacity(1);
          }

          // Also update regular marker (cluster marker) position with smooth animation
          // This ensures vehicles appear to move smoothly even when zoomed out in cluster mode
          const regularMarkerKey = `regular_${
            vehicleId?.toString() || vehicle.asrt_nm
          }`;
          let regularMarker = this.markerRegistry.get(regularMarkerKey);
          // Fallback: try to get regular marker from animated marker reference
          if (
            !regularMarker &&
            animatedMarker &&
            (animatedMarker as any).regularMarkerRef
          ) {
            regularMarker = (animatedMarker as any).regularMarkerRef;
          }
          if (regularMarker && regularMarker !== animatedMarker) {
            if (regularMarker.setLatLng) {
              // Animate regular marker smoothly to show movement even in cluster mode
              // Use 10 seconds duration to match API refresh interval for continuous movement
              regularMarker.setLatLng([vehicle.lat, vehicle.lng], {
                animate: true,
                duration: 10.0, // 10 seconds to match API refresh interval
                easeLinearity: 0.25,
              });
            }

            // Update icon rotation to show direction change
            if ((regularMarker as any).setRotationAngle) {
              (regularMarker as any).setRotationAngle(iconAngle + 270);
            }

            // Update popup content with new speed
            if (regularMarker.getPopup) {
              const popup = regularMarker.getPopup();
              if (popup) {
                popup.setContent(title);
              }
            }

            // Update cluster group to reflect new positions smoothly
            // Remove and re-add marker to cluster to update its position
            if (this.vehicleClusterGroup && regularMarker) {
              // Remove from cluster temporarily to allow smooth animation
              this.vehicleClusterGroup.removeLayer(regularMarker);
              // Re-add after a small delay to allow animation to start
              setTimeout(() => {
                if (this.vehicleClusterGroup && regularMarker) {
                  this.vehicleClusterGroup.addLayer(regularMarker);
                }
              }, 100);
            }
          }
        }
      } else if (vehicle.lat !== null && vehicle.lng !== null) {
        // First time or no previous position - just update stored position
        this.m_prv_lat[vehicleId] = vehicle.lat;
        this.m_prv_lng[vehicleId] = vehicle.lng;
      }
    });

    // Update table data
    this.updateStsList();
    this.cdr.detectChanges();
  }

  /**
   * Open popup only for the selected vehicle marker (not all markers)
   */
  private openPopupForSelectedVehicle(): void {
    if (!this.selectedVehicleRaw || !this.map) {
      return;
    }

    const markerKey =
      this.selectedVehicleRaw.asrt_id?.toString() ||
      this.selectedVehicleRaw.asrt_nm;
    if (!markerKey) {
      return;
    }

    // Try to find animated marker first
    let marker = this.markerRegistry.get(markerKey);

    // If not found, try regular marker
    if (!marker) {
      marker = this.markerRegistry.get(`regular_${markerKey}`);
    }

    // If still not found, try animated marker from mov_marker object
    if (!marker && this.selectedVehicleRaw.asrt_id) {
      marker = this.mov_marker[this.selectedVehicleRaw.asrt_id] || null;
    }

    // Open popup only for selected vehicle
    if (marker) {
      // Close all other popups first
      this.map.closePopup();
      // Open popup for selected vehicle
      marker.openPopup();
    }
  }

  /**
   * Maintain zoom level on selected vehicle (not cluster) after data reload
   */
  private maintainZoomOnSelectedVehicle(): void {
    if (!this.selectedVehicleRaw || !this.map) {
      return;
    }

    if (this.selectedVehicleRaw.lat && this.selectedVehicleRaw.lng) {
      // Get current zoom level or use stored zoom level
      const targetZoom =
        this.selectedVehicleZoomLevel || this.map.getZoom() || 17;

      // Ensure we're zoomed to the vehicle, not a cluster
      // Use setView with a minimum zoom level to prevent clustering
      if (targetZoom < 13) {
        // If zoom is too low (clustering), zoom in to show individual vehicle
        this.map.setView(
          [this.selectedVehicleRaw.lat, this.selectedVehicleRaw.lng],
          17,
          {
            animate: true,
            duration: 0.5,
          }
        );
        this.selectedVehicleZoomLevel = 17;
      } else {
        // Maintain current zoom level on vehicle location (use stored zoom or minimum 17)
        const finalZoom = targetZoom >= 13 ? targetZoom : 17;
        this.map.setView(
          [this.selectedVehicleRaw.lat, this.selectedVehicleRaw.lng],
          finalZoom,
          {
            animate: true,
            duration: 0.5,
          }
        );
        this.selectedVehicleZoomLevel = finalZoom;
      }
    }
  }

  private applyVehicleFilters(): void {
    const filtered = this.allVehiclesRaw.filter((vehicle) => {
      if (
        this.selectedVehicleGroup &&
        this.selectedVehicleGroup != 0 &&
        vehicle.asrt_grp_id != this.selectedVehicleGroup
      ) {
        return false;
      }
      if (
        this.selectedVehicleCategory &&
        this.selectedVehicleCategory != 0 &&
        vehicle.asrt_ctgry_id != this.selectedVehicleCategory
      ) {
        return false;
      }
      if (
        this.selectedLocationGroup &&
        vehicle.fnce_grp_id != this.selectedLocationGroup
      ) {
        return false;
      }
      return true;
    });
    this.filteredVehiclesRaw = filtered;
    this.vehicles = filtered.map((vehicle, idx) =>
      this.mapVehicleToDisplay(vehicle, idx)
    );

    // For playback mode, populate all vehicles (not filtered) for vehicle selection dropdown
    if (this.activeHeaderTab === "playback") {
      this.playbackVehicles = this.allVehiclesRaw.map((vehicle, idx) =>
        this.mapVehicleToDisplay(vehicle, idx)
      );
      // Apply filters for playback vehicles
      this.filterPlaybackVehicles();
    } else {
      // In livetrack mode, use filtered vehicles
      this.playbackVehicles = this.vehicles;
      this.filteredPlaybackVehicles = this.playbackVehicles;
    }

    // Only auto-select first vehicle if in playback mode and no vehicle is selected
    if (this.activeHeaderTab === "livetrack") {
      // Don't auto-select in livetrack - show all vehicles
      // BUT preserve selected vehicle if widget is open (don't close widget during refresh)
      if (!this.showVehicleSlider || !this.selectedVehicleRaw) {
        this.selectedVehicle = null;
        this.selectedVehicleRaw = null;
      } else {
        // Widget is open - update selectedVehicleRaw with fresh data but keep widget open
        const selectedId =
          this.selectedVehicleRaw?.asrt_id || this.selectedVehicleRaw?.asrt_nm;
        if (selectedId) {
          const updatedVehicle = this.allVehiclesRaw.find(
            (v) =>
              v.asrt_id?.toString() === selectedId?.toString() ||
              v.asrt_nm?.toString() === selectedId?.toString()
          );
          if (updatedVehicle) {
            this.selectedVehicleRaw = updatedVehicle;
            // Also update selectedVehicle to match
            this.selectedVehicle =
              this.vehicles.find(
                (v) =>
                  v.id === updatedVehicle.asrt_nm ||
                  v.id === updatedVehicle.asrt_id?.toString()
              ) || null;
            // Don't update snapshot - keep using original snapshot to prevent widget API calls
          } else {
            // Vehicle no longer exists - close widget
            this.selectedVehicle = null;
            this.selectedVehicleRaw = null;
            this.selectedVehicleRawSnapshot = null;
            this.showVehicleSlider = false;
          }
        }
      }
    } else if (this.activeHeaderTab === "playback" && !this.selectedVehicle) {
      this.selectedVehicle = this.vehicles[0] ?? null;
      this.playbackFilters.vehicleId = this.selectedVehicle?.id ?? "";
      if (this.selectedVehicle) {
        this.selectedVehicleRaw =
          filtered.find((v) => v.asrt_nm === this.selectedVehicle!.id) || null;
      }
    }
    // Populate status lists with filtered vehicles so table reflects active filters
    this.populateStatusLists();
    // Compute summaries from filtered vehicles
    this.computeSummaries(this.filteredVehiclesRaw);
    // this.updateAvailableTrips();
    this.currentPage = 1; // Reset to first page on filter change
    this.updateStsList(); // Update DataGrid data
    this.renderVehicleMarkers();
    this.isSummaryLoading = false;
  }

  onVehicleGroupChange(): void {
    this.selectedVehicleCategory = 0; // Reset category when group changes
    this.fetchVehicleCategories();
    this.applyVehicleFilters();
  }

  private mapVehicleToDisplay(vehicle: any, index: number): TrackingVehicle {
    const lastPing = vehicle.data_received_ts
      ? moment(vehicle.data_received_ts).format("DD-MM-YYYY hh:mm:ss A")
      : "--";
    const diffMins = this.diffInMinutes(vehicle.data_received_ts);
    const status = this.resolveVehicleStatus(vehicle, diffMins);
    return {
      id: vehicle.asrt_nm ?? `Vehicle-${index + 1}`,
      shortName: vehicle.asrt_nm?.split(" ")[0] ?? `VH-${index + 1}`,
      status,
      lat: Number(vehicle.lat) || 0,
      lng: Number(vehicle.lng) || 0,
      speed: Number(vehicle.spd_ct) || 0,
      lastPing,
      location: vehicle.loc_nm ?? "—",
      driver: {
        name: vehicle.crw_nm ?? "NA",
        contact: vehicle.crw_ph ?? "--",
        license: vehicle.lic_no ?? "—",
        doj: vehicle.driver_doj ?? "--",
      },
      vehicle: {
        make: vehicle.asrt_type_nm ?? "Fleet",
        model: vehicle.asrt_ctgry_nm ?? "General",
        plate: vehicle.asrt_nm ?? "--",
        vin: vehicle.dev_imei_nu ?? "--",
        fuel: vehicle.fuel_typ ?? "—",
      },
      trip: {
        label: vehicle.asrt_grp_nm ?? "Active Route",
        distance: vehicle.odmtr_ct ? `${vehicle.odmtr_ct} km` : "—",
        duration: vehicle.trp_duration ?? "--",
        alerts: vehicle.alert_cnt ?? 0,
      },
      parkingSummary: {
        count: vehicle.parking_cnt ?? 0,
        longest: vehicle.longest_stop ?? "--",
      },
      rawStatusId: vehicle.asrtglblId,
      diffMins,
      tripId: vehicle.trp_rn_id ?? "",
      icon_path: vehicle.icon_path || vehicle.icon_pathr,
      icon_pathr: vehicle.icon_pathr || vehicle.icon_path,
      direction: Number(vehicle.drcn_ct) || Number(vehicle.direction) || 0,
      asrt_id: vehicle.asrt_id,
    };
  }

  private computeSummaries(list: any[]): void {
    // Update counts directly from arrays (matching live-tracking component logic)
    // After populateStatusLists() is called, these arrays are populated correctly
    const updates: Record<string, number> = {
      total: this.allVehiclesList ? this.allVehiclesList.length : 0,
      online: this.vehiclesList ? this.vehiclesList.length : 0,
      offline: this.offlineVehicles ? this.offlineVehicles.length : 0,
      repair: this.repairVehicles ? this.repairVehicles.length : 0,
      others: this.notOpratedVehicles ? this.notOpratedVehicles.length : 0,
      running: this.runningVehicles ? this.runningVehicles.length : 0,
      stationedShort: this.stationedShort ? this.stationedShort.length : 0,
      stationedMedium: this.stationedMedium ? this.stationedMedium.length : 0,
      stationedLong: this.stationedLong ? this.stationedLong.length : 0,
    };
    this.summaryCards = this.summaryCards.map((card) => ({
      ...card,
      value: updates[card.key] ?? 0,
    }));
  }

  private updateAvailableTrips(): void {
    if (!this.playbackFilters.vehicleId) {
      this.availableTrips = [];
      this.availableTripsData = [];
      return;
    }
    // Fetch trips for selected vehicle and date (like playback component)
    // Use asrt_id from vehicle data (playbackFilters.vehicleId already contains asrt_id)
    const asrt_id = this.playbackFilters.vehicleId;
    const date = this.playbackTrip.date || this.buildLocalDateValue(0);

    var preDtaObj = {
      trpdate: this.datepipe.transform(date, "yyyy-MM-dd"),
      // "sdate": this.datepipe.transform(this.sdate, 'yyyy-MM-dd 00:00'),
      // "edate": this.datepipe.transform(this.edate, 'yyyy-MM-dd 23:59'),
      asrt_id: asrt_id,
    };
    console.log(preDtaObj);

    // Show loading while fetching trip data
    this.tripDataLoading = true;

    this.api.post(preDtaObj, "playback/vehicle/trips").subscribe(
      (res) => {
        const trips = res?.["data"] || [];
        this.availableTripsData = trips; // Store full trip data
        console.log(this.availableTripsData);
        this.tripDataLoading = false; // Hide loading after data is bound
        // this.availableTrips = trips.map((trip: any) => ({
        //   id: trip.trip_rn_id||0,
        //   label: trip.rte_nm || `Trip ${trip.trp_sqnce_nu||0} ${trip.rte_nm||0}`
        // }));

        // this.getTripData(this.availableTrips[0].id);
      },
      (err) => {
        console.error("Error fetching trips:", err);
        this.tripDataLoading = false; // Hide loading on error
        this.availableTripsData = [];
      }
    );

    // this.api.get(`playback/trips/${asrt_id}/${date}`).subscribe((res) => {
    //   const trips = res?.['data'] || [];
    //   this.availableTrips = trips.map((trip: any) => ({
    //     id: trip.trip_id || trip.id,
    //     label: trip.trip_label || trip.label || `Trip ${trip.trip_id || trip.id}`
    //   }));

    //   // Clear trip selection if current trip is not in new list
    //   if (this.playbackTrip.tripId && !this.availableTrips.find(t => t.id === this.playbackTrip.tripId)) {
    //     this.playbackTrip.tripId = '';
    //   }
    // }, (err) => {
    //   console.error('Error fetching trips:', err);
    //   this.availableTrips = [];
    // });
  }

  onTripDateChange(date: string): void {
    this.playbackTrip.date = date;
    // Clear trip selection and fetch new trips
    this.playbackTrip.tripId = "";
    this.updateAvailableTrips();
    // Force change detection to update widget
    this.cdr.detectChanges();
  }

  /**
   * Handle From datetime-local picker change
   */
  onFromPickerChange(): void {
    if (!this.playbackFilters.from) return;
    const parsed = moment(
      this.playbackFilters.from,
      ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"],
      true
    );
    if (parsed.isValid()) {
      this.playbackFilters.fromDisplay = parsed.format("DD-MM-YYYY HH:mm:ss");
      this.onRangeDateChange();
    }
  }

  /**
   * Handle To datetime-local picker change
   */
  onToPickerChange(): void {
    if (!this.playbackFilters.to) return;
    const parsed = moment(
      this.playbackFilters.to,
      ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"],
      true
    );
    if (parsed.isValid()) {
      this.playbackFilters.toDisplay = parsed.format("DD-MM-YYYY HH:mm:ss");
      this.onRangeDateChange();
    }
  }

  /**
   * Max value for datetime-local input (prevents future dates)
   */
  get todayDateTimeMax(): string {
    return moment().format("YYYY-MM-DDTHH:mm");
  }

  /**
   * Max value for date input (prevents future dates)
   */
  get todayDateMax(): string {
    return moment().format("YYYY-MM-DD");
  }

  /**
   * Handle Trip date picker change
   */
  onTripDatePickerChange(): void {
    if (!this.playbackTrip.date) return;
    const parsed = moment(this.playbackTrip.date, "YYYY-MM-DD", true);
    if (parsed.isValid()) {
      this.playbackTrip.dateDisplay = parsed.format("DD-MM-YYYY");
      this.playbackTrip.tripId = "";
      this.updateAvailableTrips();
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle From date input change (dd-mm-yyyy HH:mm:ss format)
   * Parses the display format and converts to internal format
   */
  onFromDateChange(_event?: any): void {
    const value = this.playbackFilters.fromDisplay;
    if (!value) return;

    // Parse dd-mm-yyyy HH:mm:ss format
    const parsed = moment(value, "DD-MM-YYYY HH:mm:ss", true);
    if (parsed.isValid()) {
      this.playbackFilters.from = parsed.format("YYYY-MM-DDTHH:mm");
      this.onRangeDateChange();
    } else {
      // Try dd-mm-yyyy format without time
      const parsedDateOnly = moment(value, "DD-MM-YYYY", true);
      if (parsedDateOnly.isValid()) {
        this.playbackFilters.from =
          parsedDateOnly.format("YYYY-MM-DD") + "T00:00";
        this.playbackFilters.fromDisplay =
          parsedDateOnly.format("DD-MM-YYYY") + " 00:00:00";
        this.onRangeDateChange();
      }
    }
  }

  /**
   * Handle To date input change (dd-mm-yyyy HH:mm:ss format)
   * Parses the display format and converts to internal format
   */
  onToDateChange(_event?: any): void {
    const value = this.playbackFilters.toDisplay;
    if (!value) return;

    // Parse dd-mm-yyyy HH:mm:ss format
    const parsed = moment(value, "DD-MM-YYYY HH:mm:ss", true);
    if (parsed.isValid()) {
      this.playbackFilters.to = parsed.format("YYYY-MM-DDTHH:mm");
      this.onRangeDateChange();
    } else {
      // Try dd-mm-yyyy format without time
      const parsedDateOnly = moment(value, "DD-MM-YYYY", true);
      if (parsedDateOnly.isValid()) {
        this.playbackFilters.to =
          parsedDateOnly.format("YYYY-MM-DD") + "T23:59";
        this.playbackFilters.toDisplay =
          parsedDateOnly.format("DD-MM-YYYY") + " 23:59:59";
        this.onRangeDateChange();
      }
    }
  }

  /**
   * Handle Trip Date input change (dd-mm-yyyy format)
   * Parses the display format and converts to internal format
   */
  onTripDateInputChange(_event?: any): void {
    const value = this.playbackTrip.dateDisplay;
    if (!value) return;

    // Parse dd-mm-yyyy format
    const parsed = moment(value, "DD-MM-YYYY", true);
    if (parsed.isValid()) {
      this.playbackTrip.date = parsed.format("YYYY-MM-DD");
      // Clear trip selection and fetch new trips
      this.playbackTrip.tripId = "";
      this.updateAvailableTrips();
      this.cdr.detectChanges();
    }
  }

  onRangeDateChange(): void {
    // Update display format when range dates change
    if (this.playbackFilters.from) {
      const fromMoment = moment(
        this.playbackFilters.from,
        ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"],
        true
      );
      this.rangeFromTimeDisplay = fromMoment.isValid()
        ? `TH-${fromMoment.format("DD-MM-YYYY HH:mm:ss")}`
        : "--";
    } else {
      this.rangeFromTimeDisplay = "--";
    }

    if (this.playbackFilters.to) {
      const toMoment = moment(
        this.playbackFilters.to,
        ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"],
        true
      );
      this.rangeToTimeDisplay = toMoment.isValid()
        ? `TH-${toMoment.format("DD-MM-YYYY HH:mm:ss")}`
        : "--";
    } else {
      this.rangeToTimeDisplay = "--";
    }
  }

  getPlaybackDateForWidget(): string | null {
    if (!this.playbackTrip.date) {
      return null;
    }
    // Convert date to YYYY-MM-DD format
    if (this.playbackTrip.date.includes("T")) {
      return this.playbackTrip.date.split("T")[0];
    }
    // If already in YYYY-MM-DD format, return as is
    if (this.playbackTrip.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return this.playbackTrip.date;
    }
    // Try to parse and format
    try {
      const date = new Date(this.playbackTrip.date);
      return moment(date).format("YYYY-MM-DD");
    } catch {
      return null;
    }
  }

  private resolveVehicleStatus(
    vehicle: any,
    diffMins: number
  ): Exclude<VehicleStatusKey, "all"> {
    if (vehicle.asrtglblId !== 1) {
      return "offline";
    }
    if (vehicle.spd_ct > 0 && diffMins < 5) {
      return "running";
    }
    if (diffMins <= 15) {
      return "idle";
    }
    return "stopped";
  }

  private diffInMinutes(
    timestamp: string,
    reference: moment.Moment = moment()
  ): number {
    if (!timestamp) {
      return Number.MAX_SAFE_INTEGER;
    }
    return reference.diff(moment(timestamp), "minutes");
  }

  toggleLeftDrawer(): void {
    this.leftDrawerOpen = !this.leftDrawerOpen;
  }

  toggleVehicleSelection(vehicleId: string): void {
    if (this.selectedVehicles.has(vehicleId)) {
      this.selectedVehicles.delete(vehicleId);
    } else {
      this.selectedVehicles.add(vehicleId);
    }
  }

  toggleAllVehicles(): void {
    if (this.selectedVehicles.size === this.filteredVehicles.length) {
      this.selectedVehicles.clear();
    } else {
      this.filteredVehicles.forEach((v) => this.selectedVehicles.add(v.id));
    }
  }

  getStatusCount(key: string): number {
    // Return count directly from arrays (matching live-tracking component)
    switch (key) {
      case "total":
        return this.allVehiclesList ? this.allVehiclesList.length : 0;
      case "online":
        return this.vehiclesList ? this.vehiclesList.length : 0;
      case "offline":
        return this.offlineVehicles ? this.offlineVehicles.length : 0;
      case "repair":
        return this.repairVehicles ? this.repairVehicles.length : 0;
      case "others":
        return this.notOpratedVehicles ? this.notOpratedVehicles.length : 0;
      case "running":
        return this.runningVehicles ? this.runningVehicles.length : 0;
      case "stationedShort":
        return this.stationedShort ? this.stationedShort.length : 0;
      case "stationedMedium":
        return this.stationedMedium ? this.stationedMedium.length : 0;
      case "stationedLong":
        return this.stationedLong ? this.stationedLong.length : 0;
      default:
        return 0;
    }
  }

  getVehicleStatusIcon(vehicle: TrackingVehicle): string {
    if (vehicle.status === "running") {
      return "▶"; // Green arrow
    }
    if (vehicle.status === "stopped") {
      return "📍"; // Red pin
    }
    return "🔧"; // Wrench for idle/other
  }

  getVehicleStatusColor(vehicle: TrackingVehicle): string {
    if (vehicle.status === "running") {
      return "#22c55e";
    }
    if (vehicle.status === "stopped") {
      return "#ef4444";
    }
    return "#94a3b8";
  }

  getVehicleVoltage(vehicle: TrackingVehicle): string {
    // Check if vehicle has voltage data - this would come from actual API response
    const vehicleData = this.allVehiclesRaw.find(
      (v) => v.asrt_nm === vehicle.id
    );
    if (vehicleData && vehicleData.battry_vltg_ct) {
      return `${vehicleData.battry_vltg_ct}V`;
    }
    return "NA";
  }

  formatTimestamp(timestamp: string): string {
    if (!timestamp || timestamp === "--") return "--";
    try {
      // If timestamp is already formatted, return as is
      if (timestamp.includes("-") && timestamp.includes(":")) {
        return timestamp;
      }
      return moment(timestamp).format("DD-MM-YYYY hh:mm:ss A");
    } catch {
      return timestamp;
    }
  }

  // Smooth playback points (like playback component)
  smoothPlaybackPoints(points: any[]): any[] {
    if (!points || points.length < 3) return points;
    const result: any[] = [];

    const isParking = (p: any) =>
      p &&
      ((p.stoppageTime !== undefined && p.stoppageTime > 0) ||
        p.speed_ct === 0);

    // Indices of anchors (parking or endpoints)
    const anchors: number[] = [];
    for (let i = 0; i < points.length; i++) {
      if (isParking(points[i])) anchors.push(i);
    }
    if (anchors[0] !== 0) anchors.unshift(0);
    if (anchors[anchors.length - 1] !== points.length - 1)
      anchors.push(points.length - 1);

    for (let a = 0; a < anchors.length - 1; a++) {
      const startIdx = anchors[a];
      const endIdx = anchors[a + 1];
      const segment = points.slice(startIdx, endIdx + 1);

      const start = segment[0];
      const end = segment[segment.length - 1];

      if (segment.length <= 2) {
        if (result.length === 0) result.push(start);
        else if (result[result.length - 1] !== start) result.push(start);
        result.push(end);
        continue;
      }

      const moving = segment.some(
        (p) =>
          p &&
          (p.speed_ct === undefined || p.speed_ct > 0) &&
          !(p.stoppageTime > 0)
      );
      if (!moving) {
        if (result.length === 0) result.push(...segment);
        else {
          result.push(...segment.slice(1));
        }
        continue;
      }

      const coords = segment.map((p) => ({ lat: p.lat, lng: p.lng }));
      const smoothedCoords = this.chaikinSmoothSegment(coords, 2, true);

      let seedSpeed: number | undefined = undefined;
      for (const p of segment) {
        if (
          typeof p?.speed_ct === "number" &&
          !isNaN(p.speed_ct) &&
          p.speed_ct > 0
        ) {
          seedSpeed = p.speed_ct;
          break;
        }
      }
      if (seedSpeed === undefined) {
        if (
          typeof start?.speed_ct === "number" &&
          !isNaN(start.speed_ct) &&
          start.speed_ct > 0
        )
          seedSpeed = start.speed_ct;
        else if (
          typeof end?.speed_ct === "number" &&
          !isNaN(end.speed_ct) &&
          end.speed_ct > 0
        )
          seedSpeed = end.speed_ct;
        else if (this.lastKnownSpeedCt !== null)
          seedSpeed = this.lastKnownSpeedCt;
      }

      const rebuilt: any[] = [];
      smoothedCoords.forEach((c, idx) => {
        if (idx === 0) {
          rebuilt.push(start);
        } else if (idx === smoothedCoords.length - 1) {
          rebuilt.push(end);
        } else {
          rebuilt.push({
            lat: c.lat,
            lng: c.lng,
            speed_ct: seedSpeed !== undefined ? seedSpeed : undefined,
            asrt_nm: start.asrt_nm,
            isInterpolated: true,
          });
        }
      });

      if (result.length === 0) result.push(...rebuilt);
      else {
        result.push(...rebuilt.slice(1));
      }
    }

    return result;
  }

  // Chaikin corner-cutting smoothing with optional end preservation
  chaikinSmoothSegment(
    points: { lat: number; lng: number }[],
    iterations = 2,
    keepEnds = true
  ): { lat: number; lng: number }[] {
    if (!points || points.length < 3 || iterations <= 0) return points;
    let current = points.slice();
    for (let it = 0; it < iterations; it++) {
      const next: { lat: number; lng: number }[] = [];
      const start = current[0];
      const end = current[current.length - 1];
      if (keepEnds) next.push({ ...start });
      for (let i = 0; i < current.length - 1; i++) {
        const p = current[i];
        const q = current[i + 1];
        const Q = {
          lat: 0.75 * p.lat + 0.25 * q.lat,
          lng: 0.75 * p.lng + 0.25 * q.lng,
        };
        const R = {
          lat: 0.25 * p.lat + 0.75 * q.lat,
          lng: 0.25 * p.lng + 0.75 * q.lng,
        };
        next.push(Q);
        next.push(R);
      }
      if (keepEnds) next.push({ ...end });
      current = next;
    }
    return current;
  }

  onSearchChange(): void {
    // Filter stslist by search term (vehicle number)
    this.currentPage = 1; // Reset to first page on search
  }

  selectVehicleFromTable(vehicle: any, event?: Event): void {
    if (event) {
      event.stopPropagation(); // Prevent row click when clicking on specific elements
    }

    // Find the vehicle in vehicles list and select it
    const foundVehicle = this.vehicles.find((v) => v.id === vehicle.asrt_nm);
    if (foundVehicle) {
      this.selectVehicle(foundVehicle, true); // Center on map
      // Ensure widget is visible in livetrack mode
      if (this.activeHeaderTab === "livetrack") {
        // Clear all markers when selecting vehicle from table in livetrack
        this.clearAllLiveTrackMarkers();
        // Set vehicle in playback filter (for when switching to playback)
        this.playbackFilters.vehicleId =
          vehicle.asrt_id?.toString() || vehicle.asrt_nm;
        const currentVehicleId =
          vehicle?.asrt_id?.toString() || vehicle?.asrt_nm?.toString();
        // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
        if (this.previousSelectedVehicleId !== currentVehicleId) {
          this.selectedVehicleRawSnapshot = JSON.parse(JSON.stringify(vehicle));
          this.previousSelectedVehicleId = currentVehicleId;
        }
        this.showVehicleSlider = true;
        this.selectedVehicleRaw = vehicle;
        // Re-render only the selected vehicle marker
        setTimeout(() => {
          this.renderVehicleMarkers();
        }, 100);
      }

      // Zoom to vehicle and open popup
      if (this.map && vehicle.lat && vehicle.lng) {
        this.map.flyTo([vehicle.lat, vehicle.lng], 17, {
          animate: true,
          duration: 0.8,
        });
        this.selectedVehicleZoomLevel = 17;

        // Open popup
        const marker = this.markerRegistry.get(
          vehicle.asrt_id?.toString() || vehicle.asrt_nm
        );
        if (marker) {
          marker.openPopup();
        }
      }
    }
  }

  onVehicleNameClick(vehicle: any, event: Event): void {
    event.stopPropagation();
    this.selectVehicleFromTable(vehicle, event);
  }

  onVehicleTableRowClick(e: any): void {
    if (e.data) {
      this.selectVehicleFromTable(e.data);
    }
  }

  onVehicleIconClick(vehicle: any, event: Event): void {
    event.stopPropagation();
    // Show vehicle widget when clicking on icon
    const foundVehicle = this.vehicles.find((v) => v.id === vehicle.asrt_nm);
    if (foundVehicle) {
      this.selectedVehicleRaw = vehicle;
      const currentVehicleId =
        vehicle?.asrt_id?.toString() || vehicle?.asrt_nm?.toString();
      // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
      if (this.previousSelectedVehicleId !== currentVehicleId) {
        this.selectedVehicleRawSnapshot = JSON.parse(JSON.stringify(vehicle));
        this.previousSelectedVehicleId = currentVehicleId;
      }
      this.showVehicleSlider = true;
    }
  }

  onPlaybackIconClick(vehicle: any, event: Event): void {
    event.stopPropagation();
    console.log(vehicle, event);

    // Clear all livetrack markers when switching to playback
    this.clearAllLiveTrackMarkers();

    // Find vehicle in raw vehicles list
    const rawVehicle = this.allVehiclesRaw.find(
      (v) => v.asrt_nm === vehicle.asrt_nm || v.asrt_id === vehicle.asrt_id
    );

    // Set vehicle ID for playback filter - prioritize asrt_id
    let vehicleId: any = null;
    if (rawVehicle?.asrt_id) {
      vehicleId = rawVehicle.asrt_id;
    } else if (vehicle.asrt_id) {
      vehicleId = vehicle.asrt_id;
    } else {
      // Fallback to vehicle name if no ID found
      vehicleId = vehicle.asrt_nm;
    }

    // Switch to playback tab first
    this.activeHeaderTab = "playback";

    // Set playback filter vehicle ID
    this.playbackFilters.vehicleId = String(vehicle.asrt_id);

    // Force change detection to update UI
    this.cdr.detectChanges();

    // Wait for tab switch, then update vehicle selection
    setTimeout(() => {
      // Find vehicle in playback vehicles list
      const playbackVehicle =
        this.filteredPlaybackVehicles.find(
          (v) =>
            v.asrt_id?.toString() === vehicleId.toString() ||
            v.id === vehicle.asrt_nm
        ) ||
        this.playbackVehicles.find(
          (v) =>
            v.asrt_id?.toString() === vehicleId.toString() ||
            v.id === vehicle.asrt_nm
        );

      if (playbackVehicle) {
        this.selectedVehicle = playbackVehicle;
        this.selectedVehicleRaw = rawVehicle || null;
      }

      // Trigger vehicle change to update playback filters and available trips
      this.onPlaybackVehicleChange();
    }, 100);
  }

  getEahVehicle(event: any): void {
    // Handle row click in DataGrid (like livetrack component)
    if (event && event.data) {
      // Find vehicle in raw vehicles list
      const rawVehicle = this.allVehiclesRaw.find(
        (v) =>
          v.asrt_nm === event.data.asrt_nm ||
          v.asrt_id?.toString() === event.data.asrt_id?.toString()
      );

      // Find vehicle in display vehicles list
      const vehicle = this.vehicles.find((v) => v.id === event.data.asrt_nm);

      if (vehicle) {
        // Fly straight to the row's zoom in ONE animation. This used to be
        // selectVehicle(vehicle, true) - a flyTo to zoom 17 - followed
        // immediately by a setView to zoom 20, and the second call cancelled
        // the first mid-flight, so the map cut to the vehicle instead of
        // travelling to it.
        this.selectVehicle(vehicle, true, 20); // Center on map

        // Set raw vehicle data for slider
        if (rawVehicle) {
          this.selectedVehicleRaw = rawVehicle;
        }

        // Show vehicle slider in livetrack mode
        if (this.activeHeaderTab === "livetrack") {
          const currentVehicleId =
            rawVehicle?.asrt_id?.toString() || rawVehicle?.asrt_nm?.toString();

          // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
          if (
            rawVehicle &&
            this.previousSelectedVehicleId !== currentVehicleId
          ) {
            this.selectedVehicleRawSnapshot = JSON.parse(
              JSON.stringify(rawVehicle)
            );
            this.previousSelectedVehicleId = currentVehicleId;
          }
          this.showVehicleSlider = true;

          // The flight is already under way from selectVehicle() above - do not
          // start a second one here, it would cancel the first.

          // Re-render markers to highlight selected vehicle, then open popup.
          // Both wait for the landing: this used to fire renderVehicleMarkers()
          // at +100ms, i.e. squarely inside the 800ms flight, and tearing down
          // and rebuilding every marker there is what made the flight judder.
          this.afterMapSettles(() => {
            this.renderVehicleMarkers();

            if (rawVehicle && this.map) {
              // Find marker from registry using asrt_id or asrt_nm as key,
              // falling back to the non-animated one.
              const markerKey =
                rawVehicle.asrt_id?.toString() || rawVehicle.asrt_nm;
              const marker =
                this.markerRegistry.get(markerKey) ||
                this.markerRegistry.get(`regular_${markerKey}`);
              marker?.openPopup();
            }
          });
        }
      } else if (rawVehicle) {
        // If vehicle not found in display list, use raw vehicle
        this.selectedVehicleRaw = rawVehicle;
        if (this.activeHeaderTab === "livetrack") {
          const currentVehicleId =
            rawVehicle?.asrt_id?.toString() || rawVehicle?.asrt_nm?.toString();

          // Only create/update snapshot if vehicle ID actually changed (prevents widget refresh)
          if (this.previousSelectedVehicleId !== currentVehicleId) {
            this.selectedVehicleRawSnapshot = JSON.parse(
              JSON.stringify(rawVehicle)
            );
            this.previousSelectedVehicleId = currentVehicleId;
          }
          this.showVehicleSlider = true;

          // Focus on vehicle location first and store zoom level
          if (this.map && rawVehicle.lat && rawVehicle.lng) {
            this.map.flyTo([rawVehicle.lat, rawVehicle.lng], 17, {
              animate: true,
              duration: 0.8,
            });
            this.selectedVehicleZoomLevel = 17; // Store zoom level for selected vehicle
          }

          // Re-render markers to highlight selected vehicle, then open popup.
          // Both wait for the landing: this used to fire renderVehicleMarkers()
          // at +100ms, i.e. squarely inside the 800ms flight, and tearing down
          // and rebuilding every marker there is what made the flight judder.
          this.afterMapSettles(() => {
            this.renderVehicleMarkers();

            if (rawVehicle && this.map) {
              // Find marker from registry using asrt_id or asrt_nm as key,
              // falling back to the non-animated one.
              const markerKey =
                rawVehicle.asrt_id?.toString() || rawVehicle.asrt_nm;
              const marker =
                this.markerRegistry.get(markerKey) ||
                this.markerRegistry.get(`regular_${markerKey}`);
              marker?.openPopup();
            }
          });
        }
      }
    }
  }

  updateStsList(): void {
    // Populate status-based vehicle lists (like livetrack component)
    this.populateStatusLists();

    // Update summary counts from filtered vehicles
    this.computeSummaries(
      this.filteredVehiclesRaw.length > 0
        ? this.filteredVehiclesRaw
        : this.allVehiclesRaw
    );

    // Get vehicles based on active summary key (like livetrack component)
    let vehiclesToShow: any[] = [];

    switch (this.activeSummaryKey) {
      case "total":
        vehiclesToShow = this.allVehiclesList;
        break;
      case "online":
        vehiclesToShow = this.vehiclesList; // Online vehicles
        break;
      case "offline":
        vehiclesToShow = this.offlineVehicles;
        break;
      case "repair":
        vehiclesToShow = this.repairVehicles;
        break;
      case "others":
        vehiclesToShow = this.notOpratedVehicles;
        break;
      case "running":
        vehiclesToShow = this.runningVehicles;
        break;
      case "stationedShort":
        vehiclesToShow = this.stationedShort;
        break;
      case "stationedMedium":
        vehiclesToShow = this.stationedMedium;
        break;
      case "stationedLong":
        vehiclesToShow = this.stationedLong;
        break;
      default:
        vehiclesToShow = this.allVehiclesList;
        break;
    }

    // Convert to stslist format (like livetrack component)
    let counter = 0;
    this.stslist = vehiclesToShow.map((vehicle) => {
      const rawVehicle =
        typeof vehicle === "object" && vehicle.asrt_nm
          ? vehicle
          : this.allVehiclesRaw.find(
              (v) => v.asrt_nm === (vehicle.id || vehicle)
            );
      const vehicleObj =
        typeof vehicle === "object" && vehicle.asrt_nm
          ? vehicle
          : this.vehicles.find((v) => v.id === (vehicle.id || vehicle));

      return {
        sno: ++counter,
        asrt_nm: rawVehicle?.asrt_nm || vehicleObj?.id || vehicle,
        asrt_id: rawVehicle?.asrt_id || (vehicleObj as any)?.asrt_id || null, // Add asrt_id for playback
        asrt_sts_nm: rawVehicle?.asrt_sts_nm || vehicleObj?.status || "--",
        asrt_ctgry_nm: rawVehicle?.asrt_ctgry_nm || "--",
        tdy_dst: rawVehicle?.tdy_dst || vehicleObj?.trip?.distance || "--",
        // Add fields needed for status template (like livetrack)
        clr_cd:
          rawVehicle?.clr_cd ||
          this.getVehicleStatusColor(vehicleObj || ({} as TrackingVehicle)),
        sgnl_strngth_ct: rawVehicle?.sgnl_strngth_ct || 0,
        igntn_in: rawVehicle?.igntn_in || 0,
        chrg_in: rawVehicle?.chrg_in || 0,
        spd_ct: rawVehicle?.spd_ct || vehicleObj?.speed || 0,
        diffTimeInMin:
          rawVehicle?.diffTimeInMin || (vehicleObj as any)?.diffMins || 0,
      };
    });
  }

  // Arrays matching live-tracking component structure
  masterVehicleMarkers: any[] = [];
  totalOfflineVehicleMarkers: any[] = [];
  mov_marker: any = [];
  deletemarkers: any[] = [];
  m_prv_lat: any = [];
  m_prv_lng: any = [];

  populateStatusLists(): void {
    // Clear all lists (exactly like live-tracking component)
    this.allVehiclesList = [];
    this.vehiclesList = [];
    this.offlineVehicles = [];
    this.repairVehicles = [];
    this.notOpratedVehicles = [];
    this.runningVehicles = [];
    this.stationedShort = [];
    this.stationedMedium = [];
    this.stationedLong = [];
    this.masterVehicleMarkers = [];
    this.totalOfflineVehicleMarkers = [];

    const slctdDt = new Date();
    const currentTimeStmp = moment(slctdDt).format("YYYY-MM-DD HH:mm:ss");

    // Process filtered vehicles so status lists respect active filters (group/category)
    const sourceList =
      this.filteredVehiclesRaw.length > 0
        ? this.filteredVehiclesRaw
        : this.allVehiclesRaw;
    for (var i = 0; i < sourceList.length; i++) {
      const vehicle = sourceList[i];
      this.m_prv_lat[vehicle.asrt_id] = vehicle.lat;
      this.m_prv_lng[vehicle.asrt_id] = vehicle.lng;

      // Calculate diffTimeInMin exactly like live-tracking (line 1286)
      const recievedTime = moment(vehicle.data_received_ts).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      vehicle.diffTimeInMin = moment(currentTimeStmp).diff(
        recievedTime,
        "minute"
      );

      // Add to allVehiclesList
      this.allVehiclesList.push({
        ...vehicle,
        diffTimeInMin: vehicle.diffTimeInMin,
        tdy_dst: vehicle.tdy_dst || "--",
      });

      // Categorize exactly like live-tracking component (lines 1359-1385)
      if (vehicle.asrtglblId == 1) {
        // Online
        this.vehiclesList.push(vehicle);
        // Note: masterVehicleMarkers would be created here in live-tracking
      }

      if (vehicle.asrtglblId != 1) {
        // Offline for getting asrt_sts
        this.totalOfflineVehicleMarkers.push(vehicle);
      }

      // Note: In live-tracking, line 1370 adds to offlineVehicles if onl_offl === 0
      // But showOfflineVehicles() clears and rebuilds it, so this line is effectively overwritten
      // We'll skip this to match the final behavior

      // Running, Stationed logic (exactly like live-tracking lines 1374-1385)
      // These apply to ALL vehicles in live-tracking (not just online vehicles)
      // But they should only count ONLINE vehicles (asrtglblId == 1) for the counts
      // However, live-tracking applies this to all vehicles, so we'll match that behavior
      if (vehicle.spd_ct > 0 && vehicle.diffTimeInMin < 5) {
        this.runningVehicles.push(vehicle);
      }
      if (vehicle.spd_ct >= 0 && vehicle.diffTimeInMin <= 5) {
        this.stationedShort.push(vehicle);
      }
      if (
        vehicle.spd_ct >= 0 &&
        vehicle.diffTimeInMin >= 5 &&
        vehicle.diffTimeInMin < 15
      ) {
        this.stationedMedium.push(vehicle);
      }
      if (
        vehicle.spd_ct >= 0 &&
        vehicle.diffTimeInMin >= 15 &&
        vehicle.onl_offl != 0
      ) {
        this.stationedLong.push(vehicle);
      }
    }

    // Call helper methods exactly like live-tracking (lines 1389-1392)
    this.showOfflineVehicles();
    this.showRepairVehicles();
    this.showNotOprtedVehicles();
  }

  // Helper methods matching live-tracking component exactly
  showOfflineVehicles(): void {
    this.offlineVehicles = [];
    for (var i = 0; i < this.totalOfflineVehicleMarkers.length; i++) {
      if (this.totalOfflineVehicleMarkers[i].asrtglblId == 2) {
        this.offlineVehicles.push(this.totalOfflineVehicleMarkers[i]);
      }
    }
  }

  showRepairVehicles(): void {
    this.repairVehicles = [];
    for (var i = 0; i < this.totalOfflineVehicleMarkers.length; i++) {
      if (this.totalOfflineVehicleMarkers[i].asrtglblId == 3) {
        this.repairVehicles.push(this.totalOfflineVehicleMarkers[i]);
      }
    }
  }

  showNotOprtedVehicles(): void {
    this.notOpratedVehicles = [];
    for (var i = 0; i < this.totalOfflineVehicleMarkers.length; i++) {
      if (
        this.totalOfflineVehicleMarkers[i].asrtglblId != 1 &&
        this.totalOfflineVehicleMarkers[i].asrtglblId != 2 &&
        this.totalOfflineVehicleMarkers[i].asrtglblId != 3
      ) {
        this.notOpratedVehicles.push(this.totalOfflineVehicleMarkers[i]);
      }
    }
  }

  private getFilteredVehiclesForSummary(): any[] {
    // Return vehicles based on active summary filter
    switch (this.activeSummaryKey) {
      case "online":
        return this.allVehiclesRaw.filter((v) => v.asrtglblId == 1);
      case "offline":
        return this.allVehiclesRaw.filter((v) => v.asrtglblId == 2);
      case "repair":
        return this.allVehiclesRaw.filter((v) => v.asrtglblId == 3);
      case "running":
        return this.allVehiclesRaw.filter((v) => {
          const diffMins = this.diffInMinutes(v.data_received_ts);
          return v.spd_ct > 0 && diffMins < 5;
        });
      case "stationedShort":
        return this.allVehiclesRaw.filter((v) => {
          const diffMins = this.diffInMinutes(v.data_received_ts);
          return v.spd_ct >= 0 && diffMins <= 5;
        });
      case "stationedMedium":
        return this.allVehiclesRaw.filter((v) => {
          const diffMins = this.diffInMinutes(v.data_received_ts);
          return v.spd_ct >= 0 && diffMins >= 5 && diffMins < 15;
        });
      case "stationedLong":
        return this.allVehiclesRaw.filter((v) => {
          const diffMins = this.diffInMinutes(v.data_received_ts);
          return v.spd_ct >= 0 && diffMins >= 15 && v.onl_offl != 0;
        });
      default:
        return this.allVehiclesRaw;
    }
  }

  getStatusColor(status: string): string {
    // Return color based on status (like livetrack component)
    switch (status?.toLowerCase()) {
      case "running":
        return "#22c55e";
      case "stationed":
        return "#3b82f6";
      case "stopped":
        return "#ef4444";
      case "offline":
        return "#94a3b8";
      default:
        return "#6b7280";
    }
  }
  onTripChange(event: any): void {
    // trp_rn_id is the main element for trip selection
    this.playbackTrip.tripId = event;
    console.log(
      "onTripChange",
      this.playbackTrip,
      event,
      this.playbackTrip.tripId
    );
    // Find and store selected trip data for driver details (matching play-back.component.ts getTripData)
    if (event && this.availableTripsData.length > 0) {
      const selectedTrip = this.availableTripsData.find(
        (trip: any) => trip.trip_rn_id == event || trip.trp_rn_id == event
      );

      if (selectedTrip) {
        this.selectedTripData = selectedTrip;
        // Update playback filters with trip start/end times for display (optional - trp_rn_id is main element)
        // Handle dd-MM-yyyy HH:mm format dates and convert to datetime-local format (YYYY-MM-DDTHH:mm)
        if (selectedTrip.actl_strt_ts || selectedTrip.actl_start_ts) {
          const startTime =
            selectedTrip.actl_strt_ts || selectedTrip.actl_start_ts;
          // Check if already in dd-MM-yyyy HH:mm format
          if (
            startTime &&
            startTime.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}/)
          ) {
            // Parse dd-MM-yyyy HH:mm format and convert to datetime-local format
            const parsed = moment(startTime, "DD-MM-YYYY HH:mm");
            this.playbackFilters.from = parsed.format("YYYY-MM-DDTHH:mm");
            this.tripStartTimeDisplay = startTime;
          } else {
            // Parse other formats and convert to datetime-local format
            const parsed = moment(startTime);
            this.playbackFilters.from = parsed.format("YYYY-MM-DDTHH:mm") || "";
            this.tripStartTimeDisplay =
              parsed.format("dd-MM-yyyy HH:mm") || "--";
          }
        }
        if (selectedTrip.actl_end_ts || selectedTrip.actl_end_ts) {
          const endTime = selectedTrip.actl_end_ts || selectedTrip.actl_end_ts;
          // Check if already in dd-MM-yyyy HH:mm format
          if (endTime && endTime.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}/)) {
            // Parse dd-MM-yyyy HH:mm format and convert to datetime-local format
            const parsed = moment(endTime, "DD-MM-YYYY HH:mm");
            this.playbackFilters.to = parsed.format("YYYY-MM-DDTHH:mm");
            this.tripEndTimeDisplay = endTime;
          } else {
            // Parse other formats and convert to datetime-local format
            const parsed = moment(endTime);
            this.playbackFilters.to = parsed.format("YYYY-MM-DDTHH:mm") || "";
            this.tripEndTimeDisplay = parsed.format("dd-MM-yyyy HH:mm") || "--";
          }
        }
      } else {
        this.selectedTripData = null;
      }
    } else {
      this.selectedTripData = null;
    }

    // Don't automatically trigger playback - wait for "Run playback" button click
    // Clear any existing playback data when trip changes
    this.clearPlaybackData();
  }
  onCellPrepared(e) {
    // console.log(e);
    if (e.rowType == "data" && e.row.data && e.columnIndex == 1) {
      e.cellElement.style.color = "#667eea !important";
      e.cellElement.style.cursor = "pointer";
    }
  }

  /**
   * Calculate distance between two GPS points using Haversine formula
   * @param lat1 - Latitude of point 1
   * @param lng1 - Longitude of point 1
   * @param lat2 - Latitude of point 2
   * @param lng2 - Longitude of point 2
   * @returns Distance in kilometers
   */
  calculateHaversineDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Filter out abnormal GPS signal jump points that cause large jumps during playback
   * Uses reported speed_ct for validation, look-ahead and multiple criteria for better road alignment
   * @param dataPoints - Array of GPS data points with lat, lng, speed_ct
   * @param maxJumpKm - Maximum allowed single jump distance in km (default 0.5)
   * @param speedTolerance - Tolerance multiplier for reported speed (default 1.5x)
   * @returns Filtered array with valid points only
   */
  filterAbnormalGpsPoints(
    dataPoints: any[],
    maxJumpKm: number = 0.5,
    speedTolerance: number = 1.5
  ): any[] {
    if (!dataPoints || dataPoints.length < 2) return dataPoints;

    const filteredPoints: any[] = [];
    let lastValidPoint: any = null;
    let secondLastValidPoint: any = null;
    let removedCount = 0;

    for (let i = 0; i < dataPoints.length; i++) {
      const point = dataPoints[i];

      // Skip points without valid coordinates
      if (!point.lat || !point.lng) continue;

      // Always include the first valid point
      if (lastValidPoint === null) {
        filteredPoints.push(point);
        lastValidPoint = point;
        continue;
      }

      // Never drop stop/parking points — speed_ct === 0 represents a real stop, not a GPS jump.
      // Keeping them ensures the smoothed/animated path and parking markers stay on the polyline.
      if (point.speed_ct === 0) {
        filteredPoints.push(point);
        secondLastValidPoint = lastValidPoint;
        lastValidPoint = point;
        continue;
      }

      // Calculate distance from last valid point
      const distance = this.calculateHaversineDistance(
        lastValidPoint.lat,
        lastValidPoint.lng,
        point.lat,
        point.lng
      );

      // Calculate time difference in seconds
      let timeDiffSeconds = 0;
      if (point.Signal && lastValidPoint.Signal) {
        const currentTime = this.parseSignalTimestamp(point.Signal);
        const lastTime = this.parseSignalTimestamp(lastValidPoint.Signal);
        if (currentTime && lastTime) {
          timeDiffSeconds = (currentTime.getTime() - lastTime.getTime()) / 1000;
        }
      }

      // Calculate implied speed in km/h from distance traveled
      let impliedSpeed = 0;
      if (timeDiffSeconds > 0) {
        impliedSpeed = (distance / timeDiffSeconds) * 3600; // km/h
      }

      // Get reported speed from GPS device (speed_ct) - use max of both points
      const reportedSpeed = Math.max(
        point.speed_ct || 0,
        lastValidPoint.speed_ct || 0
      );

      // Max allowed speed is based on reported speed_ct with tolerance
      // If both points report 0 speed, use a small threshold (5 km/h for GPS drift)
      const maxAllowedSpeed =
        reportedSpeed > 0 ? reportedSpeed * speedTolerance : 5;

      // For stationary points (both speed_ct = 0), use stricter distance threshold
      const isStationary =
        point.speed_ct === 0 && lastValidPoint.speed_ct === 0;
      const effectiveMaxJump = isStationary
        ? Math.min(maxJumpKm, 0.05)
        : maxJumpKm; // 50m max for stationary

      // Check for sudden direction change (potential GPS drift)
      let hasAbnormalDirectionChange = false;
      if (secondLastValidPoint && filteredPoints.length >= 2) {
        const prevBearing = this.calculateBearing(
          secondLastValidPoint.lat,
          secondLastValidPoint.lng,
          lastValidPoint.lat,
          lastValidPoint.lng
        );
        const currBearing = this.calculateBearing(
          lastValidPoint.lat,
          lastValidPoint.lng,
          point.lat,
          point.lng
        );
        const bearingDiff = Math.abs(currBearing - prevBearing);
        const normalizedDiff =
          bearingDiff > 180 ? 360 - bearingDiff : bearingDiff;
        // Flag if direction changes more than 150 degrees with significant distance
        hasAbnormalDirectionChange = normalizedDiff > 150 && distance > 0.2;
      }

      // Look-ahead: if current point seems like a jump, check if next point returns to valid trajectory
      let nextPointReturnsToTrajectory = false;
      if (i + 1 < dataPoints.length) {
        const nextPoint = dataPoints[i + 1];
        if (nextPoint.lat && nextPoint.lng) {
          const distToNext = this.calculateHaversineDistance(
            point.lat,
            point.lng,
            nextPoint.lat,
            nextPoint.lng
          );
          const distFromLastToNext = this.calculateHaversineDistance(
            lastValidPoint.lat,
            lastValidPoint.lng,
            nextPoint.lat,
            nextPoint.lng
          );
          // If next point is closer to last valid point than current, current might be outlier
          nextPointReturnsToTrajectory =
            distFromLastToNext < distance && distToNext > distance * 0.8;
        }
      }

      // Determine if this point is a signal jump
      const isJump =
        // Jump exceeds maximum allowed distance
        distance > effectiveMaxJump ||
        // Implied speed exceeds reported speed_ct with tolerance
        (timeDiffSeconds > 0 && impliedSpeed > maxAllowedSpeed) ||
        // Abnormal direction change with distance
        hasAbnormalDirectionChange ||
        // Look-ahead suggests this is an outlier
        (nextPointReturnsToTrajectory && distance > 0.3);

      // Always include the last point to ensure complete path is drawn
      const isLastPoint = i === dataPoints.length - 1;

      if (!isJump) {
        filteredPoints.push(point);
      } else if (isLastPoint && lastValidPoint) {
        // If last point is abnormal, use n-1 point's lat/lng but keep the last point's metadata
        const adjustedLastPoint = {
          ...point,
          lat: lastValidPoint.lat,
          lng: lastValidPoint.lng,
        };
        filteredPoints.push(adjustedLastPoint);
        console.log(
          `Last point had abnormal jump: Using n-1 point lat/lng (${
            lastValidPoint.lat
          }, ${lastValidPoint.lng}) for Point ${
            point.sno || i
          } - Distance was: ${distance.toFixed(3)}km`
        );
      } else {
        removedCount++;
        console.log(
          `Skipped GPS jump: Point ${
            point.sno || i
          } - Distance: ${distance.toFixed(
            3
          )}km, Implied: ${impliedSpeed.toFixed(
            1
          )}km/h, Reported: ${reportedSpeed}km/h`
        );
      }

      // Always advance the reference points so the next comparison is against the
      // immediately previous GPS reading — prevents a single flagged point from
      // making every subsequent point look like a cascading jump.
      secondLastValidPoint = lastValidPoint;
      lastValidPoint = point;
    }

    console.log(
      `GPS Filter: ${dataPoints.length} points -> ${filteredPoints.length} valid points (${removedCount} jumps removed)`
    );
    return filteredPoints;
  }

  /**
   * Calculate bearing between two GPS points
   * @returns Bearing in degrees (0-360)
   */
  calculateBearing(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;

    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x =
      Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

    let bearing = (Math.atan2(y, x) * 180) / Math.PI;
    return (bearing + 360) % 360;
  }

  /**
   * Parse signal timestamp string to Date object
   * @param timestamp - Timestamp string (e.g., "17-01-2026 00:03:32")
   * @returns Date object or null if parsing fails
   */
  parseSignalTimestamp(timestamp: string): Date | null {
    if (!timestamp) return null;

    // Try parsing "DD-MM-YYYY HH:mm:ss" format
    const match = timestamp.match(
      /(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):?(\d{2})?/
    );
    if (match) {
      const [, day, month, year, hour, minute, second] = match;
      return new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour),
        parseInt(minute),
        parseInt(second || "0")
      );
    }

    // Fallback to standard Date parsing
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Calculate total traveled distance by summing all consecutive point distances
   * @param dataPoints - Array of GPS data points with lat, lng
   * @returns Total distance in kilometers
   */
  calculateTotalTraveledDistance(dataPoints: any[]): number {
    if (!dataPoints || dataPoints.length < 2) return 0;

    let totalDistance = 0;
    for (let i = 1; i < dataPoints.length; i++) {
      const prev = dataPoints[i - 1];
      const curr = dataPoints[i];
      if (prev.lat && prev.lng && curr.lat && curr.lng) {
        totalDistance += this.calculateHaversineDistance(
          prev.lat,
          prev.lng,
          curr.lat,
          curr.lng
        );
      }
    }
    return totalDistance;
  }

  /**
   * Calculate direct distance from start to end point
   * @param dataPoints - Array of GPS data points with lat, lng
   * @returns Direct distance in kilometers
   */
  calculateStartToEndDistance(dataPoints: any[]): number {
    if (!dataPoints || dataPoints.length < 2) return 0;

    const startPoint = dataPoints[0];
    const endPoint = dataPoints[dataPoints.length - 1];

    if (!startPoint.lat || !startPoint.lng || !endPoint.lat || !endPoint.lng)
      return 0;

    return this.calculateHaversineDistance(
      startPoint.lat,
      startPoint.lng,
      endPoint.lat,
      endPoint.lng
    );
  }
}
